import { appStore } from '../core/store';
import { queuePersonalMyNoteForSync } from '../core/personalMyNotesSync';
import { PlaybackController, resolveOutputSides, type ControllerState, type BeatEvent } from '../audio/PlaybackController';
import { formatDuration } from '../core/format';
import { wakeLockManager, type WakeLockStatus } from '../core/wakeLock';
import { confirmDialog } from '../ui/confirm';
import type { MarkerNoteFormat, MarkerNoteTab, MarkerSheetData, SetlistRecord, SetlistRouting, SongMarker, SongRecord } from '../core/types';
import { allNoteTabs, markerNoteFormat, markerNoteSheet, markerNoteText, noteTabLabel } from '../core/types';
import {
  MARKER_LEAD_IN_OPTIONS,
  MARKER_LEAD_IN_BEAT_OPTIONS,
  DEFAULT_REHEARSAL_ROUTING,
  parseRehearsalRouting,
  resolveEffectiveLeadIn,
  type RoutingSide,
} from '../core/types';
import { ICON_PLAY, ICON_PAUSE, ICON_LOOP, ICON_PREV, ICON_NEXT } from '../ui/transportIcons';
import { effectiveShortLabel, markerSeekTarget, secondsPerBeat } from '../core/markerFormat';
import {
  pickupBeatsFromFirstBeatTime,
  decodeWaveformPeaks,
  drawWaveform,
  beatPositionInMeasure,
  currentSubdivisionSlot,
  type WaveformPeaks,
} from '../audio/waveform';
import { database } from '../db/database';
import { renderBeatVisual, triggerPulse, scheduleBeatPulse, pulseTierFor, type BeatVisualEls } from '../ui/beatVisual';
import {
  MAX_NOTE_SHEET_COLUMNS,
  MAX_NOTE_SHEET_ROWS,
  parseSheetClipboard,
  renderSheetTable,
  sheetClipboardExceedsLimits,
  sheetHasContent,
} from '../ui/noteSheet';
import { showToast } from '../ui/toast';
import { applyNoteZoom, renderNoteZoomControl, stepNoteZoom } from '../ui/noteZoom';
import { noteTextHasContent } from '../ui/noteContent';
import { metronomePresetStore } from '../core/metronomePresetStore';
import { CLICK_SOUND_LABELS } from '../audio/clickSounds';
import { shortSyncId } from '../core/syncDiagnostics';

let sharedAudioContext: AudioContext | null = null;
let sharedController: PlaybackController | null = null;

const PLAYER_SETLIST_WIDTH_STORAGE_KEY = 'liveMediaPlayer.performance.setlistWidthPercent';
const PLAYER_NOTES_PREVIEW_HEIGHT_STORAGE_KEY = 'liveMediaPlayer.performance.notesPreviewHeightPercent';
const PLAYER_NOTES_PREVIEW_WIDTH_STORAGE_KEY = 'liveMediaPlayer.performance.notesPreviewWidthPercent';
const PLAYER_NOTES_LAYOUT_STORAGE_KEY = 'liveMediaPlayer.performance.notesLayout';
const PLAYER_NOTES_PANE_STORAGE_KEY_PREFIX = 'liveMediaPlayer.performance.notesPane';
const PLAYER_CLICK_WIDGET_COLLAPSED_STORAGE_KEY = 'liveMediaPlayer.performance.clickWidgetCollapsed';
const PLAYER_NOTES_EARLY_SWAP_SECONDS_STORAGE_KEY = 'liveMediaPlayer.performance.notesEarlySwapSeconds';
const PLAYER_NOTES_EARLY_SWAP_BEATS_STORAGE_KEY = 'liveMediaPlayer.performance.notesEarlySwapBeats';
const PLAYER_NOTES_PREVIEW_WARNING_SECONDS_STORAGE_KEY = 'liveMediaPlayer.performance.notesPreviewWarningSeconds';
const PLAYER_MY_NOTES_STORAGE_KEY_PREFIX = 'liveMediaPlayer.performance.myNotes';
const PLAYER_MY_NOTES_FORMAT_STORAGE_KEY_PREFIX = 'liveMediaPlayer.performance.myNotesFormat';
const PLAYER_MY_NOTES_SHEET_STORAGE_KEY_PREFIX = 'liveMediaPlayer.performance.myNotesSheet';
const DEFAULT_SETLIST_WIDTH_PERCENT = 39;
const DEFAULT_NOTES_PREVIEW_HEIGHT_PERCENT = 28;
const DEFAULT_NOTES_PREVIEW_WIDTH_PERCENT = 38;
type NotesLayout = 'stacked' | 'side-by-side' | 'current-only';
const DEFAULT_NOTES_LAYOUT: NotesLayout = 'stacked';
const NOTES_LAYOUT_LABELS: Record<NotesLayout, string> = {
  stacked: 'Stacked',
  'side-by-side': 'Side by Side',
  'current-only': 'Current Only',
};
const MIN_SETLIST_WIDTH_PX = 320;
const MIN_NOW_PLAYING_WIDTH_PX = 520;
const MAX_SETLIST_WIDTH_PERCENT = 55;
const PERFORMANCE_SPLITTER_WIDTH_PX = 18;
/** How far ahead of the early-swap flip itself (see currentNotesEarlySwapSeconds)
 * the CURRENT section's notes can start showing a "Switching" heads-up —
 * user-configurable (see notesPreviewWarningSeconds), Off by default, so
 * someone reading along isn't caught off guard the instant the panel jumps
 * to the next section's content and tags it "Preview" — unless they'd
 * rather not have the extra badge at all. */
const NOTES_PREVIEW_WARNING_OPTIONS = [2, 4, 6, 8] as const;
type PlayerNotesTab = 'my-notes' | MarkerNoteTab;
/** Every record-backed tab (built-in + whatever CustomNoteTab entries
 * currently exist) eligible for the swap slot in renderNotesTabRow — NOT a
 * fixed 3-entry array anymore. This is a function, not a module-level
 * const, because the tab LIST can change at runtime (adding/removing a
 * custom tab in the Song Library) and this always needs the current one. */
function playerSharedNotesTabs(): MarkerNoteTab[] {
  return allNoteTabs(appStore.getState().settings.customNoteTabs);
}

/** 'current' shows whatever section Pane A is on; 'upcoming' previews the
 * section after it — see PlayerNotesPaneSelection's own comment. Pane A is
 * always 'current' in practice (no UI ever offers it a toggle); only Pane B
 * is ever set to 'upcoming'. */
type PlayerNotesTiming = 'current' | 'upcoming';

/** Each of the two notes panes independently picks its own tab (My Notes/
 * Lyrics/Chords/General) AND its own timing (current section vs. a preview
 * of the upcoming one) — replaces the old single global activeNotesTab,
 * which forced Pane B to always mirror Pane A's tab. This is what lets a
 * guitarist who also sings see Lyrics and Chords for the SAME current
 * section at once, side by side, instead of only ever "current tab" +
 * "same tab, next section." Pane A stays the only EDITABLE pane regardless
 * of either pane's tab/timing — see renderNotesPaneContent's `editable`
 * param. */
interface PlayerNotesPaneSelection {
  tab: PlayerNotesTab;
  timing: PlayerNotesTiming;
}

function getController(): PlaybackController {
  if (!sharedAudioContext) {
    sharedAudioContext = new AudioContext();
  }
  if (!sharedController) {
    sharedController = new PlaybackController(sharedAudioContext);
  }
  return sharedController;
}

/** Where a marker's own section ends: the next marker's timestamp, or the
 * song's total duration if `i` is the last marker. Same boundary every
 * per-marker section width/fill computation in this file already uses
 * (progress-bar section widths, count-in split math) — the loop feature
 * needs the identical boundary to know when a looping section has finished
 * playing and should jump back to its own start. */
function markerSectionEnd(markers: SongMarker[], i: number, durationSeconds: number): number {
  return i < markers.length - 1 ? markers[i + 1].timeSeconds : durationSeconds;
}

function syncedSectionTimerParts(elapsedSeconds: number, sectionLengthSeconds: number): { elapsed: string; remaining: string } {
  const totalDisplaySeconds = Math.max(0, Math.floor(sectionLengthSeconds));
  const elapsedDisplaySeconds = Math.max(0, Math.min(totalDisplaySeconds, Math.floor(Math.max(0, elapsedSeconds))));
  const remainingDisplaySeconds = Math.max(0, totalDisplaySeconds - elapsedDisplaySeconds);
  return {
    elapsed: formatDuration(elapsedDisplaySeconds),
    remaining: formatDuration(remainingDisplaySeconds),
  };
}

/** Which marker's own section `currentTime` currently falls inside — the
 * last marker whose timestamp has already been reached. Returns -1 for an
 * empty marker list (nothing to index into) or a currentTime before the
 * very first marker. Used both by the progress bar's own "genuinely
 * playing" section (as `timeIndex`) and by the notes panel, which needs the
 * identical notion of "current section" to show the right marker's notes. */
function currentMarkerIndex(markers: SongMarker[], currentTime: number): number {
  let index = -1;
  for (let i = 0; i < markers.length; i++) {
    if (currentTime >= markers[i].timeSeconds) index = i;
  }
  return index;
}

function playerNotesStorageKey(prefix: string, songId: string, markerId: string): string {
  return `${prefix}.${songId}.${markerId}`;
}

/** Per-song, per-pane tab+timing selection — same one-concern-per-key
 * storage convention as loadHiddenNotesTabs/saveHiddenNotesTabs above, just
 * two independent keys (one per pane) instead of one. Returns null for
 * "nothing saved yet" (a fresh song, or a pre-dual-pane install) rather
 * than a guessed default — the caller decides the right fallback (see the
 * per-song load site, which reproduces exact pre-dual-pane behavior). */
function loadNotesPaneSelection(songId: string, pane: 'a' | 'b'): PlayerNotesPaneSelection | null {
  try {
    const raw = localStorage.getItem(`${PLAYER_NOTES_PANE_STORAGE_KEY_PREFIX}.${pane}.${songId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const tab = (parsed as { tab?: unknown }).tab;
    const timing = (parsed as { timing?: unknown }).timing;
    // Validated against whichever tabs actually exist RIGHT NOW (built-in +
    // custom), not a fixed 4-literal check — a saved selection referencing
    // a custom tab that's since been deleted must fall back to the default
    // rather than resurface a tab id the picker no longer offers anywhere.
    const knownTabs = playerSharedNotesTabs();
    const validTab = tab === 'my-notes' || (typeof tab === 'string' && knownTabs.includes(tab)) ? (tab as PlayerNotesTab) : null;
    const validTiming = timing === 'current' || timing === 'upcoming' ? (timing as PlayerNotesTiming) : null;
    if (!validTab || !validTiming) return null;
    return { tab: validTab, timing: validTiming };
  } catch {
    return null;
  }
}

function saveNotesPaneSelection(songId: string, pane: 'a' | 'b', selection: PlayerNotesPaneSelection): void {
  try {
    localStorage.setItem(`${PLAYER_NOTES_PANE_STORAGE_KEY_PREFIX}.${pane}.${songId}`, JSON.stringify(selection));
  } catch {
    // Ignore storage failures; the UI still functions with in-memory state.
  }
}

function loadMyNotes(songId: string, markerId: string): string {
  try {
    return localStorage.getItem(playerNotesStorageKey(PLAYER_MY_NOTES_STORAGE_KEY_PREFIX, songId, markerId)) ?? '';
  } catch {
    return '';
  }
}

function saveMyNotes(songId: string, markerId: string, value: string): void {
  try {
    localStorage.setItem(playerNotesStorageKey(PLAYER_MY_NOTES_STORAGE_KEY_PREFIX, songId, markerId), value);
  } catch {
    // Ignore storage failures.
  }
}

/** My Notes gets its own Text/Sheet toggle, same as Lyrics/Chords/General
 * — but stored entirely in localStorage (this function, and
 * loadMyNotesSheet/saveMyNotesSheet below), never on the shared marker
 * record, since My Notes is private/user-owned the same way its plain-text
 * content already is. */
function loadMyNotesFormat(songId: string, markerId: string): MarkerNoteFormat {
  try {
    const raw = localStorage.getItem(playerNotesStorageKey(PLAYER_MY_NOTES_FORMAT_STORAGE_KEY_PREFIX, songId, markerId));
    return raw === 'sheet' ? 'sheet' : 'text';
  } catch {
    return 'text';
  }
}

function saveMyNotesFormat(songId: string, markerId: string, format: MarkerNoteFormat): void {
  try {
    localStorage.setItem(playerNotesStorageKey(PLAYER_MY_NOTES_FORMAT_STORAGE_KEY_PREFIX, songId, markerId), format);
  } catch {
    // Ignore storage failures.
  }
}

function loadMyNotesSheet(songId: string, markerId: string): MarkerSheetData {
  try {
    const raw = localStorage.getItem(playerNotesStorageKey(PLAYER_MY_NOTES_SHEET_STORAGE_KEY_PREFIX, songId, markerId));
    if (!raw) return { rows: [] };
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && Array.isArray((parsed as MarkerSheetData).rows) ? (parsed as MarkerSheetData) : { rows: [] };
  } catch {
    return { rows: [] };
  }
}

function saveMyNotesSheet(songId: string, markerId: string, sheet: MarkerSheetData): void {
  try {
    localStorage.setItem(playerNotesStorageKey(PLAYER_MY_NOTES_SHEET_STORAGE_KEY_PREFIX, songId, markerId), JSON.stringify(sheet));
  } catch {
    // Ignore storage failures.
  }
}

/** Short label for a setlist's routing choice, surfaced once in the
 * setlist header (see setlistPanelMeta below) rather than repeated per
 * song — every song in a setlist shares the exact same routing (see
 * SetlistRouting's own doc comment for why this became a setlist-level
 * setting). Someone about to play a show needs this to be glanceable
 * without opening the Setlists tab, especially since it's easy to forget
 * what a setlist was last set to.
 *
 * `hasClick` (true if ANY song in the setlist actually has an active
 * click — synthetic click enabled, or metronome-only) matters here: a
 * setlist with no click at all plays every song in plain stereo
 * regardless of the routing setting (see resolveOutputSides — nothing
 * exists to split), so labeling it with whatever click side happens to be
 * configured would describe a split that's never actually audible. Always
 * names BOTH sides (Tracks: _, Click: _) using plain channel language
 * (Stereo/Left/Right) rather than "ear"-based wording — this describes
 * which output channel audio goes down, not which ear it's assumed to
 * land in. */
function routingSideLabel(side: RoutingSide): string {
  return side === 'stereo' ? 'Stereo' : side === 'left' ? 'Left' : 'Right';
}

function routingTag(routing: SetlistRouting, hasClick: boolean): string {
  if (!hasClick) return 'Tracks: Stereo';
  const rehearsal = parseRehearsalRouting(routing);
  if (rehearsal) {
    return `Tracks: ${routingSideLabel(rehearsal.trackSide)}, Click: ${routingSideLabel(rehearsal.clickSide)}`;
  }
  switch (routing) {
    case 'tracks-left-click-right':
      return 'Tracks: Left, Click: Right';
    case 'tracks-right-click-left':
      return 'Tracks: Right, Click: Left';
    default:
      // Unreachable — see resolveOutputSides's own identical default for why.
      return 'Tracks: Stereo, Click: Stereo';
  }
}

function setlistHasClick(setlist: SetlistRecord, songs: SongRecord[]): boolean {
  return setlist.entries.some((entry) => {
    const song = songs.find((s) => s.id === entry.songId);
    return song != null && (song.mode === 'metronome' || song.syntheticClickEnabled);
  });
}

/** Total runtime of every song in the setlist, formatted the same M:SS way
 * as a single song's own duration (see formatDuration) — lets someone
 * glance at a setlist and know roughly how long the set runs without
 * adding it up song by song. A metronome-only song (durationSeconds: null)
 * contributes 0, same as a missing/not-yet-loaded song — this is a rough
 * "how long is this set" figure, not a precise one, and count-ins/lead-ins
 * aren't included either. */
function setlistTotalDuration(setlist: SetlistRecord, songs: SongRecord[]): number {
  return setlist.entries.reduce((total, entry) => {
    const song = songs.find((s) => s.id === entry.songId);
    return total + (song?.durationSeconds ?? 0);
  }, 0);
}

/** The setlist header/picker's full meta line — song count with its total
 * runtime in parens (reads more naturally than a third bullet-separated
 * segment, e.g. "5 songs (26:08)" not "5 songs · 26:08"), then the routing
 * summary. Shared by both places this line renders (the Player's own
 * setlist header and the setlist-switcher dropdown) so the two can't drift
 * apart on formatting. */
function setlistMetaLine(setlist: SetlistRecord, songs: SongRecord[]): string {
  const songCount = `${setlist.entries.length} song${setlist.entries.length === 1 ? '' : 's'}`;
  const duration = formatDuration(setlistTotalDuration(setlist, songs));
  return `${songCount} (${duration}) · ${routingTag(setlist.routing, setlistHasClick(setlist, songs))}`;
}

// Routing is a setlist-level setting now (see SetlistRouting), so it's no
// longer per-song-differentiated info worth repeating on every single
// setlist row — every song in a setlist shares the exact same routing.
// It's surfaced once, in the setlist header, alongside the routing picker
// itself, instead of here.
function modeTag(song: SongRecord): string {
  if (song.mode === 'tracks-click') {
    return `Tracks + Click${song.durationSeconds ? ` · ${formatDuration(song.durationSeconds)}` : ''}`;
  }
  if (song.mode === 'tracks-only') {
    return `Tracks Only${song.durationSeconds ? ` · ${formatDuration(song.durationSeconds)}` : ''}`;
  }
  return `Metronome · ${song.metronomeSettings.bpm} BPM`;
}

function setlistSongTag(song: SongRecord): string {
  if (song.mode === 'metronome') return modeTag(song);
  return song.durationSeconds ? formatDuration(song.durationSeconds) : '';
}

/** A count-in's label at rest, before playback starts — see
 * liveCountInLabel for the LIVE ticking label once the count-in is
 * actually playing (the one the Play/Pause button shows while counting in;
 * see updatePlayPauseCountIn). A beat-based count-in (secondsPerBeat
 * non-null) reads as "N counts" — the unit a drummer actually thinks in
 * when counting a band in — otherwise as a plain seconds value, matching
 * the same distinction the (now-removed) practice-control lead-in picker's
 * own "Nct" vs "Ns" labels used. */
export function countInLabel(countIn: { totalSeconds: number; secondsPerBeat: number | null }): string {
  if (countIn.secondsPerBeat !== null && countIn.secondsPerBeat > 0) {
    const counts = Math.round(countIn.totalSeconds / countIn.secondsPerBeat);
    return `Count-in · ${counts} count${counts === 1 ? '' : 's'}`;
  }
  return `Count-in · ${Math.round(countIn.totalSeconds)}s`;
}

/** The count-in's label WHILE it's actively ticking down (see countInLabel
 * for the at-rest label before playback starts) — used as the Play/Pause
 * button's title/aria-label while counting in (see updatePlayPauseCountIn).
 * remainingSeconds/secondsPerBeat come straight from either the
 * real pre-roll or the marker-tap countdown; both are floating-point, so a
 * remaining value that's conceptually exactly N whole beats/seconds can
 * land a hair on either side of N due to floating-point noise — anything
 * from plain division residue (8.0000000001) up to real seek-landing
 * imprecision (a marker-tap seek's actual post-seek currentTime can differ
 * from its pre-seek target by several microseconds — observed live at
 * ~2e-6s, ~5e-6 beats once divided). Math.ceil alone would round that
 * overshoot up to N+1 — e.g. showing "9 counts left" for an 8-count
 * count-in on its very first render — so whole-number-ish values are
 * snapped before ceiling. The epsilon (1e-4 beats/seconds) is generous
 * enough to absorb realistic overshoot from either source while staying
 * three orders of magnitude below a real timer tick (50ms in
 * PlaybackController's pre-roll), so genuine countdown progress is never
 * mistaken for boundary noise. */
export function liveCountInLabel(active: { remainingSeconds: number; secondsPerBeat: number | null }): string {
  const rawRemainingUnits = active.secondsPerBeat ? active.remainingSeconds / active.secondsPerBeat : active.remainingSeconds;
  const remainingUnits =
    Math.abs(rawRemainingUnits - Math.round(rawRemainingUnits)) < 1e-4 ? Math.round(rawRemainingUnits) : rawRemainingUnits;
  const remaining = Math.max(1, Math.ceil(remainingUnits));
  return active.secondsPerBeat ? `Count-in · ${remaining} count${remaining === 1 ? '' : 's'} left` : `Count-in · ${remaining}s left`;
}

export function renderPerformanceSection(container: HTMLElement): () => void {
  container.innerHTML = '';
  const controller = getController();

  const el = document.createElement('div');
  el.className = 'performance';
  el.innerHTML = `
    <div class="performance-grid performance-grid-resizable" id="performance-grid">
      <div class="performance-col now-playing">
        <div class="status-flag" id="status-flag" style="display:none"></div>
        <div class="current-song-heading">
          <button class="hamburger-btn player-hamburger-btn" type="button" aria-label="Open menu" data-action="open-nav-drawer">
            <span></span><span></span><span></span>
          </button>
          <div class="spotify-player-top-copy">
            <h1 class="current-song-title" id="current-title">No song loaded</h1>
          </div>
          <button class="current-song-edit-btn current-song-edit-btn-wide" id="current-song-edit-btn" type="button" title="Edit current song" aria-label="Edit current song" disabled>
            <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true"><path d="M4 20h4L18.5 9.5a2.121 2.121 0 0 0-3-3L5 17v3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>

        <div class="progress-wrap">
          <div class="progress-track" id="progress-track">
            <canvas class="progress-waveform-canvas" id="progress-waveform-canvas"></canvas>
            <div class="progress-sections" id="progress-sections"></div>
          </div>
          <div class="time-row">
            <span id="elapsed-time">0:00</span>
            <span id="remaining-time">--:--</span>
          </div>
        </div>

        <div class="row transport-and-beats-row">
          <div class="transport-row-compact transport-row-standalone" id="transport-cluster">
            <div class="transport-row-side transport-row-side-left" id="transport-cluster-left">
              <button class="btn-transport-compact btn-transport-compact-secondary btn-transport-compact-loop" id="loop-btn" title="Loop the active section">
                <span class="btn-transport-compact-icon" id="loop-icon">${ICON_LOOP}</span>
                <span class="btn-transport-compact-loop-mode-label" id="loop-mode-label"></span>
              </button>
            </div>

            <div class="transport-row-core" id="transport-cluster-core">
              <button class="btn-transport-compact btn-transport-compact-secondary" id="prev-song-btn" title="Previous song">
                <span class="btn-transport-compact-icon">${ICON_PREV}</span>
              </button>
              <button class="btn-transport-compact btn-transport-compact-play" id="play-pause-btn">
                <span class="btn-transport-compact-icon" id="play-pause-icon">${ICON_PLAY}</span>
              </button>
              <button class="btn-transport-compact btn-transport-compact-secondary" id="next-song-btn" title="Next song">
                <span class="btn-transport-compact-icon">${ICON_NEXT}</span>
              </button>
            </div>

            <div class="transport-row-side transport-row-side-right" id="transport-cluster-right">
              <button class="btn-transport-compact btn-transport-compact-secondary" id="click-status-btn" title="Click status">
                <span class="btn-transport-compact-icon">◷</span>
              </button>
              <button class="click-widget-settings-btn" id="click-settings-btn" type="button" title="Playback settings" aria-label="Playback settings" aria-expanded="false" aria-controls="click-sound-menu" disabled>
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="5" cy="12" r="2.1" fill="currentColor"/><circle cx="12" cy="12" r="2.1" fill="currentColor"/><circle cx="19" cy="12" r="2.1" fill="currentColor"/></svg>
              </button>
            </div>
          </div>

          <div class="beat-strip beat-strip-main" id="beat-strip-main">
            <div id="metronome-visual-wrap" class="metronome-visual-wrap-compact"></div>
            <div class="player-header-menu click-sound-menu" id="click-sound-menu" hidden></div>
          </div>
        </div>

        <div class="notes-panel" id="notes-panel-wide"></div>

      </div>

      <div
        class="performance-splitter"
        id="performance-splitter"
        role="separator"
        aria-label="Resize setlist panel"
        aria-orientation="vertical"
        aria-valuemin="0"
        aria-valuemax="0"
        aria-valuenow="${DEFAULT_SETLIST_WIDTH_PERCENT}"
        tabindex="0"
        title="Drag to resize the setlist. Use arrow keys when focused. Double-click to reset."
      ></div>

      <div class="performance-col setlist-panel">
        <div class="setlist-panel-header">
          <div class="setlist-panel-header-text">
            <button class="setlist-picker-trigger" id="setlist-picker-trigger" type="button" aria-expanded="false" aria-controls="setlist-picker-menu">
              <span id="setlist-panel-title">No setlist selected</span>
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="m5 7 5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div class="setlist-panel-meta" id="setlist-panel-meta"></div>
          </div>
          <div class="player-header-menu setlist-picker-menu" id="setlist-picker-menu" hidden></div>
        </div>
        <div class="row transport-and-beats-row-compact">
          <div class="transport-row-compact" id="transport-row-compact">
            <button class="btn-transport-compact btn-transport-compact-secondary" id="prev-song-btn-compact" title="Previous song">
              <span class="btn-transport-compact-icon">${ICON_PREV}</span>
              <span class="btn-transport-beat-flash" data-beat-number="1"></span>
            </button>
            <button class="btn-transport-compact btn-transport-compact-play" id="play-pause-btn-compact">
              <span class="btn-transport-compact-icon" id="play-pause-icon-compact">${ICON_PLAY}</span>
              <span class="btn-transport-beat-flash" data-beat-number="2"></span>
            </button>
            <button class="btn-transport-compact btn-transport-compact-secondary" id="next-song-btn-compact" title="Next song">
              <span class="btn-transport-compact-icon">${ICON_NEXT}</span>
              <span class="btn-transport-beat-flash" data-beat-number="3"></span>
            </button>
            <button class="btn-transport-compact btn-transport-compact-secondary btn-transport-compact-loop" id="loop-btn-compact" title="Loop the active section">
              <span class="btn-transport-compact-icon" id="loop-icon-compact">${ICON_LOOP}</span>
              <span class="btn-transport-compact-loop-mode-label" id="loop-mode-label-compact"></span>
              <span class="btn-transport-beat-flash" data-beat-number="4"></span>
            </button>
          </div>
          <div class="beat-strip beat-strip-header" id="beat-strip-header">
            <div id="header-beat-visual-wrap"></div>
          </div>
          <button class="click-widget-settings-btn" id="click-settings-btn-compact" type="button" title="Playback settings" aria-label="Playback settings" aria-expanded="false" aria-controls="click-sound-menu-compact" disabled>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="5" cy="12" r="2.1" fill="currentColor"/><circle cx="12" cy="12" r="2.1" fill="currentColor"/><circle cx="19" cy="12" r="2.1" fill="currentColor"/></svg>
          </button>
          <div class="player-header-menu click-sound-menu" id="click-sound-menu-compact" hidden></div>
        </div>
        <div class="row setlist-notes-view-toggle" id="setlist-notes-view-toggle">
          <div class="setlist-notes-view-switch" role="tablist" aria-label="Panel view">
            <span class="compact-setlist-segment" role="presentation">
              <button class="pill pill-toggle is-active" id="view-toggle-setlist-btn" data-view="setlist" role="tab" aria-selected="true">Setlist</button>
              <button class="compact-setlist-picker-trigger" id="compact-setlist-picker-trigger" type="button" title="Choose setlist" aria-label="Choose setlist" aria-expanded="false" aria-controls="setlist-picker-menu-compact">
                <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><circle cx="5" cy="12" r="2.1" fill="currentColor"/><circle cx="12" cy="12" r="2.1" fill="currentColor"/><circle cx="19" cy="12" r="2.1" fill="currentColor"/></svg>
              </button>
            </span>
            <span class="compact-notes-segment" role="presentation">
              <button class="pill pill-toggle" id="view-toggle-notes-btn" data-view="notes" role="tab" aria-selected="false">Notes</button>
              <span class="compact-notes-inline-tools" id="compact-notes-inline-tools" aria-label="Notes tools"></span>
              <button class="compact-notes-options-trigger" id="compact-notes-options-trigger" type="button" title="Notes settings" aria-label="Notes settings" aria-expanded="false">
                <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><circle cx="5" cy="12" r="2.1" fill="currentColor"/><circle cx="12" cy="12" r="2.1" fill="currentColor"/><circle cx="19" cy="12" r="2.1" fill="currentColor"/></svg>
              </button>
            </span>
          </div>
          <div class="player-utility-drawer setlist-picker-drawer" id="setlist-picker-menu-compact" hidden>
            <button type="button" class="player-utility-drawer-scrim" data-action="close-setlist-picker" aria-label="Close setlist picker"></button>
            <section class="player-utility-drawer-sheet setlist-picker-drawer-sheet" role="dialog" aria-modal="true" aria-label="Choose Setlist">
              <div class="player-utility-drawer-handle" aria-hidden="true"></div>
              <div class="player-utility-drawer-heading">
                <h2>Choose Setlist</h2>
                <button type="button" class="player-utility-drawer-close" data-action="close-setlist-picker" aria-label="Close">×</button>
              </div>
              <div class="player-utility-drawer-options setlist-picker-drawer-options" id="setlist-picker-menu-compact-options"></div>
            </section>
          </div>
        </div>
        <div class="setlist-panel-scroll" id="setlist-panel-scroll"></div>
        <div class="notes-panel" id="notes-panel-compact" style="display:none"></div>
      </div>
    </div>
  `;
  container.appendChild(el);

  const performanceGrid = el.querySelector<HTMLDivElement>('#performance-grid')!;
  const performanceSplitter = el.querySelector<HTMLDivElement>('#performance-splitter')!;
  const statusFlag = el.querySelector<HTMLDivElement>('#status-flag')!;
  const currentTitle = el.querySelector<HTMLHeadingElement>('#current-title')!;
  const currentSongEditBtn = el.querySelector<HTMLButtonElement>('#current-song-edit-btn')!;
  const progressTrack = el.querySelector<HTMLDivElement>('#progress-track')!;
  const progressWaveformCanvas = el.querySelector<HTMLCanvasElement>('#progress-waveform-canvas')!;
  const progressSections = el.querySelector<HTMLDivElement>('#progress-sections')!;
  const elapsedEl = el.querySelector<HTMLSpanElement>('#elapsed-time')!;
  const remainingEl = el.querySelector<HTMLSpanElement>('#remaining-time')!;
  const playPauseBtn = el.querySelector<HTMLButtonElement>('#play-pause-btn')!;
  const playPauseIcon = el.querySelector<HTMLSpanElement>('#play-pause-icon')!;
  const loopBtn = el.querySelector<HTMLButtonElement>('#loop-btn')!;
  const loopModeLabel = el.querySelector<HTMLSpanElement>('#loop-mode-label')!;
  const prevSongBtn = el.querySelector<HTMLButtonElement>('#prev-song-btn')!;
  const nextSongBtn = el.querySelector<HTMLButtonElement>('#next-song-btn')!;
  // A second transport row lives in the setlist panel's own header — shown
  // ONLY in the narrow/portrait layout (see style.css's narrow-viewport
  // media query), where now-playing itself is de-emphasized in favor of the
  // setlist. Both rows share the same compact Previous/Play/Loop/Next
  // styling and button sizing now (now-playing's own row shrank to match,
  // freeing most of its vertical space for the notes panel below) — kept as
  // genuinely separate elements (not CSS-repositioned copies of each other)
  // since they live in different DOM parents, but driven by the exact same
  // state/click handling so neither is ever a second, independently-tracked
  // source of truth. Icon-only (no text label) — a real media player's
  // small transport buttons don't caption themselves either.
  const playPauseBtnCompact = el.querySelector<HTMLButtonElement>('#play-pause-btn-compact')!;
  const playPauseIconCompact = el.querySelector<HTMLSpanElement>('#play-pause-icon-compact')!;
  const loopBtnCompact = el.querySelector<HTMLButtonElement>('#loop-btn-compact')!;
  const loopModeLabelCompact = el.querySelector<HTMLSpanElement>('#loop-mode-label-compact')!;
  const prevSongBtnCompact = el.querySelector<HTMLButtonElement>('#prev-song-btn-compact')!;
  const nextSongBtnCompact = el.querySelector<HTMLButtonElement>('#next-song-btn-compact')!;
  const beatStripMain = el.querySelector<HTMLDivElement>('#beat-strip-main')!;
  const metronomeVisualWrap = el.querySelector<HTMLDivElement>('#metronome-visual-wrap')!;
  const clickSettingsBtn = el.querySelector<HTMLButtonElement>('#click-settings-btn')!;
  const clickSoundMenu = el.querySelector<HTMLDivElement>('#click-sound-menu')!;
  const setlistPanelTitle = el.querySelector<HTMLSpanElement>('#setlist-panel-title')!;
  const setlistPanelMeta = el.querySelector<HTMLDivElement>('#setlist-panel-meta')!;
  const setlistPanelScroll = el.querySelector<HTMLDivElement>('#setlist-panel-scroll')!;
  const setlistPickerTrigger = el.querySelector<HTMLButtonElement>('#setlist-picker-trigger')!;
  const setlistPickerMenu = el.querySelector<HTMLDivElement>('#setlist-picker-menu')!;
  const compactSetlistPickerTrigger = el.querySelector<HTMLButtonElement>('#compact-setlist-picker-trigger')!;
  const compactSetlistPickerMenu = el.querySelector<HTMLDivElement>('#setlist-picker-menu-compact')!;
  const compactSetlistPickerMenuOptions = el.querySelector<HTMLDivElement>('#setlist-picker-menu-compact-options')!;
  // Two instances of the same notes-panel content — wide/landscape shows
  // #notes-panel-wide permanently (below the transport buttons, in the free
  // space the tablet layout has); narrow/portrait shows #notes-panel-compact
  // instead, toggled against the setlist itself via #setlist-notes-view-
  // toggle (only one of the two is ever visible in a given layout at once,
  // driven by the same CSS media query that swaps every other compact/wide
  // pair in this file — but BOTH exist in the DOM simultaneously and are
  // kept in sync, same reasoning as the Play/Pause button pairing). The
  // toggle row and setlist-panel-scroll are only relevant/visible in the
  // narrow layout; wide layout's setlist panel has no notes toggle at all
  // (its own notes panel already sits permanently in the other column).
  const notesPanelWide = el.querySelector<HTMLDivElement>('#notes-panel-wide')!;
  const notesPanelCompact = el.querySelector<HTMLDivElement>('#notes-panel-compact')!;
  const viewToggleSetlistBtn = el.querySelector<HTMLButtonElement>('#view-toggle-setlist-btn')!;
  const viewToggleNotesBtn = el.querySelector<HTMLButtonElement>('#view-toggle-notes-btn')!;
  const compactNotesInlineTools = el.querySelector<HTMLSpanElement>('#compact-notes-inline-tools')!;
  const compactNotesOptionsTrigger = el.querySelector<HTMLButtonElement>('#compact-notes-options-trigger')!;
  const compactLayoutMediaQuery = window.matchMedia('(max-width: 899px), (orientation: portrait)');
  // A second, smaller instance of the SAME beat-circle visual as the main
  // #metronome-visual-wrap row (all subdivisions, not just main beats —
  // unlike the old single watch-square this replaces) — lives in the
  // setlist panel's own fixed header instead, so someone watching the
  // setlist for "which section am I in" doesn't lose sight of the click
  // just because it used to only ever show up by the transport controls.
  // Rebuilt per-song (same beatsPerMeasure/accentFirst as the main row —
  // see renderMetronomeVisual), NOT built once, since a metronome-mode
  // song's own beatsPerMeasure can differ between songs in a setlist.
  const beatStripHeader = el.querySelector<HTMLDivElement>('#beat-strip-header')!;
  const headerBeatVisualWrap = el.querySelector<HTMLDivElement>('#header-beat-visual-wrap')!;
  const clickSettingsBtnCompact = el.querySelector<HTMLButtonElement>('#click-settings-btn-compact')!;
  const clickSoundMenuCompact = el.querySelector<HTMLDivElement>('#click-sound-menu-compact')!;
  const transportRowCompact = el.querySelector<HTMLDivElement>('#transport-row-compact')!;
  const transportBeatFlashEls = Array.from(transportRowCompact.querySelectorAll<HTMLSpanElement>('.btn-transport-beat-flash'));
  let headerBeatVisualEls: BeatVisualEls = { pills: [], labels: [] };

  function readSavedSetlistWidth(): number {
    try {
      const saved = Number.parseFloat(localStorage.getItem(PLAYER_SETLIST_WIDTH_STORAGE_KEY) ?? '');
      return Number.isFinite(saved) ? saved : DEFAULT_SETLIST_WIDTH_PERCENT;
    } catch {
      return DEFAULT_SETLIST_WIDTH_PERCENT;
    }
  }

  let preferredSetlistWidthPercent = readSavedSetlistWidth();
  let activeResizePointerId: number | null = null;
  let preferredNotesPreviewHeightPercent = readSavedNotesPreviewHeight();
  let preferredNotesPreviewWidthPercent = readSavedNotesPreviewWidth();
  let activeNotesResizePointerId: number | null = null;
  let progressLabelFitRafId: number | null = null;
  let titleFitRafId: number | null = null;
  let notesTabCollapseRafId: number | null = null;
  let notesSavedIndicatorTimeoutId: number | null = null;

  /** Whether the current section's notes and the upcoming section's preview
   * stack vertically (the original layout — current above, next below) or
   * sit side by side horizontally. A per-user preference, not tied to
   * viewport width — someone on a wide tablet may still prefer stacked, and
   * side-by-side can still make sense narrow if the notes themselves are
   * short. Persisted the same way the resize percentages are. */
  function readSavedNotesLayout(): NotesLayout {
    try {
      const saved = localStorage.getItem(PLAYER_NOTES_LAYOUT_STORAGE_KEY);
      return saved === 'side-by-side' || saved === 'stacked' || saved === 'current-only' ? saved : DEFAULT_NOTES_LAYOUT;
    } catch {
      return DEFAULT_NOTES_LAYOUT;
    }
  }

  let notesLayout: NotesLayout = readSavedNotesLayout();

  function saveNotesLayout(): void {
    try {
      localStorage.setItem(PLAYER_NOTES_LAYOUT_STORAGE_KEY, notesLayout);
    } catch {
      // Session-only preference still works.
    }
  }

  /** Whether the narrow/portrait header's visual-click widget (the row of
   * numbered beat pills next to the transport buttons) is collapsed away
   * to reclaim vertical space — a per-user preference, same persistence
   * pattern as notesLayout above. Collapsing it doesn't lose the beat
   * indicator entirely: while collapsed AND a real click is playing, the
   * transport buttons themselves take over as the indicator instead (see
   * updateTransportBeatIndicators) — this toggle only ever controls WHERE
   * the beat shows, never whether it's visible at all. */
  function readSavedClickWidgetCollapsed(): boolean {
    try {
      return localStorage.getItem(PLAYER_CLICK_WIDGET_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  let clickWidgetCollapsed = readSavedClickWidgetCollapsed();

  function saveClickWidgetCollapsed(): void {
    try {
      localStorage.setItem(PLAYER_CLICK_WIDGET_COLLAPSED_STORAGE_KEY, String(clickWidgetCollapsed));
    } catch {
      // Session-only preference still works.
    }
  }

  /** How far ahead of the actual playhead the notes panel's "Current"
   * section swaps to show the UPCOMING section instead — lets a musician
   * start reading the next part before they need to play it, rather than
   * only finding out once the audio has already crossed into it. A GLOBAL
   * Player preference (unlike markerLeadInSeconds/markerLeadInBeats, which
   * are per-song count-in settings with a genuinely different purpose —
   * seek/countdown timing, not notes display) — same {seconds, beats}
   * shape and the same "beats only meaningful when this song's BPM is
   * known" restriction, but stored and edited once for the whole Player,
   * not per song. 0 (the default) means no early swap at all — the
   * historical, exact-playhead-position behavior every layout already had
   * before this setting existed. Read from the gear menu next to the
   * transport (see renderClickSoundMenu — the same menu now doubles as a
   * general "click + notes timing" settings surface). */
  function readSavedNotesEarlySwapSeconds(): number {
    try {
      const saved = Number.parseFloat(localStorage.getItem(PLAYER_NOTES_EARLY_SWAP_SECONDS_STORAGE_KEY) ?? '');
      return Number.isFinite(saved) && saved >= 0 ? saved : 0;
    } catch {
      return 0;
    }
  }

  function readSavedNotesEarlySwapBeats(): number | null {
    try {
      const saved = Number.parseInt(localStorage.getItem(PLAYER_NOTES_EARLY_SWAP_BEATS_STORAGE_KEY) ?? '', 10);
      return Number.isFinite(saved) && saved > 0 ? saved : null;
    } catch {
      return null;
    }
  }

  let notesEarlySwapSeconds = readSavedNotesEarlySwapSeconds();
  let notesEarlySwapBeats = readSavedNotesEarlySwapBeats();

  function saveNotesEarlySwap(): void {
    try {
      localStorage.setItem(PLAYER_NOTES_EARLY_SWAP_SECONDS_STORAGE_KEY, String(notesEarlySwapSeconds));
      if (notesEarlySwapBeats === null) localStorage.removeItem(PLAYER_NOTES_EARLY_SWAP_BEATS_STORAGE_KEY);
      else localStorage.setItem(PLAYER_NOTES_EARLY_SWAP_BEATS_STORAGE_KEY, String(notesEarlySwapBeats));
    } catch {
      // Session-only preference still works.
    }
  }

  /** The early-swap amount actually used right now, in seconds — mirrors
   * computeCountInPreview's own beats->seconds conversion (secondsPerBeat
   * from the CURRENT song's own BPM, not a frozen value), so retuning a
   * song's tempo keeps this correct automatically rather than going stale
   * against whatever BPM was in effect when the beat count was chosen.
   * Falls back to the plain seconds value whenever beats aren't set, or
   * the current song has no known BPM to convert them against at all
   * (e.g. mid-song-switch, or a tracks-only song with no click). */
  function currentNotesEarlySwapSeconds(): number {
    if (notesEarlySwapBeats === null) return notesEarlySwapSeconds;
    const song = getCurrentSong();
    if (!song) return notesEarlySwapSeconds;
    return notesEarlySwapBeats * secondsPerBeat(song.metronomeSettings);
  }

  /** Off by default (0) — same "must be explicitly opted into" stance as
   * the early-swap setting above, since the Preview badge is an extra
   * heads-up most people won't want until they discover they need it. */
  function readSavedNotesPreviewWarningSeconds(): number {
    try {
      const saved = Number.parseFloat(localStorage.getItem(PLAYER_NOTES_PREVIEW_WARNING_SECONDS_STORAGE_KEY) ?? '');
      return Number.isFinite(saved) && (NOTES_PREVIEW_WARNING_OPTIONS as readonly number[]).includes(saved) ? saved : 0;
    } catch {
      return 0;
    }
  }

  let notesPreviewWarningSeconds = readSavedNotesPreviewWarningSeconds();

  function saveNotesPreviewWarningSeconds(): void {
    try {
      localStorage.setItem(PLAYER_NOTES_PREVIEW_WARNING_SECONDS_STORAGE_KEY, String(notesPreviewWarningSeconds));
    } catch {
      // Session-only preference still works.
    }
  }

  /** Keep timeline labels readable without needlessly abbreviating the whole
   * song. Every section first gets its full marker name at the same shared
   * type size; only the individual sections whose text genuinely overflows
   * fall back to their saved short label. Measuring the rendered element
   * also accounts for the real font, padding, and current divider position. */
  function fitProgressMarkerLabels(): void {
    progressSections.querySelectorAll<HTMLDivElement>('.progress-tick-label').forEach((label) => {
      const fullLabel = label.dataset.fullLabel ?? '';
      const shortLabel = label.dataset.shortLabel ?? fullLabel;
      label.textContent = fullLabel;
      label.classList.remove('uses-short-label');

      // A detached/hidden player has no useful geometry. Leave the complete
      // label in place; the ResizeObserver will run again once it has a box.
      if (label.clientWidth <= 0) return;
      if (label.scrollWidth > label.clientWidth + 0.5 && shortLabel !== fullLabel) {
        label.textContent = shortLabel;
        label.classList.add('uses-short-label');
      }
    });
  }

  function scheduleProgressMarkerLabelFit(): void {
    if (progressLabelFitRafId !== null) cancelAnimationFrame(progressLabelFitRafId);
    progressLabelFitRafId = requestAnimationFrame(() => {
      progressLabelFitRafId = null;
      fitProgressMarkerLabels();
    });
  }

  /** Each step shrinks the title further before falling back to a plain
   * ellipsis. Goes down to 0.45x — .current-song-title's base size is
   * tuned for a tablet/desktop-width column (clamp() floors at 2.55rem),
   * so a 390px phone screen genuinely needs a much bigger reduction than a
   * wider layout ever would just to fit an ordinary 4-5 word title on one
   * line at all; anything still too long even at 0.45x is exactly what the
   * ellipsis fallback is for, rather than shrinking indefinitely into
   * illegibility. */
  const TITLE_FIT_SCALE_STEPS = [1, 0.9, 0.8, 0.7, 0.6, 0.55, 0.5, 0.45];

  /** Same "measure, then step down" technique as fitProgressMarkerLabels
   * just above, applied to the current song's title instead of a marker
   * tick label: try the full base size first, shrink one step at a time
   * only while the title genuinely overflows its column, and stop at the
   * smallest step regardless of whether it still overflows — .current-
   * song-title's own overflow:hidden + text-overflow:ellipsis (see
   * style.css) takes over from there rather than shrinking indefinitely
   * into illegibility. */
  function fitCurrentSongTitle(): void {
    if (currentTitle.clientWidth <= 0) return;
    for (const scale of TITLE_FIT_SCALE_STEPS) {
      currentTitle.style.setProperty('--title-fit-scale', String(scale));
      if (currentTitle.scrollWidth <= currentTitle.clientWidth + 0.5) return;
    }
  }

  function scheduleTitleFit(): void {
    if (titleFitRafId !== null) cancelAnimationFrame(titleFitRafId);
    titleFitRafId = requestAnimationFrame(() => {
      titleFitRafId = null;
      fitCurrentSongTitle();
    });
  }

  function updateNotesInlineTabCollapse(): void {
    notesPanelCompact.querySelectorAll<HTMLElement>('.notes-title-inline-tabs').forEach((tabs) => {
      tabs.classList.remove('is-tab-collapsed');
      if (tabs.clientWidth <= 0) return;
      const children = Array.from(tabs.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
      const firstTop = children[0]?.offsetTop ?? 0;
      const hasWrapped = children.some((child) => Math.abs(child.offsetTop - firstTop) > 0.5);
      const hasOverflow = tabs.scrollWidth > tabs.clientWidth + 0.5;
      tabs.classList.toggle('is-tab-collapsed', hasWrapped || hasOverflow);
    });
  }

  function scheduleNotesInlineTabCollapse(): void {
    if (notesTabCollapseRafId !== null) cancelAnimationFrame(notesTabCollapseRafId);
    notesTabCollapseRafId = requestAnimationFrame(() => {
      notesTabCollapseRafId = null;
      updateNotesInlineTabCollapse();
    });
  }

  function setCurrentTitle(text: string): void {
    currentTitle.textContent = text;
    scheduleTitleFit();
  }

  function setlistWidthBounds(): { min: number; max: number } {
    const width = performanceGrid.getBoundingClientRect().width;
    if (width <= 0) return { min: 0, max: MAX_SETLIST_WIDTH_PERCENT };
    const min = Math.min(MAX_SETLIST_WIDTH_PERCENT, (MIN_SETLIST_WIDTH_PX / width) * 100);
    const maxFromLeftColumn = ((width - MIN_NOW_PLAYING_WIDTH_PX - PERFORMANCE_SPLITTER_WIDTH_PX) / width) * 100;
    return { min, max: Math.max(min, Math.min(MAX_SETLIST_WIDTH_PERCENT, maxFromLeftColumn)) };
  }

  function applySetlistWidth(percent: number): number {
    const bounds = setlistWidthBounds();
    const clamped = Math.min(bounds.max, Math.max(bounds.min, percent));
    performanceGrid.style.setProperty('--setlist-panel-width', `${clamped}%`);
    performanceSplitter.setAttribute('aria-valuemin', String(Math.round(bounds.min)));
    performanceSplitter.setAttribute('aria-valuemax', String(Math.round(bounds.max)));
    performanceSplitter.setAttribute('aria-valuenow', String(Math.round(clamped)));
    performanceSplitter.setAttribute('aria-valuetext', `Setlist is ${Math.round(clamped)}% of the player width`);
    return clamped;
  }

  function savePreferredSetlistWidth(): void {
    try {
      localStorage.setItem(PLAYER_SETLIST_WIDTH_STORAGE_KEY, preferredSetlistWidthPercent.toFixed(1));
    } catch {
      // Storage can be unavailable in private browsing; resizing still works for this session.
    }
  }

  function readSavedNotesPreviewHeight(): number {
    try {
      const saved = Number.parseFloat(localStorage.getItem(PLAYER_NOTES_PREVIEW_HEIGHT_STORAGE_KEY) ?? '');
      return Number.isFinite(saved) ? saved : DEFAULT_NOTES_PREVIEW_HEIGHT_PERCENT;
    } catch {
      return DEFAULT_NOTES_PREVIEW_HEIGHT_PERCENT;
    }
  }

  function applyNotesPreviewHeight(percent: number): number {
    const clamped = Math.min(60, Math.max(16, percent));
    notesPanelWide.style.setProperty('--notes-preview-height', `${clamped}%`);
    notesPanelCompact.style.setProperty('--notes-preview-height', `${clamped}%`);
    return clamped;
  }

  function readSavedNotesPreviewWidth(): number {
    try {
      const saved = Number.parseFloat(localStorage.getItem(PLAYER_NOTES_PREVIEW_WIDTH_STORAGE_KEY) ?? '');
      return Number.isFinite(saved) ? saved : DEFAULT_NOTES_PREVIEW_WIDTH_PERCENT;
    } catch {
      return DEFAULT_NOTES_PREVIEW_WIDTH_PERCENT;
    }
  }

  /** Side-by-side layout's own split — the SAME idea as
   * applyNotesPreviewHeight above (a resizable share the Next panel claims,
   * left to the Current panel to fill the rest), just driven by horizontal
   * width instead of vertical height, and stored separately so switching
   * layouts doesn't clobber whichever split ratio the user already dialed
   * in for the other orientation. */
  function applyNotesPreviewWidth(percent: number): number {
    const clamped = Math.min(60, Math.max(16, percent));
    notesPanelWide.style.setProperty('--notes-preview-width', `${clamped}%`);
    notesPanelCompact.style.setProperty('--notes-preview-width', `${clamped}%`);
    scheduleNotesInlineTabCollapse();
    return clamped;
  }

  function getVisibleNotesPanel(): HTMLDivElement {
    return window.getComputedStyle(notesPanelWide).display !== 'none' ? notesPanelWide : notesPanelCompact;
  }

  function savePreferredNotesPreviewHeight(): void {
    try {
      localStorage.setItem(PLAYER_NOTES_PREVIEW_HEIGHT_STORAGE_KEY, preferredNotesPreviewHeightPercent.toFixed(1));
    } catch {
      // Session-only resizing still works.
    }
  }

  function savePreferredNotesPreviewWidth(): void {
    try {
      localStorage.setItem(PLAYER_NOTES_PREVIEW_WIDTH_STORAGE_KEY, preferredNotesPreviewWidthPercent.toFixed(1));
    } catch {
      // Session-only resizing still works.
    }
  }

  /** Drags along whichever axis the current layout actually splits on —
   * bottom-up height in the stacked layout, right-to-left width in
   * side-by-side. Next sits on the right in plain DOM order (Current, then
   * the handle, then Next — see .notes-content-split.is-side-by-side's own
   * CSS comment for why this is a plain `row`, not a row-reverse), so
   * Next's own width is measured from the panel's RIGHT edge inward,
   * mirroring how height is already measured from the panel's BOTTOM edge
   * upward: dragging the handle further from that edge grows Next. */
  function resizeNotesPreviewFromPointer(clientX: number, clientY: number): void {
    const targetPanel = getVisibleNotesPanel();
    const box = targetPanel.getBoundingClientRect();
    if (notesLayout === 'side-by-side') {
      if (box.width <= 0) return;
      const previewWidth = ((box.right - clientX) / box.width) * 100;
      preferredNotesPreviewWidthPercent = applyNotesPreviewWidth(previewWidth);
    } else {
      if (box.height <= 0) return;
      const previewHeight = ((box.bottom - clientY) / box.height) * 100;
      preferredNotesPreviewHeightPercent = applyNotesPreviewHeight(previewHeight);
    }
  }

  function stopNotesResize(pointerId?: number): void {
    if (activeNotesResizePointerId === null || (pointerId !== undefined && pointerId !== activeNotesResizePointerId)) return;
    const activePanel = getVisibleNotesPanel();
    const handle = activePanel.querySelector<HTMLElement>('.notes-resize-handle');
    if (handle?.hasPointerCapture(activeNotesResizePointerId)) handle.releasePointerCapture(activeNotesResizePointerId);
    activeNotesResizePointerId = null;
    activePanel.classList.remove('is-resizing-notes');
    document.body.classList.remove('is-resizing-performance');
    if (notesLayout === 'side-by-side') savePreferredNotesPreviewWidth();
    else savePreferredNotesPreviewHeight();
  }

  preferredNotesPreviewHeightPercent = applyNotesPreviewHeight(preferredNotesPreviewHeightPercent);
  preferredNotesPreviewWidthPercent = applyNotesPreviewWidth(preferredNotesPreviewWidthPercent);

  function applyNotesLayout(): void {
    notesPanelWide.classList.toggle('is-side-by-side', notesLayout === 'side-by-side');
    notesPanelCompact.classList.toggle('is-side-by-side', notesLayout === 'side-by-side');
    notesPanelWide.classList.toggle('is-current-only', notesLayout === 'current-only');
    notesPanelCompact.classList.toggle('is-current-only', notesLayout === 'current-only');
  }
  applyNotesLayout();

  /** Applies one of the 3 layout choices from the layout menu (see
   * notesLayoutMenuOpen) — replaces the old 2-way toggleNotesLayout flip
   * now that there's a third option (current-only) a simple flip can't
   * express. A no-op re-render is harmless (renderNotesPanel's own
   * lastRenderedNotesKey guard would normally skip it), but
   * lastRenderedNotesKey is explicitly cleared first since the notes
   * CONTENT hasn't changed — only which chrome/panels are visible around
   * it — which that key alone wouldn't otherwise detect. */
  function setNotesLayout(layout: NotesLayout): void {
    notesLayout = layout;
    applyNotesLayout();
    saveNotesLayout();
    notesLayoutMenuOpen = false;
    lastRenderedNotesKey = null;
    renderNotesPanel();
  }

  function resizeSetlistFromPointer(clientX: number): void {
    const bounds = performanceGrid.getBoundingClientRect();
    if (bounds.width <= 0) return;
    preferredSetlistWidthPercent = ((bounds.right - clientX) / bounds.width) * 100;
    applySetlistWidth(preferredSetlistWidthPercent);
  }

  function stopSetlistResize(pointerId?: number): void {
    if (activeResizePointerId === null || (pointerId !== undefined && pointerId !== activeResizePointerId)) return;
    if (performanceSplitter.hasPointerCapture(activeResizePointerId)) {
      performanceSplitter.releasePointerCapture(activeResizePointerId);
    }
    activeResizePointerId = null;
    performanceGrid.classList.remove('is-resizing');
    document.body.classList.remove('is-resizing-performance');
    savePreferredSetlistWidth();
  }

  performanceSplitter.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    activeResizePointerId = event.pointerId;
    performanceSplitter.setPointerCapture(event.pointerId);
    performanceGrid.classList.add('is-resizing');
    document.body.classList.add('is-resizing-performance');
    resizeSetlistFromPointer(event.clientX);
    event.preventDefault();
  });
  performanceSplitter.addEventListener('pointermove', (event) => {
    if (event.pointerId === activeResizePointerId) resizeSetlistFromPointer(event.clientX);
  });
  performanceSplitter.addEventListener('pointerup', (event) => stopSetlistResize(event.pointerId));
  performanceSplitter.addEventListener('pointercancel', (event) => stopSetlistResize(event.pointerId));
  performanceSplitter.addEventListener('dblclick', () => {
    preferredSetlistWidthPercent = DEFAULT_SETLIST_WIDTH_PERCENT;
    applySetlistWidth(preferredSetlistWidthPercent);
    savePreferredSetlistWidth();
  });
  performanceSplitter.addEventListener('keydown', (event) => {
    const bounds = setlistWidthBounds();
    let nextWidth: number | null = null;
    if (event.key === 'ArrowLeft') nextWidth = preferredSetlistWidthPercent + (event.shiftKey ? 5 : 2);
    if (event.key === 'ArrowRight') nextWidth = preferredSetlistWidthPercent - (event.shiftKey ? 5 : 2);
    if (event.key === 'Home') nextWidth = bounds.min;
    if (event.key === 'End') nextWidth = bounds.max;
    if (nextWidth === null) return;
    event.preventDefault();
    preferredSetlistWidthPercent = applySetlistWidth(nextWidth);
    savePreferredSetlistWidth();
  });
  applySetlistWidth(preferredSetlistWidthPercent);

  function getActiveSetlist(): SetlistRecord | null {
    const { setlists, settings } = appStore.getState();
    return setlists.find((s) => s.id === settings.activeSetlistId) ?? null;
  }

  function getEntrySong(index: number): SongRecord | null {
    const setlist = getActiveSetlist();
    if (!setlist) return null;
    const entry = setlist.entries[index];
    if (!entry) return null;
    return appStore.getState().songs.find((s) => s.id === entry.songId) ?? null;
  }

  /** The song actually loaded into the controller right now (not just
   * "whatever the active setlist index points at" — those briefly disagree
   * during a song switch), used by the lead-in picker/countdown to know
   * whether a synthetic click (and thus a known BPM) is available. */
  function getCurrentSong(): SongRecord | null {
    return appStore.getState().songs.find((s) => s.id === currentSongId) ?? null;
  }

  function closePlayerHeaderMenus(): void {
    setlistPickerMenu.hidden = true;
    compactSetlistPickerMenu.hidden = true;
    compactSetlistPickerMenu.classList.remove('is-open');
    clickSoundMenu.hidden = true;
    clickSoundMenuCompact.hidden = true;
    setlistPickerTrigger.setAttribute('aria-expanded', 'false');
    compactSetlistPickerTrigger.setAttribute('aria-expanded', 'false');
    compactNotesOptionsTrigger.setAttribute('aria-expanded', String(notesLayoutMenuOpen));
    clickSettingsBtn.setAttribute('aria-expanded', 'false');
    clickSettingsBtnCompact.setAttribute('aria-expanded', 'false');
  }

  function renderPlayerHeaderMenus(): void {
    const { setlists, settings, songs } = appStore.getState();
    const setlistOptions =
      setlists.length > 0
        ? setlists
            .map(
              (setlist) => `<button type="button" class="setlist-picker-option${setlist.id === settings.activeSetlistId ? ' is-selected' : ''}" data-setlist-id="${setlist.id}">
                <span>${escapeHtml(setlist.name)}</span>
                <small>${setlistMetaLine(setlist, songs)}</small>
                ${setlist.id === settings.activeSetlistId ? '<strong>Active</strong>' : ''}
              </button>`,
            )
            .join('')
        : '<p class="player-header-menu-empty">No setlists yet.</p>';

    const activeSetlist = getActiveSetlist();
    setlistPickerMenu.innerHTML = `${setlistOptions}<button type="button" class="setlist-picker-manage" data-player-manage="edit-setlist" ${activeSetlist ? '' : 'disabled'}>Edit current setlist</button>`;
    compactSetlistPickerMenuOptions.innerHTML = setlistOptions;
  }

  /** Lets the current song borrow just the SOUND (and its accent/volume)
   * from one of the standalone Metronome tab's saved presets — reusing that
   * existing save/name/recall system (metronomePresetStore) rather than
   * building a second, separate preset store just for the Player. Only
   * `sound`, `accentFirstBeat`, and `clickVolume` are copied onto the song's
   * own metronomeSettings; this is deliberately NOT a full "load preset"
   * (unlike the Metronome tab's own Load button) — a song's BPM/time
   * signature/subdivision/count-in are already tuned per-song in the Song
   * Library and must never be silently overwritten by picking a sound here. */
  /** "Show/Hide visual click" — the wide/landscape layout's own gear menu
   * only. The narrow/portrait compact row doesn't offer the beat-pill
   * widget at all anymore (a 390px phone genuinely has no room for it
   * alongside 4 full-size transport buttons — it was measured collapsing
   * to 0px in practice), so this toggle would have nothing to affect
   * there; the compact gear menu keeps only the click-sound/preset picker.
   * The click's beat is still visible on mobile via the transport buttons
   * themselves flashing gold (.shows-beat-indicators), unrelated to this
   * toggle. Only rendered at all when there's an actual click to show/hide
   * (a tracks-only song has nothing for this toggle to affect). */
  function renderClickWidgetVisibilityToggle(): string {
    if (!beatStripHeader.classList.contains('has-click')) return '';
    const label = clickWidgetCollapsed ? 'Show visual click' : 'Hide visual click';
    // No separate .player-header-menu-divider needed anymore — every
    // direct-child row in a .player-header-menu now gets its own bottom
    // border by default (see that rule's own comment), so this row is
    // already visually separated from whatever follows it without a
    // dedicated spacer element.
    return `<button type="button" data-action="toggle-click-widget-collapsed">${label}</button>`;
  }

  /** Appended after the click-sound preset list in the SAME gear menu,
   * rather than getting its own dedicated trigger button — click sound is
   * itself a rarely-touched setting, and practice speed is rarer still, so
   * both belong tucked in the one overflow menu rather than adding more
   * permanently-visible buttons to an already-tight transport row (the
   * narrow/compact row in particular has no width to spare — see its own
   * doc comment). Returns '' (renders nothing) when it isn't actually
   * usable right now — Rehearsal mode, a real file-backed song loaded; see
   * PlaybackController.setPlaybackRate's own doc comment for why a
   * metronome-mode song or a Live setlist can't use this at all — rather
   * than showing a dead control that would do nothing if touched.
   *
   * A continuous slider, not fixed presets — an earlier version shipped
   * six buttons 10 percentage points apart, which read as too coarse a
   * jump for dialing in a specific practice tempo. */
  function renderPracticeSpeedSection(): string {
    const state = controller.getState();
    if (!isRehearsalMode || state.activeEngine !== 'file') return '';
    const percent = Math.round(state.playbackRate * 100);
    return `
      <div class="player-header-menu-section">
        <div class="row" style="margin:0 0 6px; gap:6px; align-items:center; flex-wrap:nowrap">
          <span class="text-dim" style="white-space:nowrap; flex-shrink:0">Practice speed</span>
          <div class="row practice-speed-stepper" style="margin-left:auto; gap:6px; flex-wrap:nowrap; flex-shrink:0">
            <button type="button" class="practice-speed-step-btn" data-step="-5" aria-label="Decrease practice speed by 5 percent">−5</button>
            <span class="practice-speed-readout">${percent}%</span>
            <button type="button" class="practice-speed-step-btn" data-step="5" aria-label="Increase practice speed by 5 percent">+5</button>
          </div>
        </div>
        <input type="range" class="practice-speed-slider" min="50" max="120" step="1" value="${percent}" style="width:100%" aria-label="Practice speed" />
        <p class="practice-speed-hint" style="margin:6px 0 0; font-size:var(--text-sm)">Changes the song's speed without changing its pitch. Resets to 100% next time you load a song.</p>
      </div>
    `;
  }

  /** Reads the slider LIVE, on every 'input' tick while dragging, updating
   * only the readout text and calling setPlaybackRate — never re-running
   * renderClickSoundMenu()'s innerHTML rebuild here, which would tear out
   * and replace the very slider element mid-drag and abort the browser's
   * native drag gesture. Delegated (one listener per menu instance, not
   * one per slider) for the same reason every other menu handler here is —
   * the slider itself is rebuilt fresh each time the menu opens. */
  function handlePracticeSpeedInput(event: Event): void {
    const slider = event.target as HTMLElement;
    if (!slider.matches('.practice-speed-slider')) return;
    const percent = Number((slider as HTMLInputElement).value);
    controller.setPlaybackRate(percent / 100);
    const readout = slider.closest('.player-header-menu-section')?.querySelector<HTMLElement>('.practice-speed-readout');
    if (readout) readout.textContent = `${percent}%`;
  }

  function renderClickSoundMenu(): void {
    const song = getCurrentSong();
    const visibilityToggle = renderClickWidgetVisibilityToggle();
    const speedSection = renderPracticeSpeedSection();
    if (!song) {
      clickSoundMenu.innerHTML = visibilityToggle + '<p class="player-header-menu-empty">No song loaded.</p>' + speedSection;
      clickSoundMenuCompact.innerHTML = '<p class="player-header-menu-empty">No song loaded.</p>' + speedSection;
      return;
    }
    if (!metronomePresetStore.isLoaded()) {
      clickSoundMenu.innerHTML = visibilityToggle + '<p class="player-header-menu-empty">Loading…</p>' + speedSection;
      clickSoundMenuCompact.innerHTML = '<p class="player-header-menu-empty">Loading…</p>' + speedSection;
      return;
    }
    const presets = metronomePresetStore.getPresets();
    const currentSound = song.metronomeSettings.sound;
    const presetRows = presets
      .map(
        (preset) => `<button type="button" class="click-sound-option${preset.settings.sound === currentSound ? ' is-selected' : ''}" data-preset-id="${preset.id}">
          <span>${escapeHtml(preset.name)}</span>
          <small>${CLICK_SOUND_LABELS[preset.settings.sound]}</small>
        </button>`,
      )
      .join('');
    const manageLink = '<button type="button" data-action="manage-click-presets">Manage presets in Metronome…</button>';
    const presetsHtml =
      presets.length > 0
        ? `${presetRows}${manageLink}`
        : `<p class="player-header-menu-empty">No saved presets yet — save one in the Metronome tab.</p>${manageLink}`;
    clickSoundMenu.innerHTML = visibilityToggle + presetsHtml + speedSection;
    clickSoundMenuCompact.innerHTML = presetsHtml + speedSection;
  }

  /** The "bring the next section's notes in early" setting, appended to the
   * notes-layout menu — it's a notes-panel behavior, not a click/playback
   * setting, so it lives with Stacked/Side by Side/Current Only rather than
   * in the transport gear menu. A GLOBAL preference (see
   * notesEarlySwapSeconds/notesEarlySwapBeats' own comment for why this
   * isn't per-song like the count-in lead-in), so `song` here is only used
   * to decide whether the beat-count options are offered at all (same
   * "only when BPM is known" restriction the count-in picker already uses)
   * — never to read/store a per-song value. */
  function renderNotesEarlySwapSection(song: SongRecord | null): string {
    const hasKnownBpm = song !== null && (song.mode === 'metronome' || song.syntheticClickEnabled);
    const offOption = `<button type="button" class="lead-in-option${notesEarlySwapSeconds === 0 && notesEarlySwapBeats === null ? ' is-selected' : ''}" data-action="set-notes-early-swap-seconds" data-seconds="0">Off</button>`;
    const options = hasKnownBpm
      ? MARKER_LEAD_IN_BEAT_OPTIONS.map(
          (beats) =>
            `<button type="button" class="lead-in-option${beats === notesEarlySwapBeats ? ' is-selected' : ''}" data-action="set-notes-early-swap-beats" data-beats="${beats}">${beats}ct</button>`,
        ).join('')
      : MARKER_LEAD_IN_OPTIONS.map(
          (seconds) =>
            `<button type="button" class="lead-in-option${notesEarlySwapBeats === null && seconds === notesEarlySwapSeconds ? ' is-selected' : ''}" data-action="set-notes-early-swap-seconds" data-seconds="${seconds}">${seconds}s</button>`,
        ).join('');
    return `
      <div class="player-header-menu-section">
        <div class="row" style="margin:0 0 6px; gap:6px; align-items:center">
          <span class="text-dim">Preview timing</span>
        </div>
        <div class="lead-in-picker-grid">${offOption}${options}</div>
      </div>
      ${renderNotesPreviewWarningSection()}
    `;
  }

  /** The "Preview" badge's own lead time — how far ahead of the early-swap
   * flip above the CURRENT section's notes start showing a heads-up that
   * they're about to be replaced (see isNotesSwitchWarningActive/
   * NOTES_PREVIEW_WARNING_OPTIONS). A separate setting from the early-swap
   * amount itself since the two answer different questions: how far ahead
   * to reveal the NEXT section's content, vs. how much notice to give
   * before that reveal happens. Off by default — no per-song BPM-based
   * beat option here (unlike the early-swap picker just above) since this
   * is meant to stay a short, fixed heads-up window, not something that
   * needs to track a song's tempo. */
  function renderNotesPreviewWarningSection(): string {
    const offOption = `<button type="button" class="lead-in-option${notesPreviewWarningSeconds === 0 ? ' is-selected' : ''}" data-action="set-notes-preview-warning-seconds" data-seconds="0">Off</button>`;
    const options = NOTES_PREVIEW_WARNING_OPTIONS.map(
      (seconds) =>
        `<button type="button" class="lead-in-option${seconds === notesPreviewWarningSeconds ? ' is-selected' : ''}" data-action="set-notes-preview-warning-seconds" data-seconds="${seconds}">${seconds}s</button>`,
    ).join('');
    return `
      <div class="player-header-menu-section">
        <div class="row" style="margin:0 0 6px; gap:6px; align-items:center">
          <span class="text-dim">Switching heads-up</span>
        </div>
        <div class="lead-in-picker-grid">${offOption}${options}</div>
      </div>
    `;
  }

  function renderNotesLayoutDrawer(song: SongRecord | null): string {
    return `
      <div class="player-utility-drawer notes-layout-drawer${notesLayoutMenuOpen ? ' is-open' : ''}" ${notesLayoutMenuOpen ? '' : 'hidden'}>
        <button type="button" class="player-utility-drawer-scrim" data-action="close-notes-layout-menu" aria-label="Close notes settings"></button>
        <section class="player-utility-drawer-sheet notes-layout-drawer-sheet" role="dialog" aria-modal="true" aria-label="Notes Settings">
          <div class="player-utility-drawer-handle" aria-hidden="true"></div>
          <div class="player-utility-drawer-heading">
            <h2>Notes Settings</h2>
            <button type="button" class="player-utility-drawer-close" data-action="close-notes-layout-menu" aria-label="Close">×</button>
          </div>
          <div class="player-utility-drawer-options notes-layout-drawer-options">
            <section class="notes-drawer-section" aria-label="Layout">
              <div class="notes-drawer-section-label">Layout</div>
              <button type="button" class="player-utility-option${notesLayout === 'stacked' ? ' is-selected' : ''}" data-action="set-notes-layout" data-notes-layout="stacked"><span>Stacked</span><span class="player-utility-option-check">✓</span></button>
              <button type="button" class="player-utility-option${notesLayout === 'side-by-side' ? ' is-selected' : ''}" data-action="set-notes-layout" data-notes-layout="side-by-side"><span>Side by Side</span><span class="player-utility-option-check">✓</span></button>
              <button type="button" class="player-utility-option${notesLayout === 'current-only' ? ' is-selected' : ''}" data-action="set-notes-layout" data-notes-layout="current-only"><span>Current Only</span><span class="player-utility-option-check">✓</span></button>
            </section>
            ${renderNotesEarlySwapSection(song)}
          </div>
        </section>
      </div>
    `;
  }

  async function stopAndNavigateToEditor(hash: string): Promise<void> {
    if (controller.getState().status === 'PLAYING') {
      const confirmed = await confirmDialog({
        title: 'Stop playback to edit?',
        message: 'Editing will leave the Player and stop the current song.',
        confirmLabel: 'Stop and Edit',
        danger: true,
      });
      if (!confirmed) return;
    }
    controller.stop();
    closePlayerHeaderMenus();
    window.location.hash = hash;
  }

  setlistPickerTrigger.addEventListener('click', () => {
    const shouldOpen = setlistPickerMenu.hidden;
    closePlayerHeaderMenus();
    if (shouldOpen) {
      renderPlayerHeaderMenus();
      setlistPickerMenu.hidden = false;
      setlistPickerTrigger.setAttribute('aria-expanded', 'true');
    }
  });

  compactSetlistPickerTrigger.addEventListener('click', () => {
    const shouldOpen = compactSetlistPickerMenu.hidden;
    closePlayerHeaderMenus();
    if (shouldOpen) {
      renderPlayerHeaderMenus();
      compactSetlistPickerMenu.hidden = false;
      compactSetlistPickerMenu.classList.add('is-open');
      compactSetlistPickerTrigger.setAttribute('aria-expanded', 'true');
    }
  });

  function handleSetlistPickerMenuClick(event: Event): void {
    const closeAction = (event.target as HTMLElement).closest<HTMLElement>('[data-action="close-setlist-picker"]');
    if (closeAction) {
      event.preventDefault();
      closePlayerHeaderMenus();
      return;
    }
    const action = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-player-manage]')?.dataset.playerManage;
    if (action === 'edit-setlist') {
      event.preventDefault();
      const setlist = getActiveSetlist();
      if (setlist) void stopAndNavigateToEditor(`#/setlists/${encodeURIComponent(setlist.id)}`);
      return;
    }
    const option = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-setlist-id]');
    const setlistId = option?.dataset.setlistId;
    if (!setlistId || setlistId === appStore.getState().settings.activeSetlistId) {
      event.preventDefault();
      closePlayerHeaderMenus();
      return;
    }
    event.preventDefault();
    closePlayerHeaderMenus();
    void guardSongSwitch(async () => {
      pendingMarkerIndex = null;
      markerCountdownTarget = null;
      loopSectionIndex = null;
      setlistSubRowsCollapsed = true;
      await appStore.setActiveSetlist(setlistId);
    });
  }

  setlistPickerMenu.addEventListener('click', handleSetlistPickerMenuClick);
  compactSetlistPickerMenu.addEventListener('click', handleSetlistPickerMenuClick);
  compactSetlistPickerMenu.addEventListener('pointerdown', handleSetlistPickerMenuClick);

  function editCurrentSong(): void {
    const song = getCurrentSong();
    if (song) void stopAndNavigateToEditor(`#/library/${encodeURIComponent(song.id)}`);
  }
  currentSongEditBtn.addEventListener('click', editCurrentSong);

  /** Wide layout's gear (next to the standalone beat visual) and narrow/
   * portrait's own gear (next to the collapsible header pills — see
   * clickSettingsBtnCompact's own history: it replaced a dedicated
   * collapse/expand chevron, since that chevron left no way at all to
   * reach click settings once the pills were collapsed on a phone) open
   * the SAME underlying menu content into two separate DOM instances (same
   * "wide + compact share one render" pattern as the notes panel), so
   * either trigger works regardless of which layout is currently visible. */
  function openClickSoundMenu(trigger: HTMLButtonElement, menu: HTMLDivElement): void {
    const shouldOpen = menu.hidden;
    closePlayerHeaderMenus();
    if (shouldOpen) {
      if (!metronomePresetStore.isLoaded()) {
        void metronomePresetStore.load().then(renderClickSoundMenu);
      }
      renderClickSoundMenu();
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    }
  }
  clickSettingsBtn.addEventListener('click', () => openClickSoundMenu(clickSettingsBtn, clickSoundMenu));
  clickSettingsBtnCompact.addEventListener('click', () => openClickSoundMenu(clickSettingsBtnCompact, clickSoundMenuCompact));

  function handleClickSoundMenuClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const stepBtn = target.closest<HTMLButtonElement>('.practice-speed-step-btn');
    if (stepBtn) {
      // Nudges by a precise, repeatable 5 points — landing on an EXACT
      // percentage by dragging the slider on a small touchscreen is
      // fiddly, so this is a complement to it, not a replacement: the two
      // controls share the same state and stay in sync with each other.
      const currentPercent = Math.round(controller.getState().playbackRate * 100);
      const nextPercent = Math.min(120, Math.max(50, currentPercent + Number(stepBtn.dataset.step)));
      controller.setPlaybackRate(nextPercent / 100);
      const section = stepBtn.closest('.player-header-menu-section');
      const readout = section?.querySelector<HTMLElement>('.practice-speed-readout');
      const slider = section?.querySelector<HTMLInputElement>('.practice-speed-slider');
      if (readout) readout.textContent = `${nextPercent}%`;
      if (slider) slider.value = String(nextPercent);
      return;
    }
    if (target.dataset.action === 'toggle-click-widget-collapsed') {
      clickWidgetCollapsed = !clickWidgetCollapsed;
      saveClickWidgetCollapsed();
      applyClickWidgetCollapsedState(beatStripHeader.classList.contains('has-click'));
      renderClickSoundMenu();
      return;
    }
    if (target.dataset.action === 'manage-click-presets') {
      closePlayerHeaderMenus();
      void stopAndNavigateToEditor('#/metronome');
      return;
    }
    const presetId = target.closest<HTMLButtonElement>('[data-preset-id]')?.dataset.presetId;
    if (!presetId) return;
    const preset = metronomePresetStore.getPresets().find((p) => p.id === presetId);
    const song = getCurrentSong();
    closePlayerHeaderMenus();
    if (!preset || !song) return;
    const updatedSettings = {
      ...song.metronomeSettings,
      sound: preset.settings.sound,
      accentFirstBeat: preset.settings.accentFirstBeat,
      clickVolume: preset.settings.clickVolume,
    };
    void appStore.updateSong(song.id, { metronomeSettings: updatedSettings });
    // The song record update above is picked up next time the song is
    // loaded fresh, but if it's ALREADY loaded/playing right now, neither
    // engine watches the store on its own (see updateMetronomeSettings's
    // own comment in PlaybackController) — push the change live too, or
    // picking a new preset would silently do nothing until the next song
    // switch/reload. clickChannel is re-derived from the active setlist's
    // routing (NOT taken from updatedSettings' own stored value, which is
    // stale/irrelevant now that routing is a setlist-level setting — see
    // resolveOutputSides) so this live update can't clobber the
    // currently-loaded routing back to whatever the song record happens
    // to have on disk.
    if (currentSongId === song.id) {
      const routing = getActiveSetlist()?.routing ?? DEFAULT_REHEARSAL_ROUTING;
      const { clickSide } = resolveOutputSides(routing, song.mode !== 'metronome', true);
      controller.updateMetronomeSettings({ ...updatedSettings, clickChannel: clickSide });
    }
    showToast(`Click sound set to "${preset.name}".`, 'info');
  }
  clickSoundMenu.addEventListener('click', handleClickSoundMenuClick);
  clickSoundMenuCompact.addEventListener('click', handleClickSoundMenuClick);
  clickSoundMenu.addEventListener('input', handlePracticeSpeedInput);
  clickSoundMenuCompact.addEventListener('input', handlePracticeSpeedInput);

  const handlePlayerHeaderOutsideClick = (event: PointerEvent): void => {
    // #click-settings-btn-compact/#click-sound-menu-compact (the narrow/
    // portrait layout's own gear trigger + menu) are siblings of the
    // TRANSPORT BUTTONS inside .transport-and-beats-row-compact, not
    // wrapped in any container of their own — exempting that whole row
    // (as an earlier version of this fix did) also exempted Play/Pause/
    // Prev/Next/Loop, which sit in the very same row, so tapping any of
    // those while the compact menu was open silently left it open instead
    // of closing it like every other "outside" tap does. Matching the
    // trigger/menu themselves directly (plus their :has() ids, since
    // .matches() alone won't reach descendants of the menu) keeps the
    // narrow exemption without also covering the transport buttons beside
    // them. composedPath() (captured at dispatch time), NOT
    // event.target.closest(...) — the visual-click toggle button INSIDE
    // that menu triggers a synchronous innerHTML rebuild of its own parent
    // menu (see handleClickSoundMenuClick), which detaches the clicked
    // button from the DOM before this document-level listener runs later
    // in the same bubble phase; .closest() on an already-detached node
    // can't find any ancestor and always returns null, which made every
    // click on that toggle look like an outside click and close the menu
    // it had just updated.
    //
    // pointerdown, NOT click — Play/Pause (and a couple other buttons)
    // call event.preventDefault() on their own pointerup handler, which
    // per spec suppresses the browser's compatibility click event
    // entirely. A plain 'click' listener here never even saw those taps,
    // so tapping Play/Pause while a header menu was open silently left it
    // open instead of dismissing it like every other "outside" tap does.
    // pointerdown fires unconditionally before any handler on the target
    // has a chance to preventDefault anything.
    const path = event.composedPath();
    // composedPath() already includes every ancestor of the actual click
    // target, so matching the trigger/menu's own ids here is enough — no
    // need for an extra .closest() call on top.
    const isInsideCompactGear = path.some((node) => node instanceof Element && (node.id === 'click-settings-btn-compact' || node.id === 'click-sound-menu-compact'));
    if (isInsideCompactGear) return;
    if (!path.some((node) => node instanceof Element && node.matches('.setlist-panel-header, .beat-strip-main'))) {
      closePlayerHeaderMenus();
    }
  };
  const handlePlayerHeaderKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') closePlayerHeaderMenus();
  };
  document.addEventListener('pointerdown', handlePlayerHeaderOutsideClick);
  window.addEventListener('keydown', handlePlayerHeaderKeydown);

  /** Same "clicking away closes it" convention every other header dropdown
   * in this file already follows (see handlePlayerHeaderOutsideClick just
   * above) — extended here to the notes panel's own two remaining floating
   * dropdowns (tab overflow, notes layout — Edit no longer has one of its
   * own, see notesEditMode's comment), which previously had NO outside-
   * click dismissal at all and stayed open until explicitly toggled
   * again. */
  const handleNotesPanelOutsideClick = (event: PointerEvent): void => {
    // composedPath() (captured at dispatch time), NOT event.target.closest(...)
    // — a click INSIDE the panel that changes an action (e.g. picking a
    // notes-layout option) can trigger a synchronous renderNotesPanel()
    // call that rebuilds innerHTML and detaches the clicked element from
    // the DOM before this listener's own matches() check runs. .closest()
    // on an already-detached node can't find any ancestor and always
    // returns null — same bug class as handlePlayerHeaderOutsideClick's
    // own fix, see its comment for the full explanation.
    //
    // pointerdown, NOT click — Play/Pause (and a couple other buttons
    // elsewhere on this screen) call event.preventDefault() on their own
    // pointerup handler, which per spec suppresses the browser's
    // compatibility click event entirely, so a plain 'click' listener here
    // never saw those taps at all. pointerdown fires unconditionally,
    // before the target's own handler runs, so it still correctly defers
    // to the matches() checks below rather than racing them.
    const path = event.composedPath();
    const matches = (selector: string): boolean => path.some((node) => node instanceof Element && node.matches(selector));
    if (matches('.notes-layout-menu-wrap, .notes-tab-swap-wrap, .notes-layout-drawer')) return;
    if (notesTabSwapMenuOpenPane === null && !notesLayoutMenuOpen) return;
    notesTabSwapMenuOpenPane = null;
    notesLayoutMenuOpen = false;
    lastRenderedNotesKey = null;
    renderNotesPanel();
  };
  document.addEventListener('pointerdown', handleNotesPanelOutsideClick);

  /** `HTMLMediaElement.play()` and AudioContext.resume() are asynchronous.
   * If Pause is tapped while they are settling, remember it and apply it as
   * soon as start completes instead of losing the tap while the controller's
   * transition guard is active. */
  let isStartingPlay = false;
  let pauseRequestedDuringStart = false;
  let currentSongId: string | null = null;
  let beatVisualEls: BeatVisualEls = { pills: [], labels: [] };
  /** Drives the beat-dot flash for a synthetic-click song from playhead
   * position (see currentBeatOrdinal's own doc comment for why — the click
   * layer schedules audio ahead of time and never emits real beat events).
   * Non-null only while actively watching; torn down whenever playback
   * isn't in a state where "flashing along with the click" makes sense. */
  let syntheticBeatFlashRafId: number | null = null;
  /** rAF-driven countdown tracker for the marker-tap lead-in strip. Uses
   * the live file clock so the strip advances on the same visual beat as
   * the click instead of waiting for the next coarse timeupdate event. */
  let markerCountdownRafId: number | null = null;
  /** The last tick slot a pulse was fired for — a combined
   * beatOrdinal*subdivisionCount + subdivisionIndex key, covering every
   * tier (main beat AND every subdivision) in one dedup value, so the rAF
   * loop can safely run every animation frame (many times per tick)
   * without re-triggering the same pulse repeatedly. Reset to null on
   * every start so the very first frame always pulses rather than silently
   * waiting for the NEXT tick boundary to be crossed. Combining
   * beatOrdinal with subdivisionIndex (rather than tracking them
   * separately) means changing subdivisionCount mid-flight (an edited
   * song's setting) can never accidentally compare a key computed under
   * the OLD count against one computed under the new one and wrongly skip
   * a pulse — reset happens on every song-visual rebuild, so a stale key
   * never survives across that boundary either. */
  let lastFlashedSlotKey: number | null = null;
  let currentMarkers: SongMarker[] = [];
  let currentMarkersSongId: string | null = null;
  let currentDuration = 0;
  let isRehearsalMode = false;
  // The Player setlist opens as an overview: song rows first, no automatic
  // section expansion. The current song's sections expand only when the
  // musician taps the row toggle or starts playback.
  let setlistSubRowsCollapsed = true;
  /** Pane A and Pane B's independent tab+timing selections — shared by both
   * the wide and compact notes panel instances (only one pair of panes is
   * ever being looked at at a time regardless of layout). Defaults here are
   * placeholders overwritten immediately on song load — see the per-song
   * load site (loadNotesPaneSelection) for the real defaults/migration
   * behavior that reproduces pre-dual-pane behavior for existing users. */
  let notesPaneA: PlayerNotesPaneSelection = { tab: 'my-notes', timing: 'current' };
  let notesPaneB: PlayerNotesPaneSelection = { tab: 'my-notes', timing: 'upcoming' };
  let activeSongForNotes: string | null = null;
  /** Which pane's tab-swap dropdown (if any) is currently open — replaces
   * the old single `notesTabMenuOpen` boolean now that BOTH panes have
   * their own independent swap menu (previously only Pane A had a tab
   * menu at all). `null` means neither is open; only one can be open at a
   * time (opening one closes the other, same as any other single-menu-
   * open-at-a-time UI in this panel). */
  let notesTabSwapMenuOpenPane: 'a' | 'b' | null = null;
  let notesLayoutMenuOpen = false;
  /** Notes render READ-ONLY by default — the textarea/sheet cells aren't
   * even focusable — and only become editable once this is explicitly
   * toggled on via the pencil button next to the notes-layout toggle. Two
   * real problems this fixes: a stray tap while scrolling or reaching for
   * the transport no longer silently starts typing into (or overwriting)
   * a section's notes mid-performance, and the Mode/Clear controls (now a
   * persistent toolbar INSIDE the note content itself — see
   * renderNotesEditToolbar — not a floating dropdown) only take up space
   * while genuinely editing, so they're not a constant mispress risk
   * during normal reading/playing. Turning this ON also pauses playback
   * if a song is currently playing (see the toggle-notes-edit-mode click
   * handler) — editing while the song advances into the next section used
   * to force-exit edit mode and switch the panel away mid-keystroke,
   * silently dropping whatever was being typed; pausing removes that
   * conflict outright rather than trying to preserve an edit across a
   * section change. Resets to false on every marker/tab/song switch (see
   * the reset sites below) rather than persisting — "I was just editing
   * THIS section" shouldn't silently carry over to a different one. */
  let notesEditMode = false;
  /** The marker index the notes panel was last actually built for — renderNotesPanel
   * only rebuilds its DOM (textarea, tabs) when this genuinely changes (a
   * different marker becomes current, or the tab is switched), never on
   * every playback tick — otherwise typing into the notes textarea while
   * the song plays would have the element yanked out from under the
   * cursor multiple times a second. null means "never built yet." */
  let lastRenderedNotesKey: string | null = null;
  /** Which marker notesEditMode currently applies to — see its own comment
   * for why an edit session must never silently follow the panel onto a
   * different section. */
  let lastEditModeMarkerId: string | null = null;
  /** Faint waveform texture drawn behind the progress bar's marker sections
   * — purely decorative (polish/depth, not a navigational aid), so a failed
   * or slow decode is never worth blocking or erroring the actual progress
   * bar over. Cleared immediately on song switch (rather than left showing
   * the PREVIOUS song's shape while the new one decodes) and re-decoded from
   * scratch per song — Performance mode has no existing peak cache to reuse
   * (only the Song Library's marker editor decodes peaks, and only in
   * memory, never persisted to SongRecord), so this is a genuine one-time
   * decode per song load, same OfflineAudioContext pass as that editor. */
  let waveformPeaks: WaveformPeaks | null = null;
  let waveformPeaksSongId: string | null = null;

  // While a lead-in plays out, actual playback time is still inside the
  // PREVIOUS section (that's the whole point of the lead-in), but the
  // drummer just tapped a specific marker and expects to see THAT one
  // highlighted, not whatever section the playhead happens to be passing
  // through on the way there. pendingMarkerIndex pins the tapped marker as
  // the displayed "active" one until playback time actually reaches its own
  // timestamp, at which point it becomes truly active and this clears.
  let pendingMarkerIndex: number | null = null;
  let selectedMarkerIndex: number | null = null;
  /** True only when selectedMarkerIndex was tapped AHEAD of the playhead at
   * the moment of that tap — a forward preview, waiting for playback to
   * catch up to it (see updateMarkerProgress's own release logic below).
   * Tapping a marker BEHIND the current playhead is a deliberate "jump back
   * to look at this section" gesture instead, and must stick until the user
   * taps something else — releasing it the instant timeIndex (which is
   * already past it) diverges would undo that tap on the very next tick,
   * making it look like the setlist's marker rows simply don't respond to
   * taps behind the playhead at all. */
  let selectedMarkerIndexIsForwardPreview = false;

  /** Mirrors pendingMarkerIndex's own lifetime (set together at tap time,
   * cleared together once the marker is genuinely reached — see
   * updateMarkerProgress) but carries what updatePlayPauseCountIn actually
   * needs to drive the SAME Play/Pause button countdown a marker tap
   * re-uses: the marker's own timestamp, the ACTUAL gap the clamped seek
   * produced (not the nominal configured lead-in — a marker near 0:00
   * clamps to a shorter real gap, and the countdown must count down over
   * that same shorter span or it'd finish "early" relative to what it
   * displays), and the song's own seconds-per-beat ONLY when the lead-in
   * is currently a beat-count (so the digit ticks down in counts instead
   * of seconds, same convention the song-start pre-roll uses). */
  let markerCountdownTarget: { markerTimeSeconds: number; leadInSeconds: number; secondsPerBeatAtTap: number | null } | null = null;

  /** Which of the three loop behaviors the transport Loop button is
   * currently on, cycled by successive taps: 'off' -> 'marker' -> 'song' ->
   * 'setlist' -> 'off'.
   * - 'marker': loops the tapped marker's section — the original behavior,
   *   independent of any specific marker until one is actually tapped (see
   *   loopSectionIndex). Landing here with nothing tapped yet does nothing
   *   visible on its own; it just changes what the NEXT marker tap does.
   * - 'song': loops the whole current song from 0:00 once it ends.
   * - 'setlist': on song end, advances to the next setlist entry (wrapping
   *   back to the first after the last), same as tapping Next but hands-free
   *   and automatic.
   * checkLoopBoundary is the single place all three are actually applied. */
  type LoopMode = 'off' | 'marker' | 'song' | 'setlist';
  let loopMode: LoopMode = 'off';
  /** Standard media-player back-button behavior: if the song is already
   * near its start, jump to the previous entry; otherwise restart the
   * current song. */
  const TRANSPORT_BACK_RESTART_THRESHOLD_SECONDS = 3;
  /** Which marker's section is currently looping — only meaningful while
   * loopMode === 'marker'; null whenever loopMode is anything else, OR
   * loopMode is 'marker' but no marker has been tapped since entering that
   * mode (loop is "on" but has nothing to loop yet). Set the moment a
   * marker is tapped while in marker mode; cleared on song switch, on
   * leaving marker mode, or on tapping a DIFFERENT marker (which simply
   * re-arms the loop for the new section instead of requiring off-then-on).
   * A plain scrub or Play-from-the-top does NOT clear this — only an
   * explicit marker tap changes which section loops, matching how
   * pendingMarkerIndex itself already only changes on a marker tap. */
  let loopSectionIndex: number | null = null;
  /** True while an auto-advance triggered by loopMode 'song' or 'setlist' is
   * in flight — checkLoopBoundary runs on every controller tick, and the
   * ENDED status it watches for can persist across several ticks while that
   * work is still resolving ('setlist' mode in particular awaits a full
   * jumpToEntry, including a fresh song's own audio-blob fetch). Without this
   * guard, a second tick would see the same ENDED state and fire a second,
   * overlapping advance before the first one's activeEntryIndex update had
   * landed — skipping an extra song in 'setlist' mode. */
  let loopAutoAdvancing = false;

  /** The marker/count-in sub-row index last auto-scrolled into view (see
   * updateMarkerProgress) — null covers both "nothing scrolled yet" and
   * "no sub-row list rendered" (a metronome song, or a song with no
   * count-in/markers). Tracked so the auto-scroll only fires on a genuine
   * CHANGE of which row is current, not on every render tick while
   * playing (many times a second) — otherwise it would repeatedly yank the
   * view back even while the drummer is deliberately scrolling elsewhere
   * to glance at an upcoming section. Reset to null on every song/setlist
   * switch (see renderSetlistRows) so the first row of a freshly loaded
   * song still scrolls into view once, rather than staying stuck at
   * whatever a previous song's row index happened to be. */
  let lastScrolledMarkerIndex: number | null = null;

  async function loadCurrentEntry(): Promise<void> {
    const setlist = getActiveSetlist();
    const { activeEntryIndex } = appStore.getState().settings;
    const song = setlist ? getEntrySong(activeEntryIndex) : null;

    if (!song) {
      currentSongId = null;
      clearProgressWaveform();
      renderStaticInfo(setlist);
      return;
    }

    if (song.id !== currentSongId) {
      currentSongId = song.id;
      activeSongForNotes = song.id;
      // Pane B's fallback mirrors Pane A's tab (not a hardcoded 'my-notes')
      // specifically so a user who's never touched the new per-pane
      // controls sees EXACTLY today's pre-dual-pane behavior — Pane B
      // previewing the upcoming section in the SAME tab Pane A is on —
      // rather than something that looks like a random reset on upgrade.
      const savedPaneA = loadNotesPaneSelection(song.id, 'a');
      notesPaneA = savedPaneA ?? { tab: 'my-notes', timing: 'current' };
      const savedPaneB = loadNotesPaneSelection(song.id, 'b');
      notesPaneB = savedPaneB ?? { tab: notesPaneA.tab, timing: 'upcoming' };
      notesTabSwapMenuOpenPane = null;
      notesLayoutMenuOpen = false;
      notesEditMode = false;
      lastRenderedNotesKey = null;
      clearProgressWaveform();
      await controller.loadSong(song, setlist?.routing ?? DEFAULT_REHEARSAL_ROUTING);
      void loadProgressWaveform(song);
    }
    renderStaticInfo(setlist);
  }

  function clearProgressWaveform(): void {
    waveformPeaks = null;
    waveformPeaksSongId = null;
    const ctx = progressWaveformCanvas.getContext('2d');
    ctx?.clearRect(0, 0, progressWaveformCanvas.width, progressWaveformCanvas.height);
  }

  /** One-time decode + draw for the current song's faint background
   * texture, run in the background after loadSong() resolves so it never
   * delays actual playback readiness. Metronome-only songs have no file to
   * decode, so they simply never get a waveform (clearProgressWaveform's
   * blank canvas is correct for them). If the user has already switched to
   * a DIFFERENT song by the time this resolves, the result is discarded
   * rather than drawn — same guard pattern as the Song Library's own
   * decodeWaveformPeaks usage. */
  async function loadProgressWaveform(song: SongRecord): Promise<void> {
    if (song.mode === 'metronome') return;
    try {
      const audioRecord = await database.getAudioBlob(song.id);
      if (!audioRecord || currentSongId !== song.id) return;
      const peaks = await decodeWaveformPeaks(audioRecord.blob);
      if (currentSongId !== song.id) return;
      waveformPeaks = peaks;
      waveformPeaksSongId = song.id;
      drawProgressWaveform();
    } catch {
      // A failed decode just means no waveform texture — the progress bar
      // itself (sections, fill, labels) works fine without it, so this is
      // silently ignored rather than surfaced as an error to the drummer.
    }
  }

  /** (Re)draws the progress bar's background waveform at its current
   * rendered size — called once when peaks first arrive, and again on
   * window resize (the canvas's pixel dimensions must track its actual CSS
   * box, same reasoning as the Song Library's own waveform canvases). Very
   * low opacity by design: this is texture/polish, not a navigational aid,
   * and must never compete with the marker labels drawn on top of it. */
  function drawProgressWaveform(): void {
    if (!waveformPeaks || waveformPeaksSongId !== currentSongId) return;
    const rect = progressWaveformCanvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    progressWaveformCanvas.width = rect.width * dpr;
    progressWaveformCanvas.height = rect.height * dpr;
    drawWaveform(progressWaveformCanvas, waveformPeaks, 'rgba(255, 255, 255, 0.1)');
  }

  /** Jumps directly to any entry in the active setlist by index (tap-to-jump
   * from the setlist panel). Tapping a DIFFERENT song loads it fresh, which
   * naturally starts at 0:00. Tapping the CURRENTLY loaded song's own row is
   * the "restart this song" action (replacing the old separate Stop button)
   * — loadCurrentEntry() only reloads when the song id changes, so that case
   * needs an explicit reset back to 0:00 here. */
  async function jumpToEntry(index: number): Promise<void> {
    await guardSongSwitch(async () => {
      const setlist = getActiveSetlist();
      if (!setlist || !setlist.entries[index]) return;
      const isSameEntry = index === appStore.getState().settings.activeEntryIndex;
      pendingMarkerIndex = null;
      markerCountdownTarget = null;
      loopSectionIndex = null;
      setlistSubRowsCollapsed = true;
      await appStore.setActiveEntryIndex(index);
      await loadCurrentEntry();
      if (isSameEntry) controller.stop();
    });
  }

  async function handleTransportBackTap(): Promise<void> {
    const setlist = getActiveSetlist();
    if (!setlist) return;

    const index = appStore.getState().settings.activeEntryIndex;
    const song = getEntrySong(index);
    if (index <= 0) {
      if (song?.mode === 'metronome') {
        const status = controller.getState().status;
        controller.stop();
        if (status === 'PLAYING' || status === 'PRE_ROLLING') void controller.play();
      } else {
        controller.seek(0);
      }
      return;
    }

    const currentTime = controller.getState().currentTime;
    if (currentTime > TRANSPORT_BACK_RESTART_THRESHOLD_SECONDS) {
      controller.seek(0);
      return;
    }

    await jumpToEntry(index - 1);
  }

  function renderStaticInfo(setlist: SetlistRecord | null): void {
    renderPlayerHeaderMenus();
    if (!setlist) {
      setCurrentTitle('Select a setlist in the Setlists tab');
      currentSongEditBtn.disabled = true;
      clickSettingsBtn.disabled = true;
      clickSettingsBtnCompact.disabled = true;
      metronomeVisualWrap.innerHTML = '';
      metronomeVisualWrap.classList.add('is-empty');
      beatStripMain.classList.add('is-empty');
      beatStripHeader.classList.remove('has-click');
      headerBeatVisualWrap.classList.remove('has-click');
      setlistPanelTitle.textContent = 'No setlist selected';
      setlistPanelMeta.textContent = '';
      setlistPanelScroll.innerHTML = '<p class="empty-state">Select a setlist in the Setlists tab.</p>';
      setCurrentMarkers([], 0, null);
      return;
    }

    const index = appStore.getState().settings.activeEntryIndex;
    const song = getEntrySong(index);

    setlistPanelTitle.textContent = setlist.name;
    setlistPanelMeta.textContent = setlistMetaLine(setlist, appStore.getState().songs);
    renderSetlistRows(setlist, index);

    if (!song) {
      setCurrentTitle('No song at this position');
      currentSongEditBtn.disabled = true;
      clickSettingsBtn.disabled = true;
      clickSettingsBtnCompact.disabled = true;
      metronomeVisualWrap.innerHTML = '';
      metronomeVisualWrap.classList.add('is-empty');
      beatStripMain.classList.add('is-empty');
      beatStripHeader.classList.remove('has-click');
      headerBeatVisualWrap.classList.remove('has-click');
      applyClickWidgetCollapsedState(false);
      setCurrentMarkers([], 0, null);
      return;
    }

    setCurrentTitle(song.name);
    currentSongEditBtn.disabled = false;
    clickSettingsBtn.disabled = false;
    clickSettingsBtnCompact.disabled = false;

    // Same visual beat dots as the standalone metronome, for TWO distinct
    // sources: a pure metronome-mode song (driven by live MetronomeEngine
    // beat events, via subscribeBeats below) and a Tracks + Click/Tracks
    // Only song with a synthetic click layered on top (driven by the rAF
    // loop below instead — SyntheticClickLayer schedules audio ahead of
    // time and never emits its own beat events, so the dots are derived
    // from playhead position each frame; see currentBeatOrdinal's own doc
    // comment). The synthetic-click case additionally requires a marked
    // beatOneReferenceSeconds — without it there's no known "beat 1" to
    // anchor which of the 4 dots is currently lit (same limitation the
    // beat-alignment strip's own numbered ticks already have), so showing
    // dots with an arbitrary/wrong accent would be actively misleading
    // rather than just less informative.
    const hasEditableClickSettings = song.mode === 'metronome' || song.mode === 'tracks-click' || song.syntheticClickEnabled;
    const hasClickToWatch = song.mode === 'metronome' || (song.syntheticClickEnabled && song.beatOneReferenceSeconds !== null);
    if (hasClickToWatch) {
      renderMetronomeVisual(song.mode === 'metronome' ? song.metronomeSettings.beatsPerMeasure : 4, song.metronomeSettings.accentFirstBeat);
    } else {
      metronomeVisualWrap.innerHTML = '';
      headerBeatVisualWrap.innerHTML = '';
    }
    metronomeVisualWrap.classList.toggle('is-empty', !hasClickToWatch);
    beatStripMain.classList.toggle('is-empty', !hasEditableClickSettings);
    // Lives in the setlist panel's fixed header (see its own comment) so it
    // never disappears while scrolling the list — only its visibility
    // toggles per song, same gating condition as the beat-dot row itself.
    headerBeatVisualWrap.classList.toggle('has-click', hasClickToWatch);
    beatStripHeader.classList.toggle('has-click', hasClickToWatch);
    applyClickWidgetCollapsedState(hasClickToWatch);

    setCurrentMarkers(song.mode === 'metronome' ? [] : song.markers, song.durationSeconds ?? 0, song.id);
  }

  /** Applies the collapsed state to the narrow/portrait header's click
   * widget — the toggle itself now lives inside the compact gear's own
   * click-sound menu (see renderClickSoundMenu's "Show/Hide visual click"
   * row) rather than a dedicated chevron button, so there's nothing here to
   * show/hide directly; only the pills/indicator classes respond.
   * Collapsing doesn't remove the beat indicator entirely: while collapsed
   * AND a real click is playing, the transport buttons take over as the
   * indicator instead (see updateTransportBeatIndicators, driven by the
   * same per-song hasClickToWatch condition passed in here). */
  function applyClickWidgetCollapsedState(hasClickToWatch: boolean): void {
    if (!hasClickToWatch) {
      beatStripHeader.classList.remove('is-collapsed');
      transportRowCompact.classList.remove('shows-beat-indicators');
      clearTransportBeatIndicators();
      return;
    }
    beatStripHeader.classList.toggle('is-collapsed', clickWidgetCollapsed);
    transportRowCompact.classList.toggle('shows-beat-indicators', clickWidgetCollapsed);
    if (!clickWidgetCollapsed) clearTransportBeatIndicators();
  }

  function isTransportPlaying(): boolean {
    const status = controller.getState().status;
    return status === 'PLAYING' || status === 'PRE_ROLLING';
  }

  function renderSetlistRows(setlist: SetlistRecord, currentIndex: number): void {
    setlistPanelScroll.innerHTML = '';
    lastScrolledMarkerIndex = null;
    const songs = appStore.getState().songs;

    if (setlist.entries.length === 0) {
      setlistPanelScroll.innerHTML = '<p class="empty-state">This setlist has no songs yet.</p>';
      return;
    }

    setlist.entries.forEach((entry, index) => {
      const song = songs.find((s) => s.id === entry.songId) ?? null;
      const isCurrent = index === currentIndex;
      const isPlayed = index < currentIndex;
      const hasError = song ? song.status !== 'ok' && song.status !== 'checking' : true;

      const row = document.createElement('div');
      row.setAttribute('role', 'button');
      row.tabIndex = 0;
      row.className = [
        'setlist-panel-row',
        isCurrent ? 'is-current' : '',
        isPlayed && !isCurrent ? 'is-played' : '',
        hasError ? 'has-error' : '',
      ]
        .filter(Boolean)
        .join(' ');
      row.dataset.index = String(index);

      const name = song ? escapeHtml(song.name) : '(missing song)';
      const tag = song ? escapeHtml(setlistSongTag(song)) : 'Song no longer in library';

      // With 2+ markers, the song's own row stays a plain (unfilled) row and
      // each section gets its own indented sub-row below (real structure
      // worth distinguishing — Verse/Chorus/Bridge). With 0 or 1 markers,
      // there's only ever ONE section for the whole song, so a separate
      // one-item sub-list would just repeat the same "how far into this
      // song am I" fill the row itself can show directly — folded in here
      // instead via .setlist-panel-fill. A configured count-in no longer
      // factors into this decision at all — it shows on the Play/Pause
      // button itself while actively counting in (see
      // updatePlayPauseCountIn), not as a row here.
      const needsSubRows = !!song && song.mode !== 'metronome' && song.markers.length > 1;
      const showsOwnFill = isCurrent && song && song.mode !== 'metronome' && (!needsSubRows || setlistSubRowsCollapsed);

      row.innerHTML = `
        ${showsOwnFill ? '<div class="setlist-panel-fill" id="setlist-panel-own-fill"></div>' : ''}
        <span class="setlist-panel-num">${index + 1}</span>
        <span class="setlist-panel-info">
          <span class="setlist-panel-name">${name}</span>
          ${tag ? `<span class="setlist-panel-tag">${tag}</span>` : ''}
        </span>
        ${
          isCurrent
            ? `<button class="setlist-panel-now-badge" id="setlist-panel-now-badge" type="button" data-action="toggle-setlist-play-pause" title="${isTransportPlaying() ? 'Pause' : 'Play'}" aria-label="${isTransportPlaying() ? 'Pause' : 'Play'}">
                 <span id="setlist-panel-now-badge-icon">${isTransportPlaying() ? ICON_PAUSE : ICON_PLAY}</span>
               </button>
               ${
                 needsSubRows
                   ? `<button class="setlist-subrows-toggle-btn" type="button" data-action="toggle-setlist-subrows" title="${setlistSubRowsCollapsed ? 'Show song sections' : 'Hide song sections'}" aria-label="${setlistSubRowsCollapsed ? 'Show song sections' : 'Hide song sections'}" aria-expanded="${!setlistSubRowsCollapsed}">
                       <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path d="M6 12h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>${setlistSubRowsCollapsed ? '<path d="M12 6v12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' : ''}</svg>
                     </button>`
                   : ''
               }
               <button class="current-song-edit-btn setlist-song-edit-btn" type="button" title="Edit current song" aria-label="Edit current song">
                 <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true"><path d="M4 20h4L18.5 9.5a2.121 2.121 0 0 0-3-3L5 17v3z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
               </button>`
            : ''
        }
      `;
      setlistPanelScroll.appendChild(row);

      // Song structure markers render as indented sub-rows directly under
      // the CURRENT song only, and only after the musician asks for detail
      // with the row toggle or starts playback. That keeps the initial
      // setlist view scannable while preserving the richer section view
      // once practice actually begins.
      if (isCurrent && song && needsSubRows && !setlistSubRowsCollapsed) {
        const markerList = document.createElement('div');
        markerList.className = 'setlist-marker-list';
        markerList.id = 'setlist-marker-list';

        song.markers.forEach((marker, markerIndex) => {
          const markerRow = document.createElement('div');
          const sectionEnd = markerSectionEnd(song.markers, markerIndex, song.durationSeconds ?? 0);
          const sectionDuration = Math.max(0, sectionEnd - marker.timeSeconds);
          markerRow.className = 'setlist-marker-row';
          markerRow.setAttribute('role', 'button');
          markerRow.tabIndex = 0;
          markerRow.dataset.markerIndex = String(markerIndex);
          markerRow.innerHTML = `
            <div class="setlist-marker-fill"></div>
            <span class="setlist-marker-time">${formatDuration(marker.timeSeconds)}</span>
            <span class="setlist-marker-main">
              <span class="setlist-marker-name">${escapeHtml(marker.label || formatDuration(marker.timeSeconds))}</span>
            </span>
            <span class="setlist-marker-duration" data-section-duration="${sectionDuration}">${formatDuration(sectionDuration)}</span>
            <span class="setlist-marker-loop-tag" aria-label="Looping this section" title="Looping this section">${ICON_LOOP}</span>
            <span class="setlist-marker-pending-tag" aria-label="Queued next" title="Queued next">▶</span>
          `;
          markerList.appendChild(markerRow);
        });
        setlistPanelScroll.appendChild(markerList);
      }
    });

    const currentRow = setlistPanelScroll.querySelector(`[data-index="${currentIndex}"]`);
    currentRow?.scrollIntoView({ block: 'center' });
  }

  function rerenderSetlistRows(): void {
    const setlist = getActiveSetlist();
    if (!setlist) return;
    renderSetlistRows(setlist, appStore.getState().settings.activeEntryIndex);
    updateMarkerProgress(controller.getState().currentTime, controller.getState().status === 'PRE_ROLLING');
  }

  function expandCurrentSetlistDetails(): void {
    if (!setlistSubRowsCollapsed) return;
    setlistSubRowsCollapsed = false;
    rerenderSetlistRows();
  }

  function renderMetronomeVisual(beatsPerMeasure: number, accentFirst: boolean): void {
    metronomeVisualWrap.innerHTML = `<div class="metronome-visual" id="beat-dots"></div>`;
    const dotsWrap = metronomeVisualWrap.querySelector<HTMLDivElement>('#beat-dots')!;
    beatVisualEls = renderBeatVisual(dotsWrap, beatsPerMeasure, accentFirst);

    headerBeatVisualWrap.innerHTML = `<div class="header-beat-visual" id="header-beat-dots"></div>`;
    const headerDotsWrap = headerBeatVisualWrap.querySelector<HTMLDivElement>('#header-beat-dots')!;
    headerBeatVisualEls = renderBeatVisual(headerDotsWrap, beatsPerMeasure, accentFirst);
  }

  /** Flashes the transport button standing in for the given 1-4 beat
   * position gold (see .btn-transport-beat-flash's own comment for why the
   * buttons take over as the click indicator at all — icons stay put, only
   * the background tints) — only ever called for the MAIN beat
   * (subdivisionIndex 0), never "and"/sixteenth ticks, since there are
   * exactly 4 buttons for 4 beats with no equivalent slot for a
   * subdivision. Only 4/4 (or anything whose beatsPerMeasure the caller
   * has already clamped to 1-4) maps onto these 4 buttons at all — see
   * updateTransportBeatIndicators' own doc comment for what happens
   * otherwise. Clears the previously-lit button itself rather than relying
   * on a CSS transition/timeout, so it can never fall out of sync with the
   * actual beat schedule. */
  function flashTransportBeatIndicator(position: number): void {
    for (const el of transportBeatFlashEls) {
      el.classList.toggle('is-active', Number(el.dataset.beatNumber) === position);
    }
  }

  function clearTransportBeatIndicators(): void {
    for (const el of transportBeatFlashEls) el.classList.remove('is-active');
  }

  function stopMarkerCountdownRaf(): void {
    if (markerCountdownRafId === null) return;
    cancelAnimationFrame(markerCountdownRafId);
    markerCountdownRafId = null;
  }

  function tickMarkerCountdownRaf(): void {
    markerCountdownRafId = null;
    if (markerCountdownTarget === null) return;
    updateMarkerProgress(controller.currentFileTime(), controller.getState().status === 'PRE_ROLLING');
    markerCountdownRafId = requestAnimationFrame(tickMarkerCountdownRaf);
  }

  function syncMarkerCountdownRaf(): void {
    if (markerCountdownTarget !== null) {
      if (markerCountdownRafId === null) markerCountdownRafId = requestAnimationFrame(tickMarkerCountdownRaf);
      return;
    }
    stopMarkerCountdownRaf();
  }

  /** Same delay-scheduling pattern as beatVisual.ts's own scheduleBeatPulse
   * (setTimeout for beat.scheduledTime - audioContext.currentTime, NOT an
   * immediate flash) — MetronomeEngine's beat events fire up to 0.2s AHEAD
   * of when the click is actually audible (see MetronomeEngine's own
   * lookahead-scheduler doc comment), so flashing immediately would show
   * the beat before you hear it. Only 1-4 beat positions map onto the 4
   * transport buttons at all; a time signature with MORE than 4 beats per
   * measure has no slot for beats 5+ (clamped away entirely rather than
   * wrapping back onto button 1, which would misleadingly look like a
   * downbeat) — a real but accepted limitation of reusing exactly 4
   * existing buttons instead of a dedicated N-wide row. */
  function scheduleTransportBeatIndicator(beat: BeatEvent, audioContext: AudioContext): void {
    if (beat.subdivisionIndex !== 0) return; // only the main beat, never "and"/sixteenth ticks
    const position = beat.beatIndexInMeasure + 1;
    if (position < 1 || position > 4) return;
    const delayMs = Math.max(0, (beat.scheduledTime - audioContext.currentTime) * 1000);
    setTimeout(() => flashTransportBeatIndicator(position), delayMs);
  }

  /** Per-frame tick for the synthetic-click beat pulse (see
   * syntheticBeatFlashRafId's own doc comment for why this exists as a
   * separate rAF loop rather than piggybacking on controller state updates
   * — those arrive via the native <audio> `timeupdate` event, which fires
   * far too coarsely for a crisp per-tick pulse). Reads file time through
   * controller.currentClickFileTime() — the click layer's OWN notion of
   * current position, derived from the same AudioContext anchor the audio
   * scheduler itself uses — rather than state.currentTime (sourced from
   * HTMLMediaElement, a different clock not guaranteed to stay in lockstep
   * with AudioContext; reading it here previously caused the visual pulse
   * to drift out of sync with the audible click). This is also what makes
   * the pulse correctly continue through a pre-roll count-in: the click
   * layer is already running and reporting negative (pre-song) file time
   * throughout PRE_ROLLING, well before state.currentTime has any meaning
   * at all (the file engine hasn't started yet).
   *
   * Uses currentSubdivisionSlot at the song's OWN subdivision setting (1,
   * 2, or 4) uniformly for every tier, including the main beat — there's
   * no separate "is this the beat" special case, since subdivisionIndex 0
   * from that same function already means exactly that (matching
   * BeatEvent's own convention on the live-metronome path). A song with
   * subdivision=1 simply never produces a non-zero subdivisionIndex at
   * all, so the "and"/sixteenth tiers are naturally unreachable without
   * needing an explicit off/on branch here. */
  function tickSyntheticBeatFlash(song: SongRecord): void {
    const beatOneReferenceSeconds = song.beatOneReferenceSeconds;
    if (beatOneReferenceSeconds === null) return; // shouldn't be running at all in this case; defensive no-op
    const fileTime = controller.currentClickFileTime();
    if (fileTime === null) return; // click isn't actually running; defensive no-op
    const { bpm, subdivision } = song.metronomeSettings;
    const offsetSeconds = song.syntheticClickOffsetSeconds;

    const slot = currentSubdivisionSlot(fileTime, bpm, offsetSeconds, subdivision);
    const slotKey = slot.beatOrdinal * subdivision + slot.subdivisionIndex;
    if (slotKey !== lastFlashedSlotKey) {
      lastFlashedSlotKey = slotKey;
      const position = beatPositionInMeasure(slot.beatOrdinal, bpm, offsetSeconds, beatOneReferenceSeconds);
      if (position !== null) {
        const tier = pulseTierFor(slot.subdivisionIndex, subdivision);
        const secondsPerTick = secondsPerBeat(song.metronomeSettings) / subdivision;
        const isDownbeat = tier === 'beat' && position === 1 && song.metronomeSettings.accentFirstBeat;
        // beatPositionInMeasure is 1-4; pill array is 0-indexed. The header
        // beat visual is a second instance of the exact same row (see
        // headerBeatVisualEls's own comment) — driven by the identical
        // arguments as the main row, all subdivisions included, not the
        // old single watch-square's main-beats-only behavior.
        triggerPulse(beatVisualEls, position - 1, slot.subdivisionIndex, subdivision, secondsPerTick * 1000, isDownbeat);
        triggerPulse(headerBeatVisualEls, position - 1, slot.subdivisionIndex, subdivision, secondsPerTick * 1000, isDownbeat);
        // Same main-beat-only, collapsed-only gating as the live-metronome
        // path (scheduleTransportBeatIndicator) — already real-time here
        // (this whole function is a per-frame rAF tick, not a lookahead
        // scheduler), so no delay/setTimeout is needed, unlike that path.
        if (tier === 'beat' && position >= 1 && position <= 4 && transportRowCompact.classList.contains('shows-beat-indicators')) {
          flashTransportBeatIndicator(position);
        }
      }
    }

    syntheticBeatFlashRafId = requestAnimationFrame(() => tickSyntheticBeatFlash(song));
  }

  function stopSyntheticBeatFlash(): void {
    if (syntheticBeatFlashRafId !== null) {
      cancelAnimationFrame(syntheticBeatFlashRafId);
      syntheticBeatFlashRafId = null;
    }
    lastFlashedSlotKey = null;
    clearTransportBeatIndicators();
  }

  /** Starts/stops the rAF loop to match whether it SHOULD currently be
   * running — called on every controller state update (play/pause/song
   * switch/etc.), so it's always idempotent rather than something callers
   * need to remember to pair start/stop calls for. Runs during BOTH
   * PLAYING and PRE_ROLLING — the click is audibly counting in during the
   * pre-roll too, so the visual needs to track it from the very first
   * count-in beat, not just once the song itself starts. */
  function syncSyntheticBeatFlash(status: ControllerState['status']): void {
    const song = getCurrentSong();
    const shouldRun =
      (status === 'PLAYING' || status === 'PRE_ROLLING') &&
      !!song &&
      song.syntheticClickEnabled &&
      song.beatOneReferenceSeconds !== null;
    if (shouldRun && syntheticBeatFlashRafId === null) {
      lastFlashedSlotKey = null;
      tickSyntheticBeatFlash(song);
    } else if (!shouldRun && syntheticBeatFlashRafId !== null) {
      stopSyntheticBeatFlash();
    }
  }

  /** Called whenever the loaded song changes (or has no markers). Rebuilds
   * the progress bar's section elements — the expensive DOM-structure part,
   * done once per song rather than per tick. The readable marker sub-rows
   * themselves are built inline in renderSetlistRows (they live under the
   * current song in the setlist panel, not here), since that already runs
   * first in renderStaticInfo's flow and rebuilds on every song change. */
  function setCurrentMarkers(markers: SongMarker[], durationSeconds: number, songId: string | null): void {
    const sameMarkerOrder =
      currentMarkersSongId === songId &&
      currentMarkers.length === markers.length &&
      currentMarkers.every((marker, index) => marker.id === markers[index]?.id);
    currentMarkers = markers;
    currentMarkersSongId = songId;
    currentDuration = durationSeconds;
    if (!sameMarkerOrder) {
      pendingMarkerIndex = null;
      markerCountdownTarget = null;
      // A new/reordered marker list cannot safely retain an index into the
      // previous list. Ordinary note edits keep the same IDs/order and must
      // preserve the marker the performer explicitly tapped.
      loopSectionIndex = null;
    }
    progressSections.innerHTML = '';
    // Force the notes panel to genuinely rebuild for the new song, even if
    // its first marker happens to share the same id/tab combination
    // lastRenderedNotesKey already holds (astronomically unlikely given
    // generateId(), but the cost of being explicit here is nothing).
    lastRenderedNotesKey = null;
    renderNotesPanel();

    if (durationSeconds <= 0) return;

    if (markers.length === 0) {
      // No markers set up for this song yet: still show a single unlabeled
      // section spanning the whole bar, so progress fill keeps working.
      // Explicit width is required now that .progress-section uses
      // flex: 0 0 auto (see style.css) rather than flex: 1 — it no longer
      // stretches to fill available space on its own.
      const section = document.createElement('div');
      section.className = 'progress-section';
      section.style.width = '100%';
      const sectionFill = document.createElement('div');
      sectionFill.className = 'progress-section-fill';
      section.appendChild(sectionFill);
      const countInFill = document.createElement('div');
      countInFill.className = 'progress-section-countin-fill';
      section.appendChild(countInFill);
      progressSections.appendChild(section);
      return;
    }

    // .progress-sections lays these out with `gap: 3px` between them (see
    // style.css) — with N sections that's (N-1) * 3px of space the percentage
    // widths below must NOT claim, or the row overflows its rounded track by
    // that many pixels. calc() reserves that space up front: each section's
    // width is its time-share of the remaining (100% minus total gap) space.
    const gapPx = 3;
    const totalGapPx = Math.max(0, markers.length - 1) * gapPx;

    markers.forEach((marker, i) => {
      const endT = i < markers.length - 1 ? markers[i + 1].timeSeconds : durationSeconds;
      const widthPct = ((endT - marker.timeSeconds) / durationSeconds) * 100;

      const section = document.createElement('div');
      section.className = 'progress-section' + (i % 2 === 1 ? ' is-odd' : '');
      section.style.width = `calc(${Math.max(0, widthPct)}% - ${(totalGapPx * widthPct) / 100}px)`;
      section.dataset.markerIndex = String(i);

      const sectionFill = document.createElement('div');
      sectionFill.className = 'progress-section-fill';
      section.appendChild(sectionFill);

      // A SECOND fill layer, normally 0-width — only grows on whichever
      // section is genuinely playing (timeIndex) while a marker-tap
      // countdown is in flight (see updateMarkerProgress), representing the
      // trailing slice of that section's own remaining time that IS the
      // countdown window. Same count-in amber the Play/Pause button turns
      // gold while counting in (see updatePlayPauseCountIn), so both
      // visibly tie back to the same "counting in" state rather than
      // looking unrelated.
      const countInFill = document.createElement('div');
      countInFill.className = 'progress-section-countin-fill';
      section.appendChild(countInFill);

      const label = document.createElement('div');
      label.className = 'progress-tick-label';
      const fullLabel = marker.label.trim() || effectiveShortLabel(marker);
      const shortLabel = effectiveShortLabel(marker);
      label.dataset.fullLabel = fullLabel;
      label.dataset.shortLabel = shortLabel;
      label.textContent = fullLabel;
      label.title = fullLabel;
      label.setAttribute('aria-label', fullLabel);
      section.appendChild(label);

      progressSections.appendChild(section);
    });

    updateMarkerProgress(0);
    scheduleProgressMarkerLabelFit();
  }

  /** Updates the current song's OWN row fill (#setlist-panel-own-fill) —
   * only present at all when that song has 0 or 1 markers (see
   * renderSetlistRows's showsOwnFill), in which case there's exactly one
   * "section" spanning the whole song, so this is the same plain
   * currentTime/currentDuration ratio the top progress bar's own single
   * implicit section uses. No-op (element simply doesn't exist) once a song
   * has 2+ markers, where per-marker sub-rows take over instead. */
  function updateOwnRowFill(currentTime: number): void {
    const fillEl = setlistPanelScroll.querySelector<HTMLDivElement>('#setlist-panel-own-fill');
    if (!fillEl || currentDuration <= 0) return;
    fillEl.style.width = `${Math.min(100, (currentTime / currentDuration) * 100)}%`;
  }

  /** Called on every playback tick: cheap style-only updates to the already-
   * built section elements and the setlist panel's marker sub-rows — never
   * rebuilds DOM, so this can run at timeupdate frequency without
   * disrupting anything (e.g. focus, scroll position). `isPreRolling`
   * suppresses the is-active/is-pending highlight on every marker row (fills
   * stay harmlessly at 0%, just not visually "current") while a count-in is
   * playing — the Play/Pause button's own countdown (see
   * updatePlayPauseCountIn) is what should read as active during that
   * window, not section 2's row, even though currentTime (still file-time
   * 0, the file hasn't started yet) would otherwise make the first real
   * section look "current". */
  function updateMarkerProgress(currentTime: number, isPreRolling = false): void {
    if (currentDuration <= 0) return;
    updateOwnRowFill(currentTime);

    if (currentMarkers.length === 0) {
      // Single implicit whole-song section (no markers set up yet).
      const fillEl = progressSections.querySelector<HTMLDivElement>('.progress-section-fill');
      if (fillEl) fillEl.style.width = `${Math.min(100, (currentTime / currentDuration) * 100)}%`;
      return;
    }

    const timeIndex = Math.max(0, currentMarkerIndex(currentMarkers, currentTime));

    // Once playback genuinely reaches the pending marker's own timestamp,
    // it IS the active section — stop overriding and let time drive display
    // normally again. The Count-in row's marker-tap countdown ends at the
    // exact same moment, for the same reason.
    if (pendingMarkerIndex !== null && timeIndex >= pendingMarkerIndex) {
      pendingMarkerIndex = null;
      markerCountdownTarget = null;
    }
    // A plain single tap (the "preview this section's notes" gesture — see
    // selectedMarkerIndex's own comment) pins a marker as displayed with NO
    // seek involved, so nothing else was going to move timeIndex past it on
    // its own. Once genuine playback (normal advancing time, not a seek)
    // catches up to or passes a FORWARD-looking pin, it must release the
    // same way pendingMarkerIndex does above — otherwise a single tap made
    // early in the song pins the notes panel to that section FOREVER, even
    // once the band has played on into every later section. Only forward
    // pins release this way — see selectedMarkerIndexIsForwardPreview's own
    // comment for why a BACKWARD jump (tapping an earlier marker to look
    // back at it) must never auto-release just because timeIndex is already
    // past it.
    //
    // Excluded while Loop is armed on a DIFFERENT section than the pin
    // (loopSectionIndex !== selectedMarkerIndex): checkLoopBoundary's own
    // repeated seek-back-to-loop-start also advances/resets timeIndex on
    // every cycle, which used to look identical to "genuine playback caught
    // up to the forward pin" — releasing a forward tap the user had JUST
    // made, before their second (seek-confirming) tap could land on it.
    // That silently ate the first tap, forcing a third tap to actually jump
    // to a forward marker while looping (never backward — see above), since
    // the tapped section was still ahead of wherever the OLD loop kept
    // replaying from. Once the tapped marker itself becomes the loop target
    // (loopSectionIndex === selectedMarkerIndex, set on the real second
    // tap), this exclusion no longer applies and normal auto-release
    // resumes for whatever gets tapped next.
    const isLoopChurnDifferentSection = loopMode === 'marker' && loopSectionIndex !== null && loopSectionIndex !== selectedMarkerIndex;
    if (selectedMarkerIndex !== null && selectedMarkerIndexIsForwardPreview && timeIndex > selectedMarkerIndex && !isLoopChurnDifferentSection) {
      selectedMarkerIndex = null;
      selectedMarkerIndexIsForwardPreview = false;
    }
    const displayIndex = pendingMarkerIndex ?? selectedMarkerIndex ?? timeIndex;

    // Left progress bar acts like a standard scrubber (Spotify-style): every
    // section already played shows full solid fill, the truly-playing
    // section fills partway, and everything ahead stays empty — a normal
    // continuous "how far through the whole song am I" readout, letting the
    // drummer tap anywhere (exact timestamp, no marker-snapping) to seek.
    const barFillPctFor = (i: number): number => {
      const marker = currentMarkers[i];
      const endT = i < currentMarkers.length - 1 ? currentMarkers[i + 1].timeSeconds : currentDuration;
      if (currentTime >= endT) return 100;
      if (currentTime <= marker.timeSeconds) return 0;
      return ((currentTime - marker.timeSeconds) / (endT - marker.timeSeconds)) * 100;
    };

    // While a marker-tap countdown is in flight, the section GENUINELY
    // playing (timeIndex) has its own trailing slice carved out as "this is
    // the countdown window" — the pending target (e.g. C1) hasn't started
    // yet and must show no fill of its own; the countdown time is really
    // being spent inside the section still playing (e.g. V1), specifically
    // the shrinking tail from the current playhead to the tapped marker's
    // own timestamp. Null whenever nothing's pending, so barFillPctFor's
    // normal 0-100% behavior is entirely unaffected for every other
    // section/state.
    const countInSplitFor = (
      i: number,
    ): { normalPct: number; countInLeftPct: number } | null => {
      if (!markerCountdownTarget || i !== timeIndex) return null;
      const marker = currentMarkers[i];
      const endT = i < currentMarkers.length - 1 ? currentMarkers[i + 1].timeSeconds : currentDuration;
      const sectionSpan = endT - marker.timeSeconds;
      if (sectionSpan <= 0) return null;
      const countInStart = markerCountdownTarget.markerTimeSeconds - markerCountdownTarget.leadInSeconds;
      const normalPct = Math.min(100, Math.max(0, ((countInStart - marker.timeSeconds) / sectionSpan) * 100));
      const countInLeftPct = Math.min(100, Math.max(normalPct, ((currentTime - marker.timeSeconds) / sectionSpan) * 100));
      return { normalPct, countInLeftPct };
    };

    // Setlist rows (right side) only ever color the section genuinely
    // playing right now (timeIndex) — every other row, whether already
    // played or still ahead, stays plain/uncolored rather than sitting at a
    // stale 100% or 0% fill. Keeps that list to exactly two things worth
    // calling out: the section actually playing, and (via is-pending
    // elsewhere) the lead-in target it's headed to — not a wall of
    // colored-in history. During a lead-in the real current section (e.g.
    // Verse 1) still keeps ticking up toward 100% normally; only which row
    // gets the highlight/label treatment (is-current/is-active/is-pending)
    // is affected by displayIndex, never this fill math.
    const rowFillPctFor = (i: number): number => (i === timeIndex ? barFillPctFor(i) : 0);

    // The top progress bar's "is-current" (gold border + label) tracks
    // timeIndex — the section GENUINELY playing right now — never
    // displayIndex. displayIndex (which becomes the pending marker's own
    // index while a countdown is in flight) still affects the setlist rows
    // below via is-pending/is-active, and the top bar now ALSO adds a
    // lighter "queued next" outline on whichever marker is SELECTED or
    // PENDING ahead of the current playhead. That keeps a tapped target
    // from reading like a mistake even before/while the countdown runs.
    // This queued outline must stay visually secondary to is-current: the
    // gold border remains the only "this audio is actually here now"
    // signal.
    // The count-in anticipation still also shows as a trailing fill INSIDE
    // the section still truly playing (see countInSplitFor above), so the
    // drummer gets both pieces of context at once: where they are, and
    // where playback will land when the countdown ends.
    const sectionEls = progressSections.querySelectorAll<HTMLDivElement>('.progress-section');
    currentMarkers.forEach((_marker, i) => {
      const isCurrent = i === timeIndex;
      const destinationIndex = pendingMarkerIndex ?? selectedMarkerIndex;
      const isPendingTarget = destinationIndex !== null && i === destinationIndex && i !== timeIndex;
      const sectionEl = sectionEls[i];
      if (!sectionEl) return;
      sectionEl.classList.toggle('is-current', isCurrent);
      sectionEl.classList.toggle('is-pending-target', isPendingTarget);
      const split = countInSplitFor(i);
      const fillEl = sectionEl.querySelector<HTMLDivElement>('.progress-section-fill');
      if (fillEl) fillEl.style.width = `${split ? split.normalPct : barFillPctFor(i)}%`;
      const countInFillEl = sectionEl.querySelector<HTMLDivElement>('.progress-section-countin-fill');
      if (countInFillEl) {
        countInFillEl.style.left = split ? 'auto' : '0';
        countInFillEl.style.right = '0';
        countInFillEl.style.width = `${split ? 100 - split.countInLeftPct : 0}%`;
      }
      const labelEl = sectionEl.querySelector<HTMLDivElement>('.progress-tick-label');
      labelEl?.classList.toggle('is-active', isCurrent);
      labelEl?.classList.toggle('is-pending-target', isPendingTarget);
    });

    // isPreRolling only suppresses the marker-row highlight for the SONG-
    // START pre-roll (no marker tapped — pendingMarkerIndex is null, so
    // "section 2"/whatever timeIndex resolves to from currentTime still at
    // ~0 would otherwise falsely look current before the file has actually
    // started). A MID-SONG marker-tap lead-in also reports PRE_ROLLING (same
    // controller mechanism) but has a real pendingMarkerIndex — that case
    // must still show is-pending on the tapped row, or tapping ahead into a
    // song that's already playing looks like the tap silently did nothing
    // during the whole countdown, with no visual difference from a mistake.
    const suppressForSongStartPreRoll = isPreRolling && pendingMarkerIndex === null;
    const markerList = setlistPanelScroll.querySelector<HTMLDivElement>('#setlist-marker-list');
    let activeRowEl: HTMLElement | null = null;
    markerList?.querySelectorAll<HTMLButtonElement>('.setlist-marker-row').forEach((row, i) => {
      const isPending = !suppressForSongStartPreRoll && pendingMarkerIndex !== null && i === pendingMarkerIndex;
      const isSelected = !suppressForSongStartPreRoll && pendingMarkerIndex === null && selectedMarkerIndex !== null && i === selectedMarkerIndex;
      const isActive = !suppressForSongStartPreRoll && !isPending && !isSelected && i === displayIndex;
      const split = countInSplitFor(i);
      const isCountingIn = split !== null && i === timeIndex;
      row.classList.toggle('is-selected', isSelected);
      row.classList.toggle('is-pending', isPending);
      row.classList.toggle('is-active', isActive);
      row.classList.toggle('is-counting-in', isCountingIn);
      row.classList.toggle('is-loop-target', loopMode === 'marker' && loopSectionIndex === i);
      const fillEl = row.querySelector<HTMLDivElement>('.setlist-marker-fill');
      if (fillEl) {
        fillEl.style.left = isCountingIn ? 'auto' : '0';
        fillEl.style.right = '0';
        fillEl.style.width = isCountingIn && split ? `${100 - split.countInLeftPct}%` : `${rowFillPctFor(i)}%`;
      }
      const durationEl = row.querySelector<HTMLSpanElement>('.setlist-marker-duration');
      if (durationEl) {
        const marker = currentMarkers[i];
        const sectionStart = marker?.timeSeconds ?? 0;
        const sectionEnd = marker ? markerSectionEnd(currentMarkers, i, currentDuration) : sectionStart;
        const sectionLength = Math.max(0, sectionEnd - sectionStart);
        const elapsedInSection = i === timeIndex ? Math.max(0, currentTime - sectionStart) : 0;
        const timer = syncedSectionTimerParts(elapsedInSection, sectionLength);
        durationEl.textContent =
          i === timeIndex && sectionLength > 0
            ? `${timer.elapsed} • ${timer.remaining}`
            : formatDuration(sectionLength);
      }
      if (isPending || isActive || isSelected) activeRowEl = row;
    });

    // Keep whichever row is genuinely current (or pending, mid-lead-in)
    // scrolled into view automatically — a long song's marker list can run
    // well past the visible setlist panel height, and without this the
    // current row can silently scroll off-screen as playback moves through
    // later sections, leaving the drummer staring at whatever the list
    // happened to be scrolled to instead of where they actually are in the
    // song. During a song-start pre-roll (isPreRolling with no pending
    // marker) there's nothing to scroll to yet — the count-in itself now
    // shows on the Play/Pause button (see updatePlayPauseCountIn), not a
    // row in this list — so activeRowEl stays null and this is a no-op
    // until real playback (or a mid-song marker-tap countdown, which DOES
    // have a pending row) gives it something to target. Only fires on a
    // genuine index CHANGE (see lastScrolledMarkerIndex's own comment) —
    // never on every render tick, so it doesn't fight a drummer
    // deliberately scrolling elsewhere to glance ahead.
    const targetIndex = isPreRolling ? -1 : displayIndex;
    if (targetIndex !== lastScrolledMarkerIndex && activeRowEl !== null) {
      lastScrolledMarkerIndex = targetIndex;
      const targetRow: HTMLElement = activeRowEl;
      targetRow.scrollIntoView({ block: 'nearest' });
    }
  }

  /** The three Play/Pause UI elements that all double as the count-in
   * indicator while one is actively counting down — the wide "now playing"
   * column's own button, the setlist panel's compact transport row, and the
   * current-song badge inline in the setlist row itself (only present while
   * that row is rendered, hence the live lookup rather than a cached
   * reference). Kept in one place so updatePlayPauseCountIn can't
   * accidentally drift the three out of sync with each other. */
  function playPauseCountInTargets(): Array<{ btn: HTMLButtonElement; icon: HTMLSpanElement }> {
    const targets = [
      { btn: playPauseBtn, icon: playPauseIcon },
      { btn: playPauseBtnCompact, icon: playPauseIconCompact },
    ];
    const badge = setlistPanelScroll.querySelector<HTMLButtonElement>('#setlist-panel-now-badge');
    const badgeIcon = setlistPanelScroll.querySelector<HTMLSpanElement>('#setlist-panel-now-badge-icon');
    if (badge && badgeIcon) targets.push({ btn: badge, icon: badgeIcon });
    return targets;
  }

  /** Live-drives the Play/Pause button (all three copies — see
   * playPauseCountInTargets) as the count-in indicator while EITHER kind of
   * count-in is genuinely in flight: the song-start pre-roll (`preRoll`,
   * from ControllerState — runs BEFORE the file starts, so has its own
   * remaining-time tracking already) or a marker-tap countdown (this
   * module's own markerCountdownTarget, computed from `currentTime` ticking
   * toward the marker's own timestamp — see markerCountdownTarget's own
   * comment for why a marker tap needs this at all: the seek-based lead-in
   * has no equivalent "count-in playing out" controller state the way the
   * pre-roll does). The two are mutually exclusive in practice (pre-roll
   * only exists at song start, marker taps only happen mid-song), so
   * whichever is non-null at call time wins; if neither is, the button(s)
   * go back to normal. While active: turns the button(s) gold
   * (.is-counting-in) and swaps the icon for a live-ticking digit — the
   * exact same "8, 7, 6...1" progression liveCountInLabel computes, reused
   * here (parsed back out of its own text) rather than re-deriving the
   * rounding separately — and mirrors liveCountInLabel's full text onto the
   * title/aria-label for screen readers. Must be called AFTER
   * renderControllerState's own plain PLAY/PAUSE icon logic on every tick
   * (see its call site) so this has the final say over the same DOM nodes
   * while counting in, rather than being immediately overwritten by it. */
  function updatePlayPauseCountIn(preRoll: ControllerState['preRoll'], currentTime = 0): void {
    const active = preRoll
      ? { totalSeconds: preRoll.totalSeconds, remainingSeconds: preRoll.remainingSeconds, secondsPerBeat: preRoll.secondsPerBeat }
      : markerCountdownTarget
        ? {
            totalSeconds: markerCountdownTarget.leadInSeconds,
            remainingSeconds: Math.max(0, markerCountdownTarget.markerTimeSeconds - currentTime),
            secondsPerBeat: markerCountdownTarget.secondsPerBeatAtTap,
          }
        : null;

    const targets = playPauseCountInTargets();
    if (!active) {
      // Icon content is left alone here — whichever plain PLAY/PAUSE icon
      // renderControllerState's own logic just set (this function always
      // runs after it) is already correct; only the gold styling and the
      // title/aria-label (set to a countdown string below while active)
      // need resetting back to the plain Play/Pause label.
      const isPlaying = controller.getState().status === 'PLAYING' || controller.getState().status === 'PRE_ROLLING';
      const label = isPlaying ? 'Pause' : 'Play';
      targets.forEach(({ btn }) => {
        btn.classList.remove('is-counting-in');
        btn.title = label;
        btn.setAttribute('aria-label', label);
      });
      return;
    }

    const label = liveCountInLabel(active);
    const digit = label.match(/\d+/)?.[0] ?? '';
    targets.forEach(({ btn, icon }) => {
      btn.classList.add('is-counting-in');
      icon.innerHTML = `<span class="play-pause-countin-digit">${digit}</span>`;
      btn.title = label;
      btn.setAttribute('aria-label', label);
    });
  }

  /** Checked on every controller tick while a loop is armed AND a section
   * is actually set (loopSectionIndex non-null — see its own comment). Once
   * playback reaches the end of that section (the next marker's timestamp,
   * or the song's own end if it's the last marker), jumps back to the
   * section's start and keeps playing, with NO lead-in/count-in — a drummer
   * looping a phrase needs it tight, not re-counted-in every lap. Only the
   * very first pass through a (re-)armed section gets the normal lead-in
   * (handled in the marker-tap handler itself, not here).
   *
   * Two distinct ways to "reach the end" need handling: mid-song, currentTime
   * simply crosses the boundary while status stays PLAYING (seek() alone
   * preserves PLAYING — see its own comment in PlaybackController); but
   * looping the LAST marker's section means the boundary IS the song's
   * genuine end, where the file has actually stopped and status has already
   * flipped to ENDED — a plain seek() there wouldn't resume playback (the
   * underlying media element already stopped), so that case explicitly
   * calls play() again after seeking back. */
  /** 'my-notes' has its own fixed label (it's not a CustomNoteTab and isn't
   * in the customNoteTabs list at all — see PlayerNotesTab's own comment);
   * every other tab (built-in or custom) is looked up fresh via
   * noteTabLabel, which folds in whatever CustomNoteTab entries currently
   * exist rather than assuming a fixed 3-entry set. */
  function playerNotesTabLabel(tab: PlayerNotesTab): string {
    if (tab === 'my-notes') return 'My Notes';
    return noteTabLabel(tab, appStore.getState().settings.customNoteTabs);
  }

  function getPlayerNotesText(songId: string, marker: SongMarker | undefined, tab: PlayerNotesTab): string {
    if (!marker) return '';
    return tab === 'my-notes' ? loadMyNotes(songId, marker.id) : markerNoteText(marker, tab);
  }

  function getPlayerNotesFormat(songId: string, marker: SongMarker | undefined, tab: PlayerNotesTab): MarkerNoteFormat {
    if (!marker) return 'text';
    return tab === 'my-notes' ? loadMyNotesFormat(songId, marker.id) : markerNoteFormat(marker, tab);
  }

  function getPlayerNotesSheet(songId: string, marker: SongMarker | undefined, tab: PlayerNotesTab): MarkerSheetData {
    if (!marker) return { rows: [] };
    return tab === 'my-notes' ? loadMyNotesSheet(songId, marker.id) : markerNoteSheet(marker, tab);
  }

  function renderPlayerNoteTab(marker: SongMarker | undefined, songId: string, tab: PlayerNotesTab, activeTab: PlayerNotesTab, pane: 'a' | 'b'): string {
    const hasContent = !!marker && (noteTextHasContent(getPlayerNotesText(songId, marker, tab)) || sheetHasContent(getPlayerNotesSheet(songId, marker, tab)));
    const isActive = tab === activeTab;
    const label = playerNotesTabLabel(tab);
    return `<button class="notes-tab${isActive ? ' is-active' : ''}${hasContent ? ' has-content' : ''}${tab === 'my-notes' ? ' notes-tab-local' : ''}" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}" data-action="set-player-notes-tab" data-notes-pane="${pane}" data-notes-tab="${tab}"><span class="notes-tab-label">${escapeHtml(label)}</span></button>`;
  }

  /** Fixed "My Notes" tab + ONE swappable second slot showing whichever of
   * Lyrics/Chords/General is this pane's current selection, with a small
   * chevron opening a dropdown to swap it for one of the other two — never
   * more than 2 tab buttons in the row, so there's no width to overflow
   * regardless of how many shared tabs exist or how narrow the pane is
   * (replaces the old "show every visible tab, scroll if they don't fit"
   * row, which could bleed/scroll unpredictably at narrow widths — see the
   * removed hiddenSharedNotesTabs/mobileRevealedNotesTabs machinery this
   * superseded, no longer needed now that only one shared tab is ever
   * shown at a time per pane). */
  function renderNotesTabRow(marker: SongMarker | undefined, songId: string, pane: PlayerNotesPaneSelection, paneAttr: 'a' | 'b'): string {
    const swapTab: MarkerNoteTab = pane.tab === 'my-notes' ? 'lyrics' : pane.tab;
    const isSwapOpen = notesTabSwapMenuOpenPane === paneAttr;
    // The chevron menu lists every other available tab, not just the hidden
    // swap-slot candidate, so narrow layouts can collapse to the active tab
    // plus one menu without losing access to the rest.
    const otherTabs: PlayerNotesTab[] = ['my-notes', ...playerSharedNotesTabs()].filter((tab) => tab !== pane.tab);
    return `
      ${renderPlayerNoteTab(marker, songId, 'my-notes', pane.tab, paneAttr)}
      <div class="notes-tab-swap-wrap">
        ${renderPlayerNoteTab(marker, songId, swapTab, pane.tab, paneAttr)}
        <button type="button" class="notes-tab-swap-chevron${isSwapOpen ? ' is-active' : ''}" data-action="toggle-notes-tab-swap-menu" data-notes-pane="${paneAttr}" aria-expanded="${String(isSwapOpen)}" aria-haspopup="true" title="Swap this tab" aria-label="Swap this tab">
          <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true"><path d="m5 7 5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="player-header-menu notes-tab-swap-menu" data-role="notes-tab-swap-menu" ${isSwapOpen ? '' : 'hidden'}>
          ${otherTabs
            .map((tab) => {
              const hasContent = !!marker && (noteTextHasContent(getPlayerNotesText(songId, marker, tab)) || sheetHasContent(getPlayerNotesSheet(songId, marker, tab)));
              return `<button type="button" class="${hasContent ? 'has-content' : ''}" data-action="set-player-notes-tab" data-notes-pane="${paneAttr}" data-notes-tab="${tab}">${escapeHtml(playerNotesTabLabel(tab))}</button>`;
            })
            .join('')}
        </div>
      </div>
    `;
  }

  function renderCompactNotesInlineTabs(marker: SongMarker | undefined, songId: string, pane: PlayerNotesPaneSelection, paneAttr: 'a' | 'b'): string {
    return `<div class="row notes-title-inline-tabs notes-tabs notes-tabs-shell">
      ${renderNotesTabRow(marker, songId, pane, paneAttr)}
    </div>`;
  }

  function renderNotesEditToggleButton(): string {
    return `<button class="player-icon-btn notes-edit-toggle${notesEditMode ? ' is-active' : ''}" data-action="toggle-notes-edit-mode" title="${notesEditMode ? 'Done editing' : 'Edit notes'}" aria-label="${notesEditMode ? 'Done editing' : 'Edit notes'}" aria-pressed="${String(notesEditMode)}">
      ${
        notesEditMode
          ? `<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="m4 10 4 4 8-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
          : `<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M13.5 3.5a1.5 1.5 0 0 1 2.12 0l.88.88a1.5 1.5 0 0 1 0 2.12L7 16H4v-3z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`
      }
    </button>`;
  }

  function renderCompactNotesInlineTools(marker: SongMarker | undefined, tab: PlayerNotesTab, format: MarkerNoteFormat): string {
    if (!marker) return '';
    return `${tab === 'my-notes' ? '' : renderNoteZoomControl(format, 'data-role="notes-pill-zoom"')}${renderNotesEditToggleButton()}`;
  }

  /** The edit toolbar — Mode: Text/Sheet, Clear, and (for My Notes) the
   * "Local" badge — lives INSIDE the note content itself now, as a real
   * row pinned to the top of the shell (like a standard text editor's own
   * toolbar), not a floating dropdown anchored off the pencil button. That
   * removes the whole "tap into the text to dismiss the menu" dance the
   * dropdown needed: there's no menu to dismiss anymore, it's just always
   * there for as long as notesEditMode is on. Only ever rendered when
   * notesEditMode is true (see both call sites). My Notes gets the exact
   * same Mode toggle as every other tab now — see set-player-notes-format's
   * own comment for why its Sheet content is stored locally rather than on
   * the shared marker record. */
  function renderNotesEditToolbar(marker: SongMarker, tab: PlayerNotesTab, format: MarkerNoteFormat, isEmpty: boolean): string {
    return `<div class="notes-edit-toolbar">
      ${tab === 'my-notes' ? `<span class="note-toolbar-btn note-toolbar-local" aria-hidden="true">Private</span>` : ''}
      <button type="button" class="note-toolbar-btn note-mode-btn" data-action="set-player-notes-format" data-marker-id="${marker.id}" data-notes-tab="${tab}" data-note-format="${format === 'sheet' ? 'text' : 'sheet'}" title="Switch to ${format === 'sheet' ? 'Text' : 'Sheet'}">Mode: ${format === 'sheet' ? 'Sheet' : 'Text'} <span aria-hidden="true">${format === 'sheet' ? '↺' : '↻'}</span></button>
      <span class="notes-edit-saved-indicator" data-role="notes-saved-indicator" aria-live="polite"></span>
      <button type="button" class="note-toolbar-btn notes-edit-toolbar-clear" data-action="clear-player-notes" data-marker-id="${marker.id}" data-notes-tab="${tab}" ${isEmpty ? 'disabled' : ''}>Clear</button>
    </div>`;
  }

  /** Renders the notes panel for whichever marker is currently the
   * DISPLAYED one — pendingMarkerIndex if a marker tap's lead-in/count-in
   * is still running, otherwise the genuinely-playing timeIndex — exactly
   * the same displayIndex concept updateMarkerProgress uses for the
   * progress bar itself (see its own doc comment). Deliberately NOT plain
   * timeIndex/currentMarkerIndex alone: someone taps "Verse 1" specifically
   * to practice it, and the lead-in's whole point is that PLAYBACK is still
   * technically inside the PREVIOUS section for those few seconds — the
   * notes panel flipping back to the previous section's lyrics the instant
   * they tap the marker they actually want, then flipping again once
   * playback catches up, would be actively unhelpful mid-countdown, exactly
   * when they're getting ready to read along. Into BOTH the wide and
   * compact panel instances at once (they always show the same content;
   * only CSS decides which one is actually visible in a given layout).
   * Rebuilds DOM only when the active marker or tab genuinely changed since
   * the last call — see lastRenderedNotesKey's own comment for why. */
  /** Shared text/sheet content template for BOTH notes panes — factored out
   * so Pane A (editable) and Pane B (always read-only) can't drift out of
   * sync visually while differing only in editability and whether the
   * section-progress bar makes sense to show (see `showProgress`'s own
   * note below). */
  function renderNotesPaneContent(params: {
    marker: SongMarker;
    songId: string;
    tab: PlayerNotesTab;
    format: MarkerNoteFormat;
    isEmpty: boolean;
    editable: boolean;
    showProgress: boolean;
  }): string {
    const { marker, songId, tab, format, isEmpty, editable, showProgress } = params;
    const editMode = editable && notesEditMode;
    const progressBar = showProgress
      ? `<div class="notes-section-progress" aria-hidden="true">
          <div class="notes-section-progress-fill"></div>
          <div class="notes-section-preview-zone"></div>
          <div class="notes-section-preview-threshold"></div>
          <span class="notes-section-time notes-section-time-elapsed">0:00</span>
          <span class="notes-section-time notes-section-time-remaining">--:--</span>
        </div>`
      : '';
    if (format === 'text') {
      return `
        <div class="notes-textarea-shell${isEmpty ? ' is-empty' : ''}">
          ${progressBar}
          ${editMode ? renderNotesEditToolbar(marker, tab, format, isEmpty) : ''}
          ${
            !editMode && isEmpty
              ? `<div class="note-empty-hint note-empty-hint-player">
                  ${editable ? `<button type="button" class="note-empty-hint-edit-btn" data-action="toggle-notes-edit-mode">Edit</button>` : ''}
                  <span class="note-empty-hint-text">${tab === 'my-notes' ? 'No personal notes yet.' : 'No notes yet.'}</span>
                </div>`
              : ''
          }
          <textarea class="notes-textarea" data-action="edit-player-notes" data-marker-id="${marker.id}" data-notes-tab="${tab}" placeholder="${
            // A real placeholder, not a separate overlay element —
            // placeholders disappear the instant there's ANY text
            // natively, no rerender/JS needed. The old overlay
            // approach only updated on the next full DOM rebuild
            // (lastRenderedNotesKey doesn't track live keystrokes,
            // deliberately — see its own comment), so it visibly
            // stayed on screen overlapping whatever was just typed
            // until something else forced a rebuild. Only set at
            // all while editMode is on — a READONLY textarea
            // still renders its own placeholder even though it
            // can't be typed into, which duplicated the separate
            // .note-empty-hint message shown in that state (see
            // just above) with two different pieces of empty-
            // state copy stacked on top of each other.
            editMode ? (tab === 'my-notes' ? 'Start typing your personal notes.' : 'Start typing notes, or paste in a table from Google Sheets using the Mode button above.') : ''
          }" aria-label="${playerNotesTabLabel(tab)} notes" ${editMode ? '' : 'readonly tabindex="-1"'}>${escapeHtml(getPlayerNotesText(songId, marker, tab))}</textarea>
        </div>`;
    }
    return `<div class="notes-sheet-shell">
        ${progressBar}
        ${editMode ? renderNotesEditToolbar(marker, tab, format, isEmpty) : ''}
        <div class="notes-sheet-wrap note-sheet-editor${editMode ? '' : ' is-readonly'}" ${editMode ? `tabindex="0" data-action="edit-player-sheet"` : ''} data-marker-id="${marker.id}" data-notes-tab="${tab}">
          ${
            sheetHasContent(getPlayerNotesSheet(songId, marker, tab))
              ? renderSheetTable(getPlayerNotesSheet(songId, marker, tab), editMode)
              : editMode
                ? `<div class="note-sheet-empty">
                    <strong>Paste cells from Google Sheets</strong>
                    <span>Copy a range, click here, and paste. Maximum ${MAX_NOTE_SHEET_ROWS} rows × ${MAX_NOTE_SHEET_COLUMNS} columns.</span>
                  </div>`
                : `<div class="note-sheet-empty">
                    ${editable ? `<button type="button" class="note-empty-hint-edit-btn" data-action="toggle-notes-edit-mode">Edit</button>` : ''}
                    <span>No notes yet.</span>
                  </div>`
          }
        </div>
      </div>`;
  }

  function renderNotesPanel(): void {
    // The early-swap lead-in (see currentNotesEarlySwapSeconds) shifts which
    // section counts as "current" for NOTES DISPLAY purposes only — it
    // looks up currentMarkerIndex against a moment slightly ahead of the
    // real playhead, so the panel swaps to the upcoming section's content a
    // few seconds/beats before the audio actually gets there. This never
    // touches actual playback/seek position, only which marker's notes are
    // shown. This resolution only ever concerns Pane A/'current' timing —
    // Pane B, when
    // set to 'upcoming', always means "one past whatever Pane A resolves
    // to" (see upcomingMarkerIndexResolved below), with no early/late
    // nuance of its own: it's already showing "ahead" content by
    // definition, so early-swap's job (nudge a 'current' pane ahead a
    // little early) doesn't apply to it.
    const actualTimeIndex = Math.max(0, currentMarkerIndex(currentMarkers, controller.getState().currentTime));
    const earlySwapSeconds = currentNotesEarlySwapSeconds();
    const timeIndex =
      earlySwapSeconds > 0
        ? Math.max(0, currentMarkerIndex(currentMarkers, controller.getState().currentTime + earlySwapSeconds))
        : actualTimeIndex;
    const currentMarkerIndexResolved = pendingMarkerIndex ?? selectedMarkerIndex ?? timeIndex;
    // 'upcoming' timing always means "one past whatever the 'current'
    // resolution landed on" — preserves exactly what Pane B showed before
    // it became independently configurable (currentMarkers[markerIndex+1],
    // where markerIndex could itself be early-swapped/pending/selected),
    // NOT "one past the real actual playhead."
    const upcomingMarkerIndexResolved = currentMarkerIndexResolved + 1;
    function resolveNotesPane(pane: PlayerNotesPaneSelection): { markerIndex: number; marker: SongMarker | undefined } {
      const markerIndex = pane.timing === 'upcoming' ? upcomingMarkerIndexResolved : currentMarkerIndexResolved;
      return { markerIndex, marker: currentMarkers[markerIndex] as SongMarker | undefined };
    }
    const resolvedA = resolveNotesPane(notesPaneA);
    const resolvedB = resolveNotesPane(notesPaneB);
    const songId = activeSongForNotes ?? getCurrentSong()?.id ?? 'none';

    const formatA = getPlayerNotesFormat(songId, resolvedA.marker, notesPaneA.tab);
    const textHasContentA = noteTextHasContent(getPlayerNotesText(songId, resolvedA.marker, notesPaneA.tab));
    const sheetHasContentA = resolvedA.marker ? sheetHasContent(getPlayerNotesSheet(songId, resolvedA.marker, notesPaneA.tab)) : false;
    const isEmptyA = !textHasContentA && !sheetHasContentA;

    const formatB = getPlayerNotesFormat(songId, resolvedB.marker, notesPaneB.tab);
    const textHasContentB = noteTextHasContent(getPlayerNotesText(songId, resolvedB.marker, notesPaneB.tab));
    const sheetHasContentB = resolvedB.marker ? sheetHasContent(getPlayerNotesSheet(songId, resolvedB.marker, notesPaneB.tab)) : false;
    const isEmptyB = !textHasContentB && !sheetHasContentB;

    // A marker change out from under an open edit session (song switch,
    // or a marker tap elsewhere while paused) drops back to read-only
    // automatically — editing should always require a fresh, deliberate
    // tap of the pencil button for whichever section is currently on
    // screen, never silently carry over from a previous one. In practice
    // this rarely fires mid-edit anymore since turning edit mode ON
    // already pauses playback (see the toggle-notes-edit-mode click
    // handler), which is what used to move the marker out from under an
    // open edit session in the first place. Scoped to Pane A only —
    // editing is Pane-A-exclusive regardless of either pane's tab/timing.
    if (resolvedA.marker?.id !== lastEditModeMarkerId) {
      notesEditMode = false;
      lastEditModeMarkerId = resolvedA.marker?.id ?? null;
    }
    const key = `${songId}:${resolvedA.marker?.id ?? 'none'}:${notesPaneA.tab}:${notesPaneA.timing}:${formatA}:${resolvedB.marker?.id ?? 'none'}:${notesPaneB.tab}:${notesPaneB.timing}:${formatB}:${notesEditMode}:${notesLayout}`;
    if (key === lastRenderedNotesKey) return;
    lastRenderedNotesKey = key;
    const marker = resolvedA.marker;

    const html = !marker
      ? `<p class="empty-state">No markers set up for this song yet.</p>`
      : `
        <div class="notes-content-split">
          <div class="notes-pane-a">
            <div class="notes-header-block${notesTabSwapMenuOpenPane === 'a' || notesLayoutMenuOpen ? ' has-open-menu' : ''}">
              <div class="row notes-title-row">
                <div class="notes-panel-current">${escapeHtml(marker.label || 'Untitled section')}</div>
                ${renderCompactNotesInlineTabs(resolvedA.marker, songId, notesPaneA, 'a')}
                <div class="notes-title-row-controls">
                  ${notesPaneA.tab === 'my-notes' ? '' : renderNoteZoomControl(formatA, 'data-role="notes-header-zoom"')}
                  ${renderNotesEditToggleButton()}
                  <div class="notes-layout-menu-wrap">
                    <button class="player-icon-btn notes-layout-toggle" data-action="toggle-notes-layout-menu" title="Notes settings: ${NOTES_LAYOUT_LABELS[notesLayout]}" aria-label="Notes settings" aria-expanded="${String(notesLayoutMenuOpen)}" aria-haspopup="true">
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="5" cy="12" r="2.1" fill="currentColor"/><circle cx="12" cy="12" r="2.1" fill="currentColor"/><circle cx="19" cy="12" r="2.1" fill="currentColor"/></svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="row notes-header-row">
                <div class="row notes-tabs notes-tabs-shell">
                  ${renderNotesTabRow(resolvedA.marker, songId, notesPaneA, 'a')}
                </div>
              </div>
            </div>
            ${renderNotesPaneContent({
              marker,
              songId,
              tab: notesPaneA.tab,
              format: formatA,
              isEmpty: isEmptyA,
              editable: true,
              showProgress: true,
            })}
          </div>
          ${
            notesLayout === 'current-only'
              ? ''
              : `
          <div class="notes-resize-handle" data-action="resize-notes-preview" role="separator" aria-label="Resize the two notes panes" aria-orientation="horizontal" tabindex="0"></div>
          <div class="notes-pane-b">
            <div class="notes-header-block${notesTabSwapMenuOpenPane === 'b' ? ' has-open-menu' : ''}">
              <div class="row notes-title-row">
                <div class="notes-panel-current">${resolvedB.marker ? escapeHtml(resolvedB.marker.label || 'Untitled section') : notesPaneB.timing === 'upcoming' ? 'Last section' : 'No section'}</div>
                ${resolvedB.marker ? renderCompactNotesInlineTabs(resolvedB.marker, songId, notesPaneB, 'b') : ''}
                <div class="notes-title-row-controls">
                  ${notesPaneB.tab === 'my-notes' ? '' : renderNoteZoomControl(formatB, 'data-role="notes-header-zoom"')}
                  <div class="notes-pane-timing-toggle" role="group" aria-label="Pane B timing">
                    <button type="button" class="notes-timing-btn${notesPaneB.timing === 'current' ? ' is-selected' : ''}" data-action="set-notes-pane-timing" data-notes-pane="b" data-notes-timing="current">Now</button>
                    <button type="button" class="notes-timing-btn${notesPaneB.timing === 'upcoming' ? ' is-selected' : ''}" data-action="set-notes-pane-timing" data-notes-pane="b" data-notes-timing="upcoming">Next</button>
                  </div>
                </div>
              </div>
              ${
                resolvedB.marker
                  ? `<div class="row notes-header-row">
                      <div class="row notes-tabs notes-tabs-shell notes-tabs-compact">
                        ${renderNotesTabRow(resolvedB.marker, songId, notesPaneB, 'b')}
                      </div>
                    </div>`
                  : ''
              }
            </div>
            ${
              resolvedB.marker
                ? renderNotesPaneContent({
                    marker: resolvedB.marker,
                    songId,
                    tab: notesPaneB.tab,
                    format: formatB,
                    isEmpty: isEmptyB,
                    editable: false,
                    showProgress: notesPaneB.timing === 'current',
                  })
                : `<p class="empty-state notes-pane-b-empty">${notesPaneB.timing === 'upcoming' ? "You're on the last section — nothing coming up next." : 'No section to show.'}</p>`
            }
          </div>`
          }
        </div>
        ${renderNotesLayoutDrawer(getCurrentSong())}
      `;
    notesPanelWide.innerHTML = html;
    notesPanelCompact.innerHTML = html;
    notesPanelWide.querySelectorAll('.notes-title-inline-tabs').forEach((node) => node.remove());
    notesPanelCompact.querySelectorAll('.notes-title-row-controls, .notes-header-row').forEach((node) => node.remove());
    compactNotesInlineTools.innerHTML = renderCompactNotesInlineTools(marker, notesPaneA.tab, formatA);
    applyNoteZoom(notesPanelWide);
    applyNoteZoom(notesPanelCompact);
    scheduleNotesInlineTabCollapse();
    updateNotesSectionProgress();
  }

  /** Thin bar along the Current notes panel's own bottom edge, showing how
   * much of the DISPLAYED section's own time window is left before the
   * panel would naturally switch to the next one — a glanceable "how long
   * have I got" for whoever's reading lyrics/chords instead of watching
   * the transport clock. Drains right-to-left (starts full, empties as
   * time passes — see the CSS's own comment on why this reads as
   * "time left," not "time elapsed," despite being a plain width value).
   * Uses the same displayed-marker resolution renderNotesPanel itself uses
   * (pendingMarkerIndex ?? selectedMarkerIndex ?? real timeIndex) so the
   * meter always matches whichever section's notes are actually on
   * screen — including a manually pinned preview, not just the live
   * playhead. Uses ungated-per-tick / plain style.width and style variable
   * writes rather than a full notes-panel rebuild, so it never disturbs
   * textarea focus/scroll. */
  function updateNotesSectionProgress(): void {
    const panels = [notesPanelWide, notesPanelCompact];
    const currentTime = controller.getState().currentTime;
    const actualTimeIndex = Math.max(0, currentMarkerIndex(currentMarkers, currentTime));
    const markerIndex = pendingMarkerIndex ?? selectedMarkerIndex ?? actualTimeIndex;
    const marker = currentMarkers[markerIndex];
    if (!marker) return;
    const sectionStart = marker.timeSeconds;
    const sectionEnd = markerSectionEnd(currentMarkers, markerIndex, currentDuration);
    // A manually pinned marker (tapped ahead of or behind the live
    // playhead) isn't necessarily where real playback currently sits —
    // the bar has nothing meaningful to drain against until playback
    // actually reaches that section, so it just shows full rather than a
    // nonsensical negative/over-100% value.
    const elapsedInSection = markerIndex === actualTimeIndex ? currentTime - sectionStart : 0;
    const sectionLength = sectionEnd - sectionStart;
    const remainingPct = sectionLength > 0 ? Math.max(0, Math.min(100, (1 - elapsedInSection / sectionLength) * 100)) : 100;
    const earlySwapSeconds = currentNotesEarlySwapSeconds();
    const upcomingMarker = currentMarkers[actualTimeIndex + 1] as SongMarker | undefined;
    const canShowPreviewCue =
      pendingMarkerIndex === null &&
      selectedMarkerIndex === null &&
      markerIndex === actualTimeIndex &&
      sectionLength > 0 &&
      earlySwapSeconds > 0 &&
      upcomingMarker !== undefined;
    const previewZoneWidthPct = canShowPreviewCue ? Math.max(0, Math.min(100, (earlySwapSeconds / sectionLength) * 100)) : 0;
    const previewZoneLeftPct = 100 - previewZoneWidthPct;
    const secondsUntilUpcomingMarker = upcomingMarker ? upcomingMarker.timeSeconds - currentTime : Number.POSITIVE_INFINITY;
    const isPreviewActive = canShowPreviewCue && secondsUntilUpcomingMarker <= earlySwapSeconds;
    const isPreviewWarningActive =
      canShowPreviewCue &&
      !isPreviewActive &&
      notesPreviewWarningSeconds > 0 &&
      secondsUntilUpcomingMarker <= earlySwapSeconds + notesPreviewWarningSeconds;
    const timer = syncedSectionTimerParts(elapsedInSection, sectionLength);
    const elapsedLabel = timer.elapsed;
    const remainingLabel = sectionLength > 0 ? `-${timer.remaining}` : '--:--';
    panels.forEach((panel) => {
      panel.querySelectorAll<HTMLElement>('.notes-section-progress').forEach((progressEl) => {
        progressEl.classList.toggle('has-preview-cue', canShowPreviewCue);
        progressEl.classList.toggle('is-preview-warning', isPreviewWarningActive);
        progressEl.classList.toggle('is-preview-active', isPreviewActive);
        progressEl.style.setProperty('--notes-section-preview-zone-left', `${previewZoneLeftPct}%`);
        progressEl.style.setProperty('--notes-section-preview-zone-width', `${previewZoneWidthPct}%`);
        progressEl.querySelector<HTMLElement>('.notes-section-progress-fill')?.style.setProperty('width', `${remainingPct}%`);
        const elapsedEl = progressEl.querySelector<HTMLElement>('.notes-section-time-elapsed');
        const remainingEl = progressEl.querySelector<HTMLElement>('.notes-section-time-remaining');
        if (elapsedEl) elapsedEl.textContent = elapsedLabel;
        if (remainingEl) remainingEl.textContent = remainingLabel;
      });
    });
  }

  function checkLoopBoundary(state: ControllerState): void {
    if (state.status !== 'PLAYING' && state.status !== 'ENDED') return;

    if (loopMode === 'marker') {
      if (loopSectionIndex === null) return;
      const marker = currentMarkers[loopSectionIndex];
      if (!marker) return;
      const sectionEnd = markerSectionEnd(currentMarkers, loopSectionIndex, currentDuration);
      if (state.status === 'ENDED' || state.currentTime >= sectionEnd) {
        pendingMarkerIndex = null;
        markerCountdownTarget = null;
        controller.seek(marker.timeSeconds);
        if (state.status === 'ENDED') void controller.play();
      }
      return;
    }

    // 'song' and 'setlist' only ever act once the song has genuinely
    // finished, never mid-playback — unlike a marker section, there's no
    // meaningful "boundary" before the real end to react to.
    if (state.status !== 'ENDED' || loopAutoAdvancing) return;

    if (loopMode === 'song') {
      pendingMarkerIndex = null;
      markerCountdownTarget = null;
      controller.seek(0);
      void controller.play();
      return;
    }

    if (loopMode === 'setlist') {
      const setlist = getActiveSetlist();
      if (!setlist || setlist.entries.length === 0) return;
      const nextIndex = (appStore.getState().settings.activeEntryIndex + 1) % setlist.entries.length;
      loopAutoAdvancing = true;
      void jumpToEntry(nextIndex)
        .then(() => {
          const song = getCurrentSong();
          const leadIn = isRehearsalMode && song ? resolveEffectiveLeadIn(getActiveSetlist()?.countInOverride ?? null, song) : null;
          const pickupBeats = song
            ? pickupBeatsFromFirstBeatTime(song.firstBeatTimeSeconds, song.metronomeSettings, song.syntheticClickOffsetSeconds)
            : 0;
          return controller.play(leadIn, pickupBeats, song?.firstBeatTimeSeconds ?? null);
        })
        .finally(() => {
          loopAutoAdvancing = false;
        });
    }
  }

  function renderControllerState(state: ControllerState): void {
    checkLoopBoundary(state);
    const duration = state.duration || getEntrySong(appStore.getState().settings.activeEntryIndex)?.durationSeconds || 0;
    updateMarkerProgress(state.currentTime, state.status === 'PRE_ROLLING');
    renderNotesPanel();
    updateNotesSectionProgress();
    if (duration > 0) {
      const timer = syncedSectionTimerParts(state.currentTime, duration);
      elapsedEl.textContent = timer.elapsed;
      remainingEl.textContent = `-${timer.remaining}`;
    } else {
      elapsedEl.textContent = formatDuration(state.currentTime);
      remainingEl.textContent = '--:--';
    }
    syncSyntheticBeatFlash(state.status);

    const canOperate = state.activeEngine !== 'none';

    // Status is folded into the single Play/Pause toggle button — the
    // standard "one button, label/icon changes" pattern from consumer media
    // players. Stop stays a separate, always-distinct button since it means
    // something different from Pause (reset to 0:00, not just freeze).
    // A small flag above the title only appears for ERROR and ENDED, the
    // two states no two-word button label can communicate.
    const setPlayPauseDisplay = (icon: HTMLSpanElement): void => {
      if (state.status === 'PLAYING' || state.status === 'PRE_ROLLING') {
        // Pre-roll reads as "playing" from the button's perspective —
        // tapping it cancels the count-in (pause() already handles
        // PRE_ROLLING), same gesture as pausing genuine playback.
        icon.innerHTML = ICON_PAUSE;
      } else {
        icon.innerHTML = ICON_PLAY;
      }
    };
    setPlayPauseDisplay(playPauseIcon);
    setPlayPauseDisplay(playPauseIconCompact);

    // The current-song badge in the setlist panel doubles as a play/pause
    // toggle — it's only ever rendered once per renderSetlistRows call (song
    // change), so its icon/label need this same live update on every
    // controller tick rather than waiting for the next full row rebuild.
    const setlistNowBadge = setlistPanelScroll.querySelector<HTMLButtonElement>('#setlist-panel-now-badge');
    const setlistNowBadgeIcon = setlistPanelScroll.querySelector<HTMLSpanElement>('#setlist-panel-now-badge-icon');
    if (setlistNowBadge && setlistNowBadgeIcon) {
      const isPlaying = state.status === 'PLAYING' || state.status === 'PRE_ROLLING';
      setlistNowBadgeIcon.innerHTML = isPlaying ? ICON_PAUSE : ICON_PLAY;
      setlistNowBadge.title = isPlaying ? 'Pause' : 'Play';
      setlistNowBadge.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    }

    // Must run AFTER the plain PLAY/PAUSE icon logic directly above (both
    // the wide/compact buttons and the setlist badge) — while a count-in is
    // genuinely in flight (song-start pre-roll OR a mid-song marker-tap
    // countdown), this has the final say over the exact same icon/title
    // slots, overriding the plain PAUSE icon those just set with a live
    // countdown digit instead. See updatePlayPauseCountIn's own comment.
    updatePlayPauseCountIn(state.preRoll, state.currentTime);

    if (state.status === 'ERROR') {
      statusFlag.textContent = state.errorMessage ? `ERROR — ${state.errorMessage.toUpperCase()}` : 'ERROR';
      statusFlag.className = 'status-flag flag-error';
      statusFlag.style.display = 'block';
    } else if (state.status === 'ENDED') {
      statusFlag.textContent = 'ENDED — READY FOR NEXT SONG';
      statusFlag.className = 'status-flag flag-ended';
      statusFlag.style.display = 'block';
    } else {
      statusFlag.style.display = 'none';
    }

    playPauseBtn.disabled = !canOperate;
    playPauseBtnCompact.disabled = !canOperate;

    // Loop reads as "on" (is-active) for all three modes alike — marker mode
    // additionally reads as "actively looping" once a section is genuinely
    // set (loopSectionIndex), but that distinction matters for the LOOPING
    // LOGIC itself (checkLoopBoundary), not for what the button shows. The
    // mode label underneath the icon is what actually tells the drummer
    // WHAT is looping — a plain gold highlight alone reads as "loop is on"
    // but not "on for just this one section," which was genuinely ambiguous
    // once song/setlist joined marker as loop targets.
    const loopTitle: Record<LoopMode, string> = {
      off: 'Loop off — tap to loop the active section',
      marker: 'Looping the active section — tap for whole-song loop',
      song: 'Looping this song — tap for whole-setlist loop',
      setlist: 'Looping the setlist — tap to turn loop off',
    };
    const loopModeText: Record<LoopMode, string> = { off: '', marker: 'Section', song: 'Song', setlist: 'Setlist' };
    loopBtn.classList.toggle('is-active', loopMode !== 'off');
    loopBtn.disabled = !canOperate;
    loopBtn.title = loopTitle[loopMode];
    loopModeLabel.textContent = loopModeText[loopMode];
    loopBtnCompact.classList.toggle('is-active', loopMode !== 'off');
    loopBtnCompact.disabled = !canOperate;
    loopBtnCompact.title = loopTitle[loopMode];
    loopModeLabelCompact.textContent = loopModeText[loopMode];

    const setlist = getActiveSetlist();
    const index = appStore.getState().settings.activeEntryIndex;
    prevSongBtn.disabled = !setlist;
    prevSongBtnCompact.disabled = !setlist;
    nextSongBtn.disabled = !setlist || index >= setlist.entries.length - 1;
    nextSongBtnCompact.disabled = !setlist || index >= setlist.entries.length - 1;
  }

  // Online/screen-lock status has no on-screen indicator in Performance
  // mode anymore (moved to Settings/Diagnostics) — but re-acquiring the
  // wake lock whenever it drops is a real functional behavior (keeps the
  // screen on during a show) that still needs to run regardless of display.
  function handleWakeLockStatus(status: WakeLockStatus): void {
    if (status !== 'active' && status !== 'unsupported') {
      void wakeLockManager.acquire();
    }
  }

  async function handlePlayPauseTap(): Promise<void> {
    if (isStartingPlay) {
      // Preserve every press while browser playback startup is resolving:
      // odd presses request Pause, an even press toggles back to Play.
      pauseRequestedDuringStart = !pauseRequestedDuringStart;
      return;
    }
    const status = controller.getState().status;
    if (status === 'PLAYING' || status === 'PRE_ROLLING') {
      controller.pause();
    } else {
      expandCurrentSetlistDetails();
      // Gated by Rehearsal mode, same as the marker-tap lead-in itself — in
      // Live mode, Play starts the song immediately.
      // Without this gate, EVERY song start would carry its configured
      // lead-in as a mandatory silent delay, all the time, for every user —
      // not what "always applies" was meant to cover. Lead-in is now a
      // per-song setting (configured in the Song Library), not global.
      const currentSong = getCurrentSong();
      const leadIn = isRehearsalMode && currentSong ? resolveEffectiveLeadIn(getActiveSetlist()?.countInOverride ?? null, currentSong) : null;
      const pickupBeats = currentSong
        ? pickupBeatsFromFirstBeatTime(currentSong.firstBeatTimeSeconds, currentSong.metronomeSettings, currentSong.syntheticClickOffsetSeconds)
        : 0;
      isStartingPlay = true;
      pauseRequestedDuringStart = false;
      try {
        await controller.play(leadIn, pickupBeats, currentSong?.firstBeatTimeSeconds ?? null);
      } finally {
        isStartingPlay = false;
      }
      if (pauseRequestedDuringStart) {
        pauseRequestedDuringStart = false;
        controller.pause();
      }
    }
  }

  const handlePlayPausePointerUp = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    event.preventDefault();
    void handlePlayPauseTap();
  };
  const handlePlayPauseKeydown = (event: KeyboardEvent): void => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    void handlePlayPauseTap();
  };
  playPauseBtn.addEventListener('pointerup', handlePlayPausePointerUp);
  playPauseBtnCompact.addEventListener('pointerup', handlePlayPausePointerUp);
  playPauseBtn.addEventListener('keydown', handlePlayPauseKeydown);
  playPauseBtnCompact.addEventListener('keydown', handlePlayPauseKeydown);

  /** Each tap cycles to the next mode: off -> marker -> song -> setlist ->
   * off. Landing on 'marker' does NOT by itself start looping anything: it
   * just arms the NEXT marker tap to loop that section (see the marker-tap
   * handler below), same as how tapping Loop on a real device doesn't loop
   * a random spot — it loops wherever you navigate to next. Leaving 'marker'
   * mode (for 'song', 'setlist', or 'off') clears which section was
   * looping, so going back to 'marker' later starts fresh rather than
   * silently resuming whatever section happened to be looping before. */
  const LOOP_MODE_CYCLE: LoopMode[] = ['off', 'marker', 'song', 'setlist'];
  function handleLoopTap(): void {
    loopMode = LOOP_MODE_CYCLE[(LOOP_MODE_CYCLE.indexOf(loopMode) + 1) % LOOP_MODE_CYCLE.length];
    if (loopMode !== 'marker') loopSectionIndex = null;
    renderControllerState(controller.getState());
  }
  loopBtn.addEventListener('click', handleLoopTap);
  loopBtnCompact.addEventListener('click', handleLoopTap);

  prevSongBtn.addEventListener('click', () => {
    void handleTransportBackTap();
  });
  prevSongBtnCompact.addEventListener('click', () => {
    void handleTransportBackTap();
  });
  nextSongBtn.addEventListener('click', () => {
    void jumpToEntry(appStore.getState().settings.activeEntryIndex + 1);
  });
  nextSongBtnCompact.addEventListener('click', () => {
    void jumpToEntry(appStore.getState().settings.activeEntryIndex + 1);
  });

  /** Narrow/portrait layout only — switches the setlist panel's bottom half
   * between the setlist itself and the current marker's notes. Wide/
   * landscape layout ignores this entirely (its own #setlist-notes-view-
   * toggle row is hidden by CSS there — the wide notes panel is always
   * visible in its own column regardless of this state). */
  let compactPanelView: 'setlist' | 'notes' = 'setlist';

  function syncCompactPanelLayout(): void {
    if (compactLayoutMediaQuery.matches) {
      viewToggleSetlistBtn.classList.toggle('is-active', compactPanelView === 'setlist');
      viewToggleNotesBtn.classList.toggle('is-active', compactPanelView === 'notes');
      viewToggleSetlistBtn.setAttribute('aria-selected', String(compactPanelView === 'setlist'));
      viewToggleNotesBtn.setAttribute('aria-selected', String(compactPanelView === 'notes'));
      setlistPanelScroll.style.display = compactPanelView === 'setlist' ? '' : 'none';
      notesPanelCompact.style.display = compactPanelView === 'notes' ? '' : 'none';
      return;
    }
    viewToggleSetlistBtn.classList.remove('is-active');
    viewToggleNotesBtn.classList.remove('is-active');
    viewToggleSetlistBtn.setAttribute('aria-selected', 'false');
    viewToggleNotesBtn.setAttribute('aria-selected', 'false');
    setlistPanelScroll.style.display = '';
    notesPanelCompact.style.display = 'none';
  }

  function setCompactPanelView(view: 'setlist' | 'notes'): void {
    compactPanelView = view;
    syncCompactPanelLayout();
  }

  function handleCompactLayoutChange(): void {
    syncCompactPanelLayout();
  }

  function addMediaQueryListener(query: MediaQueryList, listener: () => void): void {
    if ('addEventListener' in query) query.addEventListener('change', listener);
    else (query as MediaQueryList & { addListener(listener: (this: MediaQueryList, ev: MediaQueryListEvent) => unknown): void }).addListener(listener);
  }

  function removeMediaQueryListener(query: MediaQueryList, listener: () => void): void {
    if ('removeEventListener' in query) query.removeEventListener('change', listener);
    else
      (query as MediaQueryList & { removeListener(listener: (this: MediaQueryList, ev: MediaQueryListEvent) => unknown): void }).removeListener(
        listener,
      );
  }

  syncCompactPanelLayout();
  viewToggleSetlistBtn.addEventListener('click', () => setCompactPanelView('setlist'));
  viewToggleNotesBtn.addEventListener('click', () => setCompactPanelView('notes'));
  compactNotesOptionsTrigger.addEventListener('click', () => {
    const shouldOpen = !notesLayoutMenuOpen;
    closePlayerHeaderMenus();
    compactPanelView = 'notes';
    notesLayoutMenuOpen = shouldOpen;
    compactNotesOptionsTrigger.setAttribute('aria-expanded', String(shouldOpen));
    lastRenderedNotesKey = null;
    syncCompactPanelLayout();
    renderNotesPanel();
  });

  /** Both notes-panel instances (wide + compact) share this same delegated
   * handling — same reasoning as every other compact/wide control pair in
   * this file. Tab clicks force an immediate rebuild (bypassing
   * lastRenderedNotesKey's own tick-to-tick dedup, which exists purely to
   * protect a mid-keystroke textarea, not to block an explicit tab switch). */
  function queueSavedMyNotes(song: SongRecord, markerId: string): void {
    const result = queuePersonalMyNoteForSync(song, markerId);
    if (result.queued) {
      showToast(`Queued My Notes sync: song ${shortSyncId(song.supabaseSongId)}, marker ${shortSyncId(markerId)}.`, 'info');
      return;
    }
    showToast(
      result.reason === 'song-not-synced'
        ? 'Sync this song to a Cloud Band before My Notes can sync.'
        : 'Sign in before My Notes can sync.',
      'warn',
    );
  }

  function handleNotesPanelClick(e: MouseEvent): void {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    const tab = target?.dataset.notesTab as PlayerNotesTab | undefined;
    const notesPaneAttr = target?.dataset.notesPane as 'a' | 'b' | undefined;
    if (target?.dataset.action === 'set-player-notes-tab' && tab && notesPaneAttr) {
      const songId = activeSongForNotes ?? getCurrentSong()?.id;
      if (notesPaneAttr === 'a') {
        notesPaneA = { ...notesPaneA, tab };
        notesEditMode = false;
      } else {
        notesPaneB = { ...notesPaneB, tab };
      }
      if (songId) saveNotesPaneSelection(songId, notesPaneAttr, notesPaneAttr === 'a' ? notesPaneA : notesPaneB);
      // Picking a tab from the swap dropdown should close it — same as any
      // other "choose and go" menu in this panel.
      if (notesTabSwapMenuOpenPane === notesPaneAttr) notesTabSwapMenuOpenPane = null;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'set-notes-pane-timing') {
      const timing = target.dataset.notesTiming as PlayerNotesTiming | undefined;
      if (!notesPaneAttr || !timing) return;
      const songId = activeSongForNotes ?? getCurrentSong()?.id;
      if (notesPaneAttr === 'a') notesPaneA = { ...notesPaneA, timing };
      else notesPaneB = { ...notesPaneB, timing };
      if (songId) saveNotesPaneSelection(songId, notesPaneAttr, notesPaneAttr === 'a' ? notesPaneA : notesPaneB);
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'toggle-notes-tab-swap-menu' && notesPaneAttr) {
      notesTabSwapMenuOpenPane = notesTabSwapMenuOpenPane === notesPaneAttr ? null : notesPaneAttr;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'toggle-notes-layout-menu') {
      notesLayoutMenuOpen = !notesLayoutMenuOpen;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'close-notes-layout-menu') {
      notesLayoutMenuOpen = false;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'toggle-notes-edit-mode') {
      // Captured BEFORE renderNotesPanel() below — that call replaces
      // notesPanelWide/notesPanelCompact's entire innerHTML, which
      // detaches e.target (the clicked pencil/svg) from the DOM. A
      // .closest() call against an already-detached node can't find any
      // ancestor and always returns null (same bug class fixed earlier for
      // outside-click dismissal) — capturing the container up front avoids
      // depending on e.target still being attached once the rAF callback
      // below actually runs.
      const panelInstance = (e.target as HTMLElement).closest<HTMLElement>('#notes-panel-wide, #notes-panel-compact');
      notesEditMode = !notesEditMode;
      // Pause if a song is actively playing when edit mode turns ON —
      // editing while the song advances into the next section (or an
      // early-swap preview kicks in) used to force-exit edit mode and
      // switch the panel away mid-keystroke (see the marker-change guard
      // just below renderNotesPanel's own comment), silently dropping
      // whatever was being typed. Pausing removes the conflict outright:
      // nothing can move the panel out from under an open edit session.
      // Never auto-resumes on its own — that's an explicit, separate Play
      // tap, same as every other pause in this app.
      if (notesEditMode && (controller.getState().status === 'PLAYING' || controller.getState().status === 'PRE_ROLLING')) {
        controller.pause();
      }
      lastEditModeMarkerId = currentMarkers[pendingMarkerIndex ?? selectedMarkerIndex ?? Math.max(0, currentMarkerIndex(currentMarkers, controller.getState().currentTime))]?.id ?? null;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      // Focus the textarea the instant edit mode turns on — the whole
      // point of a dedicated Edit tap is that the very next action is
      // typing, so don't make that take a second tap into the field too.
      if (notesEditMode) {
        requestAnimationFrame(() => {
          panelInstance?.querySelector<HTMLTextAreaElement>('.notes-textarea:not([readonly])')?.focus();
        });
      }
      return;
    }
    if (target?.dataset.action === 'set-notes-layout') {
      const layout = target.dataset.notesLayout as NotesLayout | undefined;
      if (layout) setNotesLayout(layout);
      return;
    }
    if (target?.dataset.action === 'set-notes-early-swap-seconds') {
      notesEarlySwapSeconds = Number(target.dataset.seconds);
      notesEarlySwapBeats = null;
      saveNotesEarlySwap();
      notesLayoutMenuOpen = false;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'set-notes-early-swap-beats') {
      notesEarlySwapBeats = Number(target.dataset.beats);
      saveNotesEarlySwap();
      notesLayoutMenuOpen = false;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'set-notes-preview-warning-seconds') {
      notesPreviewWarningSeconds = Number(target.dataset.seconds);
      saveNotesPreviewWarningSeconds();
      notesLayoutMenuOpen = false;
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'set-player-notes-format') {
      const markerId = target.dataset.markerId;
      const format = target.dataset.noteFormat as MarkerNoteFormat | undefined;
      if (!markerId || !tab || !format) return;
      const marker = currentMarkers.find((item) => item.id === markerId);
      const song = getCurrentSong();
      if (!marker || !song) return;
      // My Notes' format is local-only (localStorage), same as its content
      // — never written to the shared marker record, unlike the other tabs.
      if (tab === 'my-notes') {
        saveMyNotesFormat(song.id, marker.id, format);
        queueSavedMyNotes(song, marker.id);
      } else {
        void appStore.updateMarker(song.id, marker.id, {
          noteFormats: { ...marker.noteFormats, [tab]: format },
        });
      }
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
    if (target?.dataset.action === 'clear-player-sheet') {
      const timeIndex = Math.max(0, currentMarkerIndex(currentMarkers, controller.getState().currentTime));
      const markerIndex = pendingMarkerIndex ?? selectedMarkerIndex ?? timeIndex;
      const marker = currentMarkers[markerIndex];
      const song = getCurrentSong();
      if (!marker || !song) return;
      void (async () => {
        const confirmed = await confirmDialog({
          title: 'Clear Sheet',
          message: `Clear the ${playerNotesTabLabel(notesPaneA.tab)} sheet for this section? This cannot be undone.`,
          confirmLabel: 'Clear Sheet',
          danger: true,
        });
        if (confirmed) {
          if (notesPaneA.tab === 'my-notes') {
            saveMyNotesSheet(song.id, marker.id, { rows: [] });
            queueSavedMyNotes(song, marker.id);
            lastRenderedNotesKey = null;
            renderNotesPanel();
          } else {
            await appStore.updateMarker(song.id, marker.id, {
              noteSheets: { ...marker.noteSheets, [notesPaneA.tab]: { rows: [] } },
            });
          }
        }
      })();
      return;
    }
    if (target?.dataset.action === 'clear-player-notes') {
      const markerId = target.dataset.markerId;
      const tab = target.dataset.notesTab as PlayerNotesTab | undefined;
      const marker = currentMarkers.find((item) => item.id === markerId);
      const song = getCurrentSong();
      if (!marker || !song || !tab) return;
      void (async () => {
        const confirmed = await confirmDialog({
          title: 'Clear Notes',
          message: `Clear the ${playerNotesTabLabel(tab)} notes for this section? This cannot be undone.`,
          confirmLabel: 'Clear Notes',
          danger: true,
        });
        if (confirmed) {
          if (tab === 'my-notes') {
            saveMyNotes(song.id, marker.id, '');
            queueSavedMyNotes(song, marker.id);
          } else {
            await appStore.updateMarker(song.id, marker.id, {
              notes: { ...marker.notes, [tab]: '' },
            });
          }
          lastRenderedNotesKey = null;
          renderNotesPanel();
        }
      })();
      return;
    }
    if (target?.dataset.action === 'adjust-note-zoom') {
      const format = target.dataset.noteFormat as MarkerNoteFormat | undefined;
      const direction = Number(target.dataset.zoomDirection) as -1 | 1;
      if (!format || (direction !== -1 && direction !== 1)) return;
      stepNoteZoom(format, direction);
      lastRenderedNotesKey = null;
      renderNotesPanel();
      return;
    }
  }
  notesPanelWide.addEventListener('click', handleNotesPanelClick);
  notesPanelCompact.addEventListener('click', handleNotesPanelClick);
  compactNotesInlineTools.addEventListener('click', handleNotesPanelClick);

  /** Commits an edited note on blur/change (freeform text — re-rendering on
   * every keystroke would fight the user's own cursor position, same
   * reasoning as every other freeform text field in this app). Both notes-
   * panel instances share this handler; whichever one the edit happened in,
   * the OTHER instance is updated too on the next renderNotesPanel() call
   * (forced here, since the underlying marker data just changed). Must
   * target the same DISPLAYED marker renderNotesPanel just showed (pending
   * tap, if a lead-in/count-in is still running — see its own doc comment),
   * not the raw playhead position — otherwise editing a note while a
   * count-in is running toward the tapped marker would silently save onto
   * the PREVIOUS section instead of the one actually on screen. */
  /** Briefly flashes "Saved" in the edit toolbar's own indicator slot — the
   * one visible confirmation that an edit genuinely landed, given there's
   * no explicit Save action at all (every edit auto-saves on blur/paste/
   * cell-commit). Queries live rather than caching a reference, since the
   * toolbar is rebuilt on every renderNotesPanel() call and a cached node
   * would eventually point at a detached element. Both panel instances get
   * it, same "wide + compact always shown the same content" pattern as
   * everything else in this panel. */
  function showNotesSavedIndicator(): void {
    if (notesSavedIndicatorTimeoutId !== null) window.clearTimeout(notesSavedIndicatorTimeoutId);
    const indicators = [notesPanelWide, notesPanelCompact].map((panel) => panel.querySelector<HTMLElement>('[data-role="notes-saved-indicator"]'));
    indicators.forEach((el) => {
      if (!el) return;
      el.textContent = 'Saved';
      el.classList.add('is-visible');
    });
    notesSavedIndicatorTimeoutId = window.setTimeout(() => {
      notesSavedIndicatorTimeoutId = null;
      indicators.forEach((el) => el?.classList.remove('is-visible'));
    }, 1600);
  }

  function handleNotesPanelChange(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('notes-textarea') || target.dataset.action !== 'edit-player-notes') return;
    const marker = currentMarkers.find((item) => item.id === target.dataset.markerId);
    const tab = target.dataset.notesTab as PlayerNotesTab | undefined;
    const song = getCurrentSong();
    if (!marker || !song || !tab) return;
    if (tab === 'my-notes') {
      saveMyNotes(song.id, marker.id, (target as HTMLTextAreaElement).value);
      queueSavedMyNotes(song, marker.id);
      showNotesSavedIndicator();
      return;
    }
    void appStore.updateMarker(song.id, marker.id, {
      notes: { ...marker.notes, [tab]: (target as HTMLTextAreaElement).value },
      noteFormats: { ...marker.noteFormats, [tab]: 'text' },
    });
    showNotesSavedIndicator();
  }
  notesPanelWide.addEventListener('change', handleNotesPanelChange);
  notesPanelCompact.addEventListener('change', handleNotesPanelChange);

  function handlePlayerSheetPaste(event: ClipboardEvent): void {
    const editor = (event.target as HTMLElement).closest<HTMLElement>('.note-sheet-editor[data-action="edit-player-sheet"]');
    if (!editor) return;
    event.preventDefault();
    const marker = currentMarkers.find((item) => item.id === editor.dataset.markerId);
    const tab = editor.dataset.notesTab as PlayerNotesTab | undefined;
    const song = getCurrentSong();
    if (!marker || !song || !tab) return;
    const html = event.clipboardData?.getData('text/html') ?? '';
    const plain = event.clipboardData?.getData('text/plain') ?? '';
    const sheet = parseSheetClipboard(html, plain);
    if (sheetClipboardExceedsLimits(html, plain)) {
      showToast(`Sheet limited to ${MAX_NOTE_SHEET_ROWS} rows × ${MAX_NOTE_SHEET_COLUMNS} columns.`, 'warn');
    }
    if (tab === 'my-notes') {
      saveMyNotesSheet(song.id, marker.id, sheet);
      saveMyNotesFormat(song.id, marker.id, 'sheet');
      queueSavedMyNotes(song, marker.id);
      lastRenderedNotesKey = null;
      renderNotesPanel();
      showNotesSavedIndicator();
      return;
    }
    void appStore.updateMarker(song.id, marker.id, {
      noteSheets: { ...marker.noteSheets, [tab]: sheet },
      noteFormats: { ...marker.noteFormats, [tab]: 'sheet' },
    });
    showNotesSavedIndicator();
  }

  function handlePlayerSheetFocusOut(event: FocusEvent): void {
    const cell = (event.target as HTMLElement).closest<HTMLElement>('[data-sheet-row][data-sheet-cell]');
    const editor = cell?.closest<HTMLElement>('.note-sheet-editor[data-action="edit-player-sheet"]');
    if (!cell || !editor) return;
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && editor.contains(nextTarget)) return;
    const marker = currentMarkers.find((item) => item.id === editor.dataset.markerId);
    const tab = editor.dataset.notesTab as PlayerNotesTab | undefined;
    const song = getCurrentSong();
    if (!marker || !song || !tab) return;
    const existingSheet = tab === 'my-notes' ? loadMyNotesSheet(song.id, marker.id) : markerNoteSheet(marker, tab);
    const rows = existingSheet.rows.map((row) => row.map((item) => ({ ...item })));
    editor.querySelectorAll<HTMLElement>('[data-sheet-row][data-sheet-cell]').forEach((editedCell) => {
      const rowIndex = Number(editedCell.dataset.sheetRow);
      const cellIndex = Number(editedCell.dataset.sheetCell);
      if (rows[rowIndex]?.[cellIndex]) rows[rowIndex][cellIndex].text = editedCell.textContent ?? '';
    });
    if (tab === 'my-notes') {
      saveMyNotesSheet(song.id, marker.id, { rows });
      queueSavedMyNotes(song, marker.id);
      showNotesSavedIndicator();
      return;
    }
    // Let the pointer's click finish against the current DOM before the
    // store notification refreshes the panel underneath it.
    window.setTimeout(() => {
      void appStore.updateMarker(song.id, marker.id, { noteSheets: { ...marker.noteSheets, [tab]: { rows } } });
      showNotesSavedIndicator();
    }, 0);
  }

  function handleNotesResizePointerDown(event: PointerEvent): void {
    const handle = (event.target as HTMLElement).closest<HTMLElement>('.notes-resize-handle[data-action="resize-notes-preview"]');
    if (!handle || event.button !== 0) return;
    activeNotesResizePointerId = event.pointerId;
    handle.setPointerCapture(event.pointerId);
    getVisibleNotesPanel().classList.add('is-resizing-notes');
    document.body.classList.add('is-resizing-performance');
    resizeNotesPreviewFromPointer(event.clientX, event.clientY);
    event.preventDefault();
  }

  function handleNotesResizePointerMove(event: PointerEvent): void {
    if (event.pointerId !== activeNotesResizePointerId) return;
    resizeNotesPreviewFromPointer(event.clientX, event.clientY);
  }

  function handleNotesResizePointerUp(event: PointerEvent): void {
    stopNotesResize(event.pointerId);
  }

  function handleNotesResizeDoubleClick(): void {
    if (notesLayout === 'side-by-side') {
      preferredNotesPreviewWidthPercent = applyNotesPreviewWidth(DEFAULT_NOTES_PREVIEW_WIDTH_PERCENT);
      savePreferredNotesPreviewWidth();
    } else {
      preferredNotesPreviewHeightPercent = applyNotesPreviewHeight(DEFAULT_NOTES_PREVIEW_HEIGHT_PERCENT);
      savePreferredNotesPreviewHeight();
    }
  }

  function handleNotesResizeKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const step = event.shiftKey ? 5 : 2;
    let nextPercent: number | null = null;
    // In side-by-side, Next sits on the right (plain DOM order — see
    // .is-side-by-side's own CSS comment) — the physical Left/Right arrows
    // read as "grow/shrink the Next panel" the same intuitive way Up/Down
    // already do for height.
    if (notesLayout === 'side-by-side') {
      if (key === 'ArrowLeft') nextPercent = preferredNotesPreviewWidthPercent + step;
      if (key === 'ArrowRight') nextPercent = preferredNotesPreviewWidthPercent - step;
    } else {
      if (key === 'ArrowUp') nextPercent = preferredNotesPreviewHeightPercent + step;
      if (key === 'ArrowDown') nextPercent = preferredNotesPreviewHeightPercent - step;
    }
    if (key === 'Home') nextPercent = 60;
    if (key === 'End') nextPercent = 16;
    if (nextPercent === null) return;
    event.preventDefault();
    if (notesLayout === 'side-by-side') {
      preferredNotesPreviewWidthPercent = applyNotesPreviewWidth(nextPercent);
      savePreferredNotesPreviewWidth();
    } else {
      preferredNotesPreviewHeightPercent = applyNotesPreviewHeight(nextPercent);
      savePreferredNotesPreviewHeight();
    }
  }

  notesPanelWide.addEventListener('paste', handlePlayerSheetPaste);
  notesPanelCompact.addEventListener('paste', handlePlayerSheetPaste);
  notesPanelWide.addEventListener('focusout', handlePlayerSheetFocusOut);
  notesPanelCompact.addEventListener('focusout', handlePlayerSheetFocusOut);
  notesPanelWide.addEventListener('pointerdown', handleNotesResizePointerDown);
  notesPanelCompact.addEventListener('pointerdown', handleNotesResizePointerDown);
  notesPanelWide.addEventListener('pointermove', handleNotesResizePointerMove);
  notesPanelCompact.addEventListener('pointermove', handleNotesResizePointerMove);
  notesPanelWide.addEventListener('pointerup', handleNotesResizePointerUp);
  notesPanelCompact.addEventListener('pointerup', handleNotesResizePointerUp);
  notesPanelWide.addEventListener('pointercancel', handleNotesResizePointerUp);
  notesPanelCompact.addEventListener('pointercancel', handleNotesResizePointerUp);
  notesPanelWide.addEventListener('dblclick', handleNotesResizeDoubleClick);
  notesPanelCompact.addEventListener('dblclick', handleNotesResizeDoubleClick);
  notesPanelWide.addEventListener('keydown', (event) => {
    if ((event.target as HTMLElement).closest('.notes-resize-handle')) handleNotesResizeKeydown(event);
  });
  notesPanelCompact.addEventListener('keydown', (event) => {
    if ((event.target as HTMLElement).closest('.notes-resize-handle')) handleNotesResizeKeydown(event);
  });

  // Rehearsal/Live is set per-setlist (in the Setlists screen), not here —
  // Rehearsal/Live is set per-setlist (in the Setlists screen) — determines
  // whether the progress bar/marker rows are interactive at all. The lead-in
  // picker itself now lives in the Song Library as a per-song setting (see
  // SongRecord.markerLeadInSeconds), not here.
  function updatePracticeModeUi(): void {
    progressTrack.classList.toggle('is-scrubbable', isRehearsalMode);
    setlistPanelScroll.classList.toggle('markers-interactive', isRehearsalMode);
    // Explicit safety snap-back, not just hiding the control: if a setlist
    // that was already playing at a non-100% practice speed gets switched
    // from Rehearsal to Live mid-session, playback must return to full
    // speed immediately, not just become un-reachable to change further.
    // A no-op whenever nothing needs to change (already 1, or no file
    // engine loaded).
    if (!isRehearsalMode) controller.setPlaybackRate(1);
  }

  // Seeking is display-only unless the active setlist is in Rehearsal mode —
  // this is the entire safety gate for the feature. The marker strip and
  // progress bar are ALWAYS visible and auto-updating (so the drummer can
  // see where they are in the song live), but tapping them does nothing in
  // Live mode.
  progressTrack.addEventListener('click', (e) => {
    if (!isRehearsalMode) return;
    if (controller.getState().activeEngine !== 'file') return;
    const rect = progressTrack.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    // Manual scrubbing has no lead-in concept — it's an explicit, immediate
    // "go here" — so any pending marker target (and its countdown) from an
    // earlier tap is moot. Also clears selectedMarkerIndex: a scrub is
    // exactly as explicit as a marker tap, so it must override an earlier
    // marker preview pin the same way — otherwise scrubbing BACKWARD to a
    // point before a previously-tapped forward marker left that marker's
    // pin in place (its own forward-release in updateMarkerProgress only
    // fires once genuine playback time passes it, which a backward seek
    // never does), so the notes panel kept showing the old marker's section
    // instead of the one actually under the new playhead position. Also
    // ends any active loop (still armed, but with nothing to loop until a
    // marker is tapped again) — scrubbing away from a looping section is
    // exactly "I'm done with that loop," same as switching to a different
    // marker would be.
    pendingMarkerIndex = null;
    selectedMarkerIndex = null;
    selectedMarkerIndexIsForwardPreview = false;
    markerCountdownTarget = null;
    loopSectionIndex = null;
    updatePlayPauseCountIn(null);
    controller.seek(pct * (controller.getState().duration || 0));
  });


  async function guardSongSwitch(action: () => Promise<void>): Promise<void> {
    if (isRehearsalMode) {
      await action();
      return;
    }

    if (controller.getState().status === 'PLAYING') {
      const confirmed = await confirmDialog({
        title: 'Song is playing',
        message: 'Stop the current song and switch? This will stop playback immediately.',
        confirmLabel: 'Stop and Switch',
        danger: true,
      });
      if (!confirmed) return;
      controller.stop();
    }
    await action();
  }

  setlistPanelScroll.addEventListener('click', (e) => {
    const eventTarget = e.target as HTMLElement;

    if (eventTarget.closest('.setlist-song-edit-btn')) {
      editCurrentSong();
      return;
    }

    if (eventTarget.closest('.setlist-subrows-toggle-btn')) {
      setlistSubRowsCollapsed = !setlistSubRowsCollapsed;
      rerenderSetlistRows();
      return;
    }

    if (eventTarget.closest('#setlist-panel-now-badge')) {
      void handlePlayPauseTap();
      return;
    }

    // Song structure markers: seeking is gated behind Rehearsal mode, same
    // as the progress bar — the entire safety gate for this feature. The
    // sub-rows are only visible once the drummer expands the current song,
    // but tapping only does something (seeks) in Rehearsal mode.
    const markerRow = eventTarget.closest<HTMLElement>('.setlist-marker-row');
    if (markerRow) {
      if (!isRehearsalMode) return;
      if (controller.getState().activeEngine !== 'file') return;
      const markerIndex = Number(markerRow.dataset.markerIndex);
      const marker = currentMarkers[markerIndex];
      if (!marker || Number.isNaN(markerIndex)) return;
      const isSecondTapOnSelected = selectedMarkerIndex === markerIndex;

      if (!isSecondTapOnSelected) {
        const timeIndexAtTap = Math.max(0, currentMarkerIndex(currentMarkers, controller.getState().currentTime));
        selectedMarkerIndex = markerIndex;
        // Treat a tap on the section that is already current as a releasable
        // preview too. That keeps "tap the section I'm on to start from here"
        // from pinning the notes panel there forever once playback advances.
        selectedMarkerIndexIsForwardPreview = markerIndex >= timeIndexAtTap;
        pendingMarkerIndex = null;
        markerCountdownTarget = null;
        updateMarkerProgress(controller.getState().currentTime);
        renderNotesPanel();
        return;
      }

      // Lead-in: seek to BEFORE the marker's own timestamp so the rest of
      // the band gets a run-up into the section instead of a cold start
      // exactly on the boundary. If playback was already running, it keeps
      // running straight through the seek/lead-in — this is Rehearsal mode
      // (Live mode already returned above), the practice use case, where
      // requiring a second explicit tap just to resume after every marker
      // tap was pure friction, not a safety feature. If it was already
      // paused, it stays paused (an explicit Play/Resume tap is still
      // needed) — a marker tap only ever changes WHERE playback would
      // continue from, never whether it's playing.
      const wasPlaying = controller.getState().status === 'PLAYING';
      const currentSong = getCurrentSong();
      // Lead-in is now a per-song setting (Song Library) UNLESS the active
      // setlist pins its own override (see resolveEffectiveLeadIn) — falls
      // back to no lead-in if somehow there's no current song.
      const effectiveLeadIn = currentSong ? resolveEffectiveLeadIn(getActiveSetlist()?.countInOverride ?? null, currentSong) : { seconds: 0, beats: null };
      const leadIn = effectiveLeadIn.seconds;
      const seekTarget = markerSeekTarget(marker.timeSeconds, leadIn);
      // Pin this marker as the displayed "active" one immediately — even
      // before the lead-in seek lands — so the row highlights right away
      // instead of flashing whatever section currently owns the playhead.
      // This is a committed start/jump, not a "look back and keep it
      // pinned" selection, so it should release once playback advances
      // beyond the tapped section.
      selectedMarkerIndex = markerIndex;
      selectedMarkerIndexIsForwardPreview = true;
      pendingMarkerIndex = leadIn > 0 ? markerIndex : null;
      // Re-tapping ANY marker while Loop is armed (re)arms the loop for
      // THAT section, replacing whatever was looping before — no need to
      // turn Loop off and back on to switch which section repeats. This
      // MUST happen before controller.seek()/controller.play() below, not
      // after: PlaybackController.setState() notifies subscribers (incl.
      // renderControllerState/checkLoopBoundary) SYNCHRONOUSLY, so seek()
      // re-enters checkLoopBoundary before it even returns. If
      // loopSectionIndex still pointed at the OLD looped section at that
      // moment, checkLoopBoundary would see the freshly-seeked (later)
      // currentTime as already past the OLD section's end and immediately
      // seek BACK to it — silently undoing this tap's own seek to the NEW
      // marker before the tap handler's caller ever saw it land. That
      // exact re-entrant stomp was why jumping to a LATER marker while
      // Loop was armed on an EARLIER one took 3 taps instead of 2 (tap 2's
      // own seek was immediately reverted by the stale loop boundary,
      // requiring a 3rd tap after loopSectionIndex had finally caught up).
      if (loopMode === 'marker') loopSectionIndex = markerIndex;
      // Drive the SAME Play/Pause button countdown the song-start pre-roll
      // uses — same progression the drummer already saw at the beginning of
      // the song, now re-used for a mid-song marker jump. secondsPerBeatAtTap
      // uses THIS song's own BPM, matching the beat-count convention the
      // pre-roll's own label already follows. leadInSeconds itself is
      // derived below, from the ACTUAL post-seek landing point rather than
      // the pre-seek target — see the comment there for why.
      const secondsPerBeatAtTap = currentSong && effectiveLeadIn.beats !== null ? secondsPerBeat(currentSong.metronomeSettings) : null;
      controller.seek(seekTarget);
      if (!wasPlaying) {
        const pickupBeats = currentSong
          ? pickupBeatsFromFirstBeatTime(currentSong.firstBeatTimeSeconds, currentSong.metronomeSettings, currentSong.syntheticClickOffsetSeconds)
          : 0;
        void controller.play(isRehearsalMode && currentSong ? effectiveLeadIn : null, pickupBeats, currentSong?.firstBeatTimeSeconds ?? null);
      }
      // leadInSeconds is derived from the ACTUAL post-seek currentTime, not
      // the pre-seek seekTarget — a real seek doesn't necessarily land on
      // the exact requested sample, so anchoring off seekTarget here while
      // updatePlayPauseCountIn's remainingSeconds is anchored off the live
      // currentTime meant the two could disagree by a hair (e.g. totalSeconds
      // 3.28767123... vs remainingSeconds 3.28767199...), which was enough to
      // push the very first liveCountInLabel render past a whole-beat
      // boundary and show "9 counts left" instead of "8". Deriving both from
      // the same live currentTime keeps them exactly consistent.
      const landedTime = controller.getState().currentTime;
      markerCountdownTarget =
        leadIn > 0 ? { markerTimeSeconds: marker.timeSeconds, leadInSeconds: marker.timeSeconds - landedTime, secondsPerBeatAtTap } : null;
      // (loopSectionIndex was already reassigned above, before the seek —
      // see that comment for why the ordering matters.)
      updateMarkerProgress(landedTime);
      updatePlayPauseCountIn(controller.getState().preRoll, landedTime);
      syncMarkerCountdownRaf();
      return;
    }

    const target = eventTarget.closest<HTMLElement>('.setlist-panel-row');
    const index = target?.dataset.index;
    if (index === undefined) return;
    void jumpToEntry(Number(index));
  });

  setlistPanelScroll.addEventListener('keydown', (event) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('.setlist-panel-row');
    if (!row || event.target !== row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    row.click();
  });

  setlistPanelScroll.addEventListener('keydown', (event) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('.setlist-marker-row');
    if (!row || event.target !== row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    if (event.key === ' ' && (controller.getState().status === 'PLAYING' || controller.getState().status === 'PRE_ROLLING')) {
      controller.pause();
      return;
    }
    row.click();
  });

  const unsubController = controller.subscribe(renderControllerState);
  const unsubStore = appStore.subscribe(() => {
    const nextIsRehearsalMode = getActiveSetlist()?.mode !== 'live';
    const rehearsalModeChanged = isRehearsalMode !== nextIsRehearsalMode;
    isRehearsalMode = nextIsRehearsalMode;
    if (rehearsalModeChanged) updatePracticeModeUi();
    void loadCurrentEntry();
  });
  // scheduleBeatPulse (not the immediate triggerPulse) — MetronomeEngine
  // schedules audio up to 0.2s AHEAD of when it's actually audible (see its
  // own doc comment), and this event fires at schedule time, so pulsing
  // immediately would show each beat/subdivision up to 200ms before you
  // hear it.
  const unsubBeats = controller.subscribeBeats((beat) => {
    const song = getCurrentSong();
    const settings = song?.metronomeSettings;
    if (!settings) return; // only ever emitted while a metronome-mode song is loaded
    const secondsPerTick = secondsPerBeat(settings) / settings.subdivision;
    const isDownbeat = beat.beatIndexInMeasure === 0 && settings.accentFirstBeat;
    scheduleBeatPulse(beatVisualEls, beat, settings.subdivision, secondsPerTick, sharedAudioContext!, isDownbeat);
    scheduleBeatPulse(headerBeatVisualEls, beat, settings.subdivision, secondsPerTick, sharedAudioContext!, isDownbeat);
    if (transportRowCompact.classList.contains('shows-beat-indicators')) {
      scheduleTransportBeatIndicator(beat, sharedAudioContext!);
    }
  });

  const unsubWakeLock = wakeLockManager.subscribe(handleWakeLockStatus);
  void wakeLockManager.acquire();

  isRehearsalMode = getActiveSetlist()?.mode !== 'live';
  updatePracticeModeUi();

  void loadCurrentEntry();

  // The canvas is sized off its own rendered CSS box, same as the Song
  // Library's waveform canvases — a viewport resize (rotating a tablet)
  // changes that box without going through appStore/the controller at all,
  // so it needs its own redraw independent of every other subscription here.
  const handleResize = (): void => {
    syncCompactPanelLayout();
    applySetlistWidth(preferredSetlistWidthPercent);
    drawProgressWaveform();
    scheduleProgressMarkerLabelFit();
    scheduleTitleFit();
    scheduleNotesInlineTabCollapse();
  };
  window.addEventListener('resize', handleResize);
  addMediaQueryListener(compactLayoutMediaQuery, handleCompactLayoutChange);
  // The setlist divider changes the progress track without changing the
  // viewport, so window.resize alone cannot keep the labels accurate.
  const progressLabelResizeObserver = new ResizeObserver(scheduleProgressMarkerLabelFit);
  progressLabelResizeObserver.observe(progressSections);
  // Same reasoning as progressLabelResizeObserver above, applied to the
  // title's own column — rotating the device or dragging the setlist
  // splitter changes .current-song-title's available width without firing
  // a window resize event.
  const titleResizeObserver = new ResizeObserver(scheduleTitleFit);
  titleResizeObserver.observe(currentTitle);

  return () => {
    unsubController();
    unsubStore();
    unsubWakeLock();
    unsubBeats();
    stopMarkerCountdownRaf();
    stopSyntheticBeatFlash();
    stopSetlistResize();
    progressLabelResizeObserver.disconnect();
    titleResizeObserver.disconnect();
    if (progressLabelFitRafId !== null) cancelAnimationFrame(progressLabelFitRafId);
    if (titleFitRafId !== null) cancelAnimationFrame(titleFitRafId);
    if (notesTabCollapseRafId !== null) cancelAnimationFrame(notesTabCollapseRafId);
    window.removeEventListener('resize', handleResize);
    removeMediaQueryListener(compactLayoutMediaQuery, handleCompactLayoutChange);
    document.removeEventListener('pointerdown', handlePlayerHeaderOutsideClick);
    document.removeEventListener('pointerdown', handleNotesPanelOutsideClick);
    window.removeEventListener('keydown', handlePlayerHeaderKeydown);
  };
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

export function getSharedController(): PlaybackController | null {
  return sharedController;
}
